import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS } from "../../constants/api-urls";
import { useLocationMaster } from "../../hooks/useLocationMaster";
import { useProductDetailsMaster } from "../../hooks/useProductDetailsMaster";
import { useFactoryDetailsMaster } from "../../hooks/useFactoryDetailsMaster";
import {
  completeP2pRfqLinkAfterModuleCreate,
  getEffectiveP2pLaunchContext,
} from "../../utils/p2pRfqLinkAfterCreate";
import {
  auctionRequiresListingApproval,
  parseAuctionPublishResponse,
} from "../../utils/auctionListingApproval";
import "./CreateAuction.scss";

// Categories and sub-categories will be fetched from API

export default function CreateAuction() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const p2pLaunch = getEffectiveP2pLaunchContext(searchParams);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasCredits, setHasCredits] = useState(false);
  const [creditReservePriceCap, setCreditReservePriceCap] = useState(null);
  const [reservePriceError, setReservePriceError] = useState("");
  const [basePriceError, setBasePriceError] = useState("");
  const [checkingCredits, setCheckingCredits] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [createdAuctionId, setCreatedAuctionId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [form, setForm] = useState({
    product_name: "",
    lot_type: "Single Item",
    reserve_type: "With Reserve",
    category_id: null,
    sub_category_id: null,
    quantity: "1",
    unit_of_measure: "Pieces",
    product_description: "",
    keywords: [],
    product_attributes: {
      // Vehicle section
      condition_grade: "",
      certification: "",
      year_of_manufacture: "",
      shelf_life_expire_date: "",
      packaging_unit: "",
      packaging_type: "",
      lot_size: "",
      batch_no_lot_no: "",
      certification_compliance: "",
      // Specialised Attributes
      registration_number: "",
      vin_chassis_number: "",
      insurance_status: "",
      model: "",
      road_permit: "",
      odometer_hours: "",
      tyre_track_condition: "",
      ownership_count: "",
      service_history_available: "",
      accident_damage_history: "",
      engine_power: "",
      engine_power_unit: "",
      fuel_ev_type: "",
      axle_configuration: "",
      emission_norm: "",
      body_type_config: "",
      chassis_type: "",
      battery_health_ev: "",
      transmission: "",
      registration_expiry_date: "",
      registration_title: "",
      // Construction & Heavy Equipment fields
      construction_condition_grade: "",
      construction_year_of_manufacture: "",
      construction_shelf_life_expire_date: "",
      construction_packaging_unit: "",
      construction_packaging_type: "",
      construction_lot_size: "",
      construction_batch_no_lot_no: "",
      construction_certification_compliance: "",
      machine_type: "",
      serial_number: "",
      construction_model: "",
      hydraulic_system: "",
      construction_ownership_count: "",
      capacity_power: "",
      capacity_power_unit: "",
      work_area_dimensions: "",
      control_system: "",
      maintenance_status: "",
      operating_hours: "",
      year_of_last_overhaul: "",
      construction_fuel_type: "",
      safety_features: "",
      // Packaging & Printing Machinery fields
      packaging_condition_grade: "",
      packaging_year_of_manufacture: "",
      packaging_shelf_life_expire_date: "",
      packaging_packaging_unit: "",
      packaging_packaging_type: "",
      packaging_lot_size: "",
      packaging_batch_no_lot_no: "",
      packaging_certification_compliance: "",
      packaging_machine_type: "",
      material_compatibility: [],
      packaging_model: "",
      max_size_capacity: "",
      output_quality: "",
      printing_technology: "",
      packaging_control_system: "",
      accessories_tooling: [],
      cycle_time_throughput: "",
      printing_packaging_speed: "",
      printing_packaging_speed_unit: "",
      power_requirements: "",
      power_requirements_unit: "",
      packaging_safety_features: [],
      packaging_service_history_available: "",
      // Energy & Electrical Equipment fields
      energy_condition_grade: "",
      energy_year_of_manufacture: "",
      energy_shelf_life_expire_date: "",
      energy_packaging_unit: "",
      energy_packaging_type: "",
      energy_lot_size: "",
      energy_batch_no_lot_no: "",
      energy_certification_compliance: "",
      equipment_type: "",
      installation_year: "",
      energy_model: "",
      efficiency_class: "",
      voltage_rating: "",
      voltage_rating_unit: "",
      current_rating: "",
      current_rating_unit: "",
      kva_kw_capacity: "",
      kva_kw_capacity_unit: "",
      frequency: "",
      frequency_unit: "",
      phase_type: "",
      cooling_type: "",
      standards_compliance: "",
      operating_temp_range: "",
      // Construction Materials (Bulk) fields
      construction_materials_condition_grade: "",
      construction_materials_year_of_manufacture: "",
      construction_materials_shelf_life_expire_date: "",
      construction_materials_packaging_unit: "",
      construction_materials_packaging_type: "",
      construction_materials_lot_size: "",
      construction_materials_batch_no_lot_no: "",
      construction_materials_certification_compliance: "",
      material_type: "",
      specification: "",
      construction_materials_quantity: "",
      construction_materials_unit_of_measure: "",
      test_certificate: "",
      // Event & Exhibition Assets fields
      event_exhibition_condition_grade: "",
      event_exhibition_year_of_manufacture: "",
      event_exhibition_shelf_life_expire_date: "",
      event_exhibition_packaging_unit: "",
      event_exhibition_packaging_type: "",
      event_exhibition_lot_size: "",
      event_exhibition_batch_no_lot_no: "",
      event_exhibition_certification_compliance: "",
      asset_type: "",
      material: "",
      av_specs: "",
      includes_installation: "",
      dimensions: "",
      event_exhibition_quantity: "",
      // Scrap & Recyclables fields
      scrap_condition_grade: "",
      scrap_year_of_manufacture: "",
      scrap_shelf_life_expire_date: "",
      scrap_packaging_unit: "",
      scrap_packaging_type: "",
      scrap_lot_size: "",
      scrap_batch_no_lot_no: "",
      scrap_certification_compliance: "",
      scrap_type: "",
      scrap_form: "",
      scrap_quantity: "",
      scrap_quantity_unit: "",
      purity_percentage: "",
      contamination_details: "",
      recycling_certificate: "",
      hazardous_material_flag: "",
      // Furniture & Office Assets fields
      furniture_condition_grade: "",
      furniture_year_of_manufacture: "",
      furniture_shelf_life_expire_date: "",
      furniture_packaging_unit: "",
      furniture_packaging_type: "",
      furniture_lot_size: "",
      furniture_batch_no_lot_no: "",
      furniture_certification_compliance: "",
      furniture_type: "",
      furniture_material: "",
      load_capacity: "",
      load_capacity_unit: "",
      quantity_available: "",
      assembly_required: "",
      furniture_dimensions: "",
      warranty_return_policy: "",
      colour_finish: "",
      // Healthcare Supplies fields
      healthcare_condition_grade: "",
      healthcare_year_of_manufacture: "",
      healthcare_shelf_life_expire_date: "",
      healthcare_packaging_unit: "",
      healthcare_packaging_type: "",
      healthcare_lot_size: "",
      healthcare_batch_no_lot_no: "",
      healthcare_certification_compliance: "",
      product_type: "",
      medical_device_classification: "",
      sterilization_status: "",
      expiry_date: "",
      storage_conditions: "",
      regulatory_compliance: "",
      healthcare_quantity_available: "",
      healthcare_unit_of_measure: "",
      // Livestock & Dairy Products fields
      livestock_condition_grade: "",
      livestock_year_of_manufacture: "",
      livestock_shelf_life_expire_date: "",
      livestock_packaging_unit: "",
      livestock_packaging_type: "",
      livestock_lot_size: "",
      livestock_batch_no_lot_no: "",
      livestock_certification_compliance: "",
      species_breed: "",
      livestock_product_type: "",
      gender: "",
      weight: "",
      weight_bale_size: "",
      unit_size: "",
      age: "",
      vaccination_status: "",
      storage_temperature: "",
      treatment: "",
      purpose: "",
      health_certificate: "",
      type_hides_skins: "",
      // Retail & FMCG fields
      retail_condition_grade: "",
      retail_year_of_manufacture: "",
      retail_shelf_life_expire_date: "",
      retail_packaging_unit: "",
      retail_packaging_type: "",
      retail_lot_size: "",
      retail_batch_no_lot_no: "",
      retail_certification_compliance: "",
      product_category: "",
      flavor: "",
      return_grade: "",
      manufacturing_date: "",
      nutritional_ingredient_label: "",
      storage_conditions: "",
      allergen_information: "",
      handling_instructions: "",
      storage_temperature: "",
      storage_temperature_unit: "",
      regulatory_certifications: "",
      // Real Estate & Leasing fields
      real_estate_condition_grade: "",
      real_estate_year_of_manufacture: "",
      real_estate_shelf_life_expire_date: "",
      real_estate_packaging_unit: "",
      real_estate_packaging_type: "",
      real_estate_lot_size: "",
      real_estate_batch_no_lot_no: "",
      real_estate_certification_compliance: "",
      asset_type: "",
      ownership_title: "",
      tenure_lease_terms: "",
      utilities: [],
      floor_building_height: "",
      size_area: "",
      size_area_unit: "",
      year_built: "",
      insurance_coverage: "",
      parking_spaces: "",
      additional_facilities: [],
      zoning_approval_status: "",
      environmental_approvals: [],
      floor_load_capacity: "",
      floor_load_capacity_unit: "",
      // IT & Electronics fields
      it_electronics_condition_grade: "",
      it_electronics_year_of_manufacture: "",
      it_electronics_shelf_life_expire_date: "",
      it_electronics_packaging_unit: "",
      it_electronics_packaging_type: "",
      it_electronics_lot_size: "",
      it_electronics_batch_no_lot_no: "",
      it_electronics_certification_compliance: [],
      device_type: "",
      model: "",
      ports_connectivity: "",
      processor_spec: "",
      memory_storage: "",
      memory_storage_unit: "",
      expandability_options: "",
      expandability_unit: "",
      graphics_gpu_specs: "",
      networking_standards: "",
      os_firmware_version: "",
      warranty_status: "",
      service_history: "",
      software_licenses: "",
      safety_standards: "",
      display: "",
      panel_type: "",
      resolution: "",
      refresh_rate: "",
      brightness: "",
      color_gamut: "",
      hdr_support: "",
      touchscreen: "",
      battery_type: "",
      battery_capacity: "",
      backup_time: "",
      charging_power: "",
      battery_certifications: "",
      cycle_life: "",
      // Medical & Laboratory Equipment fields
      medical_condition_grade: "",
      medical_year_of_manufacture: "",
      medical_shelf_life_expire_date: "",
      medical_packaging_unit: "",
      medical_packaging_type: "",
      medical_lot_size: "",
      medical_batch_no_lot_no: "",
      medical_certification_compliance: [],
      equipment_type: "",
      last_calibration_date: "",
      calibration_status: "",
      throughput_capacity: "",
      power_requirements: "",
      power_requirements_unit: "",
      condition: "",
      regulatory_approval: "",
      maintenance_contract_status: "",
      accessories_included: [],
      safety_features: [],
      // Legacy fields (for backward compatibility)
      brand: "",
      year: "",
      condition: "",
      mileage: "",
      color: "",
      fuel: "",
    },
    hsn_code: "",
    hs_code: "",
    unspsc_code: "",
    gst_rate: "18",
    cgst_rate: "9",
    sgst_rate: "9",
    igst_rate: "18",
    country: "",
    state: "",
    city: "",
    pincode: "",
    address: "",
    is_export: "No",
    incoterms: "",
    loading_responsibility: "Buyer",
    packaging_responsibility: "Seller",
    insurance: "Optional",
    expected_price: "",
    reserve_price: "",
    bid_increment: "",
    start_date: "",
    end_date: "",
    auction_end_condition: "End on Scheduled Date Only",
    pickup_window_days: "7",
    collection_terms: "",
    inspection_allowed: "Yes",
    inspector_type: "Self Inspection",
    inspection_start_date: "",
    inspection_end_date: "",
    inspection_start_time: "09:00",
    inspection_end_time: "18:00",
    inspection_checklist: [],
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    faqs: [],
  });

  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [uploadedVideoUrls, setUploadedVideoUrls] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [keywordInput, setKeywordInput] = useState("");
  const [customAttributes, setCustomAttributes] = useState([{ id: Date.now(), key: "", value: "", unit: "" }]);
  const [inspectionChecklistInput, setInspectionChecklistInput] = useState("");
  const [faqList, setFaqList] = useState([{ id: 1, question: '', answer: '' }]);

  const { countries, states, cities, updateListsForCountry, updateListsForState } = useLocationMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { shippingTermsOptions } = useFactoryDetailsMaster();

  // Fetch auction categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await CommonService.get(`${API_URLS.AUCTION.BASE}/categories`);
        if (response && response.success && response.data) {
          setCategories(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error("Error fetching auction categories:", error);
        toast.error("Failed to load categories");
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch sub-categories when category changes
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!form.category_id) {
        setSubCategories([]);
        return;
      }
      try {
        const response = await CommonService.get(`${API_URLS.AUCTION.BASE}/categories/${form.category_id}/sub-categories`);
        if (response && response.success && response.data) {
          setSubCategories(Array.isArray(response.data) ? response.data : []);
        } else {
          setSubCategories([]);
        }
      } catch (error) {
        console.error("Error fetching sub-categories:", error);
        setSubCategories([]);
      }
    };
    fetchSubCategories();
  }, [form.category_id]);

  // Check if user is a seller - redirect if not
  useEffect(() => {
    const checkSellerAccess = async () => {
      const user = StorageService.getUser();
      
      if (!user) {
        toast.error('Please login to access this page');
        navigate('/login');
        return;
      }

      // Check user_type_id: 1 = BUYER, 2 = SELLER, 3 = BOTH
      let userTypeId = user.user_type_id;
      
      // Handle different formats: number, string, null, undefined
      if (userTypeId === null || userTypeId === undefined) {
        userTypeId = "";
      } else {
        userTypeId = String(userTypeId).trim();
      }
      
      // Convert to array of IDs (handle comma-separated or single value)
      const typeIds = userTypeId 
        ? userTypeId.split(",").map(id => String(id).trim()).filter(id => id && id !== "null" && id !== "undefined")
        : [];
      
      // Check if user is a seller (user_type_id = 2 or 3)
      const singleId = parseInt(userTypeId);
      const isSellerById = (!isNaN(singleId) && userTypeId.indexOf(",") === -1 && (singleId === 2 || singleId === 3)) ||
                           typeIds.some(id => {
                             const numId = parseInt(id);
                             return id === "2" || id === "3" || numId === 2 || numId === 3;
                           });
      
      // Also check company_role if available
      let isSellerByRole = false;
      try {
        const response = await CommonService.get('/company/my-profile');
        if (response && response.success && response.data) {
          const profile = response.data;
          const companyRole = profile.company_role;
          isSellerByRole = companyRole === 'SELLER' || companyRole === 'BOTH';
        }
      } catch (error) {
        console.error('Error fetching company profile for role check:', error);
      }
      
      // If user is not a seller, redirect
      if (!isSellerById && !isSellerByRole) {
        toast.error('This page is only accessible to sellers. Redirecting...');
        navigate('/dashboard/buyer');
        return;
      }
    };
    
    checkSellerAccess();
  }, [navigate]);

  useEffect(() => {
    checkCredits();
    
    // Fetch company name from user profile
    const fetchCompanyName = async () => {
      try {
        const user = StorageService.getUser();
        let userCompanyName = user?.company_name || user?.companyName || '';
        
        // If not in user object, fetch from company profile
        if (!userCompanyName) {
          try {
            const response = await CommonService.get('/company/my-profile');
            if (response && response.success && response.data) {
              userCompanyName = response.data.company_name || response.data.companyName || '';
            }
          } catch (error) {
            console.error('Error fetching company profile:', error);
          }
        }
        
        // Set company name, or use default if still empty
        if (userCompanyName && userCompanyName.trim() !== '') {
          setCompanyName(userCompanyName.trim());
        } else {
          setCompanyName('Auction'); // Default fallback
        }
      } catch (error) {
        console.error('Error fetching company name:', error);
        setCompanyName('Auction'); // Default fallback
      }
    };
    
    fetchCompanyName();
  }, []);

  // Cleanup object URLs when component unmounts or images change
  useEffect(() => {
    return () => {
      // Revoke all object URLs to prevent memory leaks
      images.forEach(img => {
        if (img instanceof File) {
          // Note: We can't revoke here because we don't store the URLs
          // The URLs are created inline in the render, so they'll be cleaned up automatically
        }
      });
    };
  }, [images]);

  const checkCredits = async () => {
    try {
      setCheckingCredits(true);
      const response = await CommonService.get(API_URLS.AUCTION.MY_CREDITS);
      if (response && response.success && response.data) {
        const availableCredits = response.data.filter(
          (c) => c.credits_available > 0 && new Date(c.valid_until) > new Date()
        );
        setHasCredits(availableCredits.length > 0);
        const primaryCredit = availableCredits[0];
        const cap = primaryCredit?.reserve_price ?? primaryCredit?.reservePrice;
        setCreditReservePriceCap(cap != null && cap !== "" ? Number(cap) : null);
        if (availableCredits.length === 0) {
          setCreditReservePriceCap(null);
          toast.info("You need to purchase auction credits first");
        }
      } else {
        setCreditReservePriceCap(null);
      }
    } catch (error) {
      console.error("Error checking credits:", error);
      setCreditReservePriceCap(null);
    } finally {
      setCheckingCredits(false);
    }
  };

  const getReservePriceValidationError = (value, reserveType = form.reserve_type) => {
    if (reserveType === "No Reserve") return "";
    const amount = parseFloat(value);
    if (!value || Number.isNaN(amount) || amount <= 0) return "";
    const cap = Number(creditReservePriceCap);
    if (!cap || cap <= 0) return "";
    if (amount > cap) {
      return `Reserve price cannot exceed ₹${cap.toLocaleString("en-IN")} (limit from Purchase Auction Credits).`;
    }
    return "";
  };

  const getBaseVsReserveValidationError = (
    baseVal,
    reserveVal,
    reserveType = form.reserve_type,
  ) => {
    if (reserveType === "No Reserve") return "";
    const base = parseFloat(baseVal);
    const reserve = parseFloat(reserveVal);
    if (!baseVal || Number.isNaN(base) || base <= 0) return "";
    if (!reserveVal || Number.isNaN(reserve) || reserve <= 0) return "";
    if (base > reserve) {
      return "Base price cannot be greater than reserve price.";
    }
    return "";
  };

  const validatePricingFields = (pricing = {}) => {
    const base = pricing.expected_price ?? form.expected_price;
    const reserve = pricing.reserve_price ?? form.reserve_price;
    const reserveType = pricing.reserve_type ?? form.reserve_type;
    setReservePriceError(getReservePriceValidationError(reserve, reserveType));
    setBasePriceError(getBaseVsReserveValidationError(base, reserve, reserveType));
  };

  const getFirstPricingValidationError = (pricing = {}) => {
    const base = pricing.expected_price ?? form.expected_price;
    const reserve = pricing.reserve_price ?? form.reserve_price;
    const reserveType = pricing.reserve_type ?? form.reserve_type;
    return (
      getReservePriceValidationError(reserve, reserveType) ||
      getBaseVsReserveValidationError(base, reserve, reserveType) ||
      ""
    );
  };

  /** Preview: only block on credit-cap breach or when both prices are set but invalid. */
  const getPreviewPricingError = (pricing = {}) => {
    const base = pricing.expected_price ?? form.expected_price;
    const reserve = pricing.reserve_price ?? form.reserve_price;
    const reserveType = pricing.reserve_type ?? form.reserve_type;
    const reserveErr = getReservePriceValidationError(reserve, reserveType);
    if (reserveErr) return reserveErr;
    const baseStr = String(base ?? "").trim();
    const res = String(reserve ?? "").trim();
    if (reserveType !== "With Reserve" || !baseStr || !res) return "";
    return getBaseVsReserveValidationError(base, reserve, reserveType);
  };

  const getPublishPricingError = () => {
    if (form.reserve_type === "With Reserve") {
      const reserve = parseFloat(form.reserve_price);
      const base = parseFloat(form.expected_price);
      if (!form.reserve_price || Number.isNaN(reserve) || reserve <= 0) {
        return "Reserve price is required.";
      }
      if (!form.expected_price || Number.isNaN(base) || base <= 0) {
        return "Base price is required.";
      }
    }
    return getFirstPricingValidationError();
  };

  const reservePriceNum =
    form.reserve_price && !Number.isNaN(parseFloat(form.reserve_price))
      ? parseFloat(form.reserve_price)
      : null;

  const handleInputChange = (field, value) => {
    const nextPricing = {
      expected_price: field === "expected_price" ? value : form.expected_price,
      reserve_price: field === "reserve_price" ? value : form.reserve_price,
      reserve_type: field === "reserve_type" ? value : form.reserve_type,
    };
    setForm((prev) => ({ ...prev, [field]: value }));

    if (
      field === "expected_price" ||
      field === "reserve_price" ||
      field === "reserve_type"
    ) {
      validatePricingFields(nextPricing);
    }

    // Reset sub-category when category changes
    if (field === "category_id") {
      setForm((prev) => ({ ...prev, sub_category_id: null }));
      // Sub-categories will be fetched automatically via useEffect
    }
    
    // Update states when country changes
    if (field === "country") {
      const countryObj = countries.find(c => c.id === value || c.label === value);
      if (countryObj) {
        updateListsForCountry(countryObj.id);
        setForm((prev) => ({ ...prev, state: "", city: "" }));
      }
    }
    
    // Update cities when state changes
    if (field === "state") {
      const stateObj = states.find(s => s.id === value || s.label === value);
      if (stateObj) {
        const countryObj = countries.find(c => c.id === form.country || c.label === form.country);
        if (countryObj) {
          updateListsForState(countryObj.id, stateObj.id);
        }
        setForm((prev) => ({ ...prev, city: "" }));
      }
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim()) {
      setForm((prev) => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()],
      }));
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (index) => {
    setForm((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index),
    }));
  };

  const handleAddCustomAttribute = () => {
    setCustomAttributes([...customAttributes, { id: Date.now(), key: "", value: "", unit: "" }]);
  };

  const handleRemoveCustomAttribute = (id) => {
    if (customAttributes.length > 1) {
      const attrToRemove = customAttributes.find(attr => attr.id === id);
      if (attrToRemove && attrToRemove.key) {
        setForm((prev) => {
          const newAttributes = { ...prev.product_attributes };
          delete newAttributes[attrToRemove.key];
          return { ...prev, product_attributes: newAttributes };
        });
      }
      setCustomAttributes(customAttributes.filter(attr => attr.id !== id));
    }
  };

  const handleUpdateCustomAttribute = (id, field, value) => {
    const updatedAttributes = customAttributes.map(attr => {
      if (attr.id === id) {
        const updatedAttr = { ...attr, [field]: value };
        
        // Update form product_attributes when key or value changes
        if (field === 'key' || field === 'value') {
          // Remove old key from form if key changed
          if (field === 'key' && attr.key) {
            setForm((prev) => {
              const newAttributes = { ...prev.product_attributes };
              delete newAttributes[attr.key];
              return { ...prev, product_attributes: newAttributes };
            });
          }
          
          // Add new key-value to form
          if (updatedAttr.key && updatedAttr.value) {
            const attributeKey = updatedAttr.key.trim();
            const attributeValue = updatedAttr.value.trim();
            setForm((prev) => ({
              ...prev,
              product_attributes: { ...prev.product_attributes, [attributeKey]: attributeValue },
            }));
          }
        }
        
        return updatedAttr;
      }
      return attr;
    });
    setCustomAttributes(updatedAttributes);
  };

  const handleAttributeChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      product_attributes: { ...prev.product_attributes, [field]: value },
    }));
  };

  const handleRemoveInspectionChecklist = (index) => {
    setForm((prev) => ({
      ...prev,
      inspection_checklist: prev.inspection_checklist.filter((_, i) => i !== index),
    }));
  };

  const addFAQ = () => {
    const newId = Math.max(...faqList.map(f => f.id), 0) + 1;
    setFaqList([...faqList, { id: newId, question: '', answer: '' }]);
  };

  const removeFAQ = (id) => {
    if (faqList.length > 1) {
      setFaqList(faqList.filter(faq => faq.id !== id));
    }
  };

  const updateFAQ = (id, field, value) => {
    setFaqList(faqList.map(faq =>
      faq.id === id ? { ...faq, [field]: value } : faq
    ));
  };

  const handleAddInspectionChecklist = () => {
    if (inspectionChecklistInput.trim()) {
      setForm((prev) => ({
        ...prev,
        inspection_checklist: [...prev.inspection_checklist, inspectionChecklistInput.trim()],
      }));
      setInspectionChecklistInput("");
    }
  };


  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;
    
    // Check total images (new files + existing)
    const totalImages = images.length + uploadedImageUrls.length;
    if (totalImages + files.length > 10) {
      toast.error(`Maximum 10 images allowed. You can add ${10 - totalImages} more.`);
      e.target.value = '';
      return;
    }
    
    // Validate file types
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length !== files.length) {
      toast.error("Please select only image files");
      e.target.value = '';
      return;
    }
    
    // Validate file size (max 5MB per image)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = imageFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      toast.error("Image size should not exceed 5MB");
      e.target.value = '';
      return;
    }
    
    const remainingSlots = 10 - totalImages;
    const newFiles = imageFiles.slice(0, remainingSlots);
    setImages((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;
    
    // Check total videos (new files + existing)
    const totalVideos = videos.length + uploadedVideoUrls.length;
    if (totalVideos + files.length > 2) {
      toast.error(`Maximum 2 videos allowed. You can add ${2 - totalVideos} more.`);
      e.target.value = '';
      return;
    }
    
    // Validate file types
    const videoFiles = files.filter(file => file.type.startsWith('video/'));
    if (videoFiles.length !== files.length) {
      toast.error("Please select only video files");
      e.target.value = '';
      return;
    }
    
    // Validate file size (max 200MB per video)
    const maxSize = 200 * 1024 * 1024; // 200MB
    const oversizedFiles = videoFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      toast.error("Video size should not exceed 200MB");
      e.target.value = '';
      return;
    }
    
    const remainingSlots = 2 - totalVideos;
    const newFiles = videoFiles.slice(0, remainingSlots);
    setVideos((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  };

  // Helper function to get full image/video URL from access path
  const getMediaUrl = (accessPath) => {
    if (!accessPath) return '';
    
    // If already a full URL, return as is
    if (accessPath.startsWith('http://') || accessPath.startsWith('https://')) {
      return accessPath;
    }
    
    const baseUrl = 'http://localhost:3000';
    
    // Handle old path formats that might have Windows absolute paths
    if (accessPath.includes('C:/usr/local/b2b/seller/AuctionProduct') || accessPath.includes('C:\\usr\\local\\b2b\\seller\\AuctionProduct')) {
      // Extract auction ID and filename from path format
      const match = accessPath.match(/AuctionProduct[\/\\](\d+)[\/\\]([^\/\\]+)$/);
      if (match) {
        const auctionId = match[1];
        const fileName = match[2];
        return `${baseUrl}/auction-media/${auctionId}/${fileName}`;
      }
      // Handle temp folder
      const tempMatch = accessPath.match(/AuctionProduct[\/\\]temp[\/\\]([^\/\\]+)$/);
      if (tempMatch) {
        const fileName = tempMatch[1];
        return `${baseUrl}/auction-media/temp/${fileName}`;
      }
    }
    
    // If relative path, prepend base URL
    let cleanPath = accessPath;
    if (cleanPath.startsWith(':3000')) {
      cleanPath = cleanPath.replace(/^:3000/, '');
    }
    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
    return `${baseUrl}${cleanPath}`;
  };

  // Remove new file (not yet uploaded)
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, idx) => idx !== index));
  };

  const removeVideo = (index) => {
    setVideos(prev => prev.filter((_, idx) => idx !== index));
  };

  // Remove existing file URL (already uploaded)
  const removeImageUrl = (index) => {
    setUploadedImageUrls(prev => prev.filter((_, idx) => idx !== index));
  };

  const removeVideoUrl = (index) => {
    setUploadedVideoUrls(prev => prev.filter((_, idx) => idx !== index));
  };

  // Upload file to backend and return URL
  const uploadFile = async (file, fileType = 'auction_image', category = 'auction', auctionId = null) => {
    try {
      if (!file || !(file instanceof File)) {
        throw new Error('Invalid file provided');
      }
      
      const uploadCompanyName = companyName || 'Auction';
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('companyName', uploadCompanyName);
      formData.append('fileType', fileType);
      
      // Pass auction ID if available (for auction-specific folder path)
      if (auctionId) {
        formData.append('auctionId', auctionId);
      }

      console.log('Uploading file:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        category,
        fileTypeParam: fileType,
        companyName: uploadCompanyName
      });

      const response = await CommonService.post('/upload/single', formData);

      console.log('Upload response:', response);

      if (response && response.success && response.data) {
        const accessPath = response.data.accessPath || response.data.filePath || '';
        console.log('File uploaded successfully, access path:', accessPath);
        return accessPath;
      }
      throw new Error(response?.message || 'Upload failed - no access path returned');
    } catch (error) {
      console.error('File upload error:', {
        fileName: file?.name,
        error: error.message,
        response: error?.response?.data,
        status: error?.response?.status,
        fullError: error
      });
      
      // Extract detailed error message
      let errorMessage = `Failed to upload ${fileType}`;
      
      if (error?.response?.data) {
        // Check for validation errors
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessage = error.response.data.errors[0] || errorMessage;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      console.error('Upload error details:', {
        fileName: file?.name,
        errorMessage,
        status: error?.response?.status,
        data: error?.response?.data
      });
      
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasCredits) {
      toast.error("Please purchase auction credits first");
      navigate("/auction/purchase-credits");
      return;
    }

    const pricingErr = getPreviewPricingError();
    if (pricingErr) {
      validatePricingFields();
      toast.error(pricingErr);
      return;
    }
    setBasePriceError("");
    setReservePriceError("");

    // Show preview instead of creating auction immediately
    setShowPreview(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToEdit = () => {
    setShowPreview(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePublishAuction = async () => {
    const pricingErr = getPublishPricingError();
    if (pricingErr) {
      validatePricingFields();
      toast.error(pricingErr);
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload new files first (to temp folder since we don't have auctionId yet)
      const newImageUrls = [];
      for (const file of images) {
        // Only upload if it's a File object, not already a URL
        if (file instanceof File) {
          try {
            const fileUrl = await uploadFile(file, 'auction_image', 'auction', null);
            if (fileUrl && fileUrl.trim() !== '') {
              newImageUrls.push(fileUrl);
              console.log('Successfully uploaded image, URL:', fileUrl);
            } else {
              console.warn('Image uploaded but no URL returned:', file.name);
            }
          } catch (error) {
            console.error('Error uploading image:', file.name, error);
            toast.error(`Failed to upload ${file.name}. Please try again.`);
            // Continue with other files even if one fails
          }
        } else if (typeof file === 'string') {
          // Already a URL, use it directly
          newImageUrls.push(file);
        }
      }

      const newVideoUrls = [];
      for (const file of videos) {
        // Only upload if it's a File object, not already a URL
        if (file instanceof File) {
          try {
            const fileUrl = await uploadFile(file, 'auction_video', 'auction', null);
            if (fileUrl && fileUrl.trim() !== '') {
              newVideoUrls.push(fileUrl);
              console.log('Successfully uploaded video, URL:', fileUrl);
            } else {
              console.warn('Video uploaded but no URL returned:', file.name);
            }
          } catch (error) {
            console.error('Error uploading video:', file.name, error);
            toast.error(`Failed to upload ${file.name}. Please try again.`);
            // Continue with other files even if one fails
          }
        } else if (typeof file === 'string') {
          // Already a URL, use it directly
          newVideoUrls.push(file);
        }
      }

      // Combine new file URLs with existing URLs
      const allImageUrls = [...uploadedImageUrls, ...newImageUrls];
      const allVideoUrls = [...uploadedVideoUrls, ...newVideoUrls];

      // If some files failed to upload, warn the user
      if (newImageUrls.length < images.filter(f => f instanceof File).length) {
        toast.warning(`${images.filter(f => f instanceof File).length - newImageUrls.length} image(s) failed to upload. Continuing with ${newImageUrls.length} successful upload(s).`);
      }
      if (newVideoUrls.length < videos.filter(f => f instanceof File).length) {
        toast.warning(`${videos.filter(f => f instanceof File).length - newVideoUrls.length} video(s) failed to upload. Continuing with ${newVideoUrls.length} successful upload(s).`);
      }

      // Map faqList to form.faqs format
      const validFAQs = faqList
        .filter(faq => faq.question && faq.question.trim() && faq.answer && faq.answer.trim())
        .map(faq => ({
          question: faq.question.trim(),
          answer: faq.answer.trim()
        }));

      // Map IDs to labels for master data fields
      const countryLabel = form.country ? (countries.find(c => c.id === form.country || c.label === form.country)?.label || form.country) : "";
      const stateLabel = form.state ? (states.find(s => s.id === form.state || s.label === form.state)?.label || form.state) : "";
      const cityLabel = form.city ? (cities.find(c => c.id === form.city || c.label === form.city)?.label || form.city) : "";
      const uomLabel = form.unit_of_measure ? (attributeUnitOptions.find(u => u.value === form.unit_of_measure || u.label === form.unit_of_measure)?.label || form.unit_of_measure) : "";
      const incotermsLabel = form.incoterms ? (shippingTermsOptions.find(i => i.value === form.incoterms || i.label === form.incoterms)?.label || form.incoterms) : "";

      // Clean product_attributes - remove empty values to store only meaningful data
      const cleanedProductAttributes = {};
      if (form.product_attributes) {
        Object.keys(form.product_attributes).forEach(key => {
          const value = form.product_attributes[key];
          // Only include non-empty values (not null, undefined, empty string, or empty array)
          if (value !== null && value !== undefined && value !== '' && 
              !(Array.isArray(value) && value.length === 0)) {
            cleanedProductAttributes[key] = value;
          }
        });
      }

      const auctionData = {
        ...form,
        faqs: validFAQs,
        images: allImageUrls,
        videos: allVideoUrls,
        product_attributes: cleanedProductAttributes, // Explicitly include cleaned product_attributes
        category_id: form.category_id ? parseInt(form.category_id) : null,
        sub_category_id: form.sub_category_id ? parseInt(form.sub_category_id) : null,
        quantity: parseFloat(form.quantity) || 1,
        unit_of_measure: uomLabel,
        country: countryLabel,
        state: stateLabel,
        city: cityLabel,
        incoterms: incotermsLabel || null,
        expected_price: form.expected_price ? parseFloat(form.expected_price) : null,
        base_price: form.expected_price ? parseFloat(form.expected_price) : null,
        reserve_price: form.reserve_price ? parseFloat(form.reserve_price) : null,
        bid_increment: parseFloat(form.bid_increment) || 0,
        gst_rate: parseFloat(form.gst_rate) || 18,
        cgst_rate: parseFloat(form.cgst_rate) || 9,
        sgst_rate: parseFloat(form.sgst_rate) || 9,
        igst_rate: parseFloat(form.igst_rate) || 18,
        pickup_window_days: parseInt(form.pickup_window_days) || 7,
        start_date: form.start_date ? new Date(form.start_date + 'T00:00:00').toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date + 'T23:59:59').toISOString() : null,
        inspection_start_date: form.inspection_start_date
          ? new Date(form.inspection_start_date + 'T00:00:00').toISOString()
          : null,
        inspection_end_date: form.inspection_end_date
          ? new Date(form.inspection_end_date + 'T23:59:59').toISOString()
          : null,
      };

      // Create auction
      const response = await CommonService.post(API_URLS.AUCTION.CREATE, auctionData);

      if (response && response.success) {
        const auctionId = response.data.auctionId;
        setCreatedAuctionId(auctionId);
        
        const publishRes = await CommonService.post(API_URLS.AUCTION.PUBLISH(auctionId), {});
        const publishData = parseAuctionPublishResponse(publishRes);
        const needsApproval = auctionRequiresListingApproval(publishData);

        if (needsApproval || publishData?.published === false) {
          toast.info(
            publishData.message ||
              "Auction submitted for approval. It will appear on the listing after approvers complete the workflow.",
          );
          if (p2pLaunch?.p2p_transaction_id && auctionId) {
            const linked = await completeP2pRfqLinkAfterModuleCreate({
              searchParams,
              p2pLaunchContext: p2pLaunch,
              sourceModule: "auction",
              sourceRfqId: auctionId,
              sourceRfqTitle: form.product_name || null,
              sourceRfqStatus: "pending_approval",
              navigate,
            });
            if (linked) return;
          }
          navigate("/auction/my/auctions");
          return;
        }

        if (publishData?.skip_reason) {
          toast.info(publishData.skip_reason, { autoClose: 8000 });
        }
        toast.success(publishData?.message || "Auction published successfully!");
        if (p2pLaunch?.p2p_transaction_id && auctionId) {
          const linked = await completeP2pRfqLinkAfterModuleCreate({
            searchParams,
            p2pLaunchContext: p2pLaunch,
            sourceModule: "auction",
            sourceRfqId: auctionId,
            sourceRfqTitle: form.product_name || null,
            sourceRfqStatus: "published",
            navigate,
          });
          if (linked) return;
        }
        navigate(`/auction/${auctionId}`);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to publish auction";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get category label from API data
  const getCategoryLabel = () => {
    if (!form.category_id) return "";
    const category = categories.find(cat => cat.category_id === parseInt(form.category_id));
    return category ? category.category_name : "";
  };

  // Helper function to get sub-category label from API data
  const getSubCategoryLabel = () => {
    if (!form.category_id || !form.sub_category_id) return "";
    const subCat = subCategories.find(sc => sc.sub_category_id === parseInt(form.sub_category_id));
    return subCat ? subCat.sub_category_name : "";
  };

  // Helper function to get unit of measure label
  const getUOMLabel = () => {
    if (!form.unit_of_measure) return '';
    
    // Try to find by value (ID) first
    const uomById = attributeUnitOptions.find(u => 
      String(u.value) === String(form.unit_of_measure) || 
      Number(u.value) === Number(form.unit_of_measure)
    );
    if (uomById) return uomById.label;
    
    // Try to find by label (in case it's already a label)
    const uomByLabel = attributeUnitOptions.find(u => 
      String(u.label) === String(form.unit_of_measure)
    );
    if (uomByLabel) return uomByLabel.label;
    
    // If not found, return empty string instead of the ID
    return '';
  };

  if (checkingCredits) {
    return <div className="loading">Checking credits...</div>;
  }

  return (
    <div className="create-auction-page">
      <div className="container">
        {showPreview && (
          <div className="preview-section">
            <button 
              type="button" 
              className="rfq-back-btn" 
              onClick={handleBackToEdit}
              style={{ marginBottom: '20px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back
            </button>
            <div className="preview-card">
              <h2>Preview Your Auction</h2>
              
              {/* Images */}
              <div className="preview-images">
                <div className="image-gallery">
                  {(images.length > 0 || uploadedImageUrls.length > 0) ? (
                    <>
                      {/* Display existing uploaded images */}
                      {uploadedImageUrls.map((url, idx) => {
                        const imageUrl = getMediaUrl(url);
                        return (
                          <div key={`url-${idx}`} className="image-thumbnail">
                            <img
                              src={imageUrl}
                              alt={`Uploaded ${idx + 1}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="image-placeholder" style={{ display: 'none' }}>
                              {url.split('/').pop()}
                            </div>
                          </div>
                        );
                      })}
                      {/* Display new files with preview */}
                      {images.map((img, index) => {
                        // Handle both File objects and URL strings
                        let imageSrc = null;
                        if (typeof img === 'string') {
                          // If it's already a URL string, use it directly
                          imageSrc = getMediaUrl(img);
                        } else if (img instanceof File) {
                          // Create object URL for File objects
                          imageSrc = URL.createObjectURL(img);
                        } else if (img && img.name) {
                          imageSrc = null; // File name only, no preview available
                        }
                        
                        return (
                          <div key={`file-${index}`} className="image-thumbnail">
                            {imageSrc ? (
                              <img 
                                src={imageSrc} 
                                alt={`Preview ${index + 1}`}
                                onError={(e) => {
                                  // Fallback if image fails to load
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className="image-placeholder" 
                              style={{ display: imageSrc ? 'none' : 'flex' }}
                            >
                              {typeof img === 'string' 
                                ? img.split('/').pop() 
                                : (img?.name || `Image ${index + 1}`)}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="no-images">No images uploaded</div>
                  )}
                </div>
              </div>

              {/* Item Details */}
              <div className="preview-item-details">
                <h3>{form.product_name || "Untitled Product"}</h3>
                {form.product_description && (
                  <p className="preview-description">{form.product_description}</p>
                )}
                <div className="item-meta">
                  <div className="meta-row">
                    <span className="meta-label">Category:</span>
                    <span className="meta-value">{getCategoryLabel() || "Not selected"}</span>
                  </div>
                  {getSubCategoryLabel() && (
                    <div className="meta-row">
                      <span className="meta-label">Sub-Category:</span>
                      <span className="meta-value">{getSubCategoryLabel()}</span>
                    </div>
                  )}
                  <div className="meta-row">
                    <span className="meta-label">Lot Type:</span>
                    <span className="meta-value">{form.lot_type || "Single Item"}</span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Reserve:</span>
                    <span className="meta-value">{form.reserve_type || "With Reserve"}</span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Quantity:</span>
                    <span className="meta-value">{form.quantity || "1"} {getUOMLabel()}</span>
                  </div>
                  {form.reserve_price && form.reserve_type === "With Reserve" && (
                    <div className="meta-row">
                      <span className="meta-label">Reserve Price:</span>
                      <span className="meta-value">₹{parseFloat(form.reserve_price).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {form.expected_price && (
                    <div className="meta-row">
                      <span className="meta-label">Base Price:</span>
                      <span className="meta-value">₹{parseFloat(form.expected_price).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {form.bid_increment && (
                    <div className="meta-row">
                      <span className="meta-label">Bid Increment:</span>
                      <span className="meta-value">₹{parseFloat(form.bid_increment).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Important Message */}
              <div className="preview-important-message">
                <p>Important: You maintain full ownership and risk until buyer acceptance.</p>
              </div>

              {/* Action Buttons */}
              <div className="preview-actions">
                <button
                  type="button"
                  className="rfq-back-btn"
                  onClick={handleBackToEdit}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m12 19-7-7 7-7"></path>
                    <path d="M19 12H5"></path>
                  </svg>
                  Back
                </button>
                <button
                  type="button"
                  className="btn-publish"
                  onClick={handlePublishAuction}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Publishing..." : "Publish Auction"}
                </button>
              </div>
            </div>
          </div>
        )}

        {!showPreview && (
          <div className="page-header">
            <h1>Auction Your Products</h1>
            <p>Complete all required fields</p>
          </div>
        )}

        {!hasCredits && (
          <div className="credit-warning">
            <p>You need to purchase auction credits first.</p>
            <button
              className="btn-primary"
              onClick={() => navigate("/auction/purchase-credits")}
            >
              Purchase Credits
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className={`auction-form ${showPreview ? 'hidden' : ''}`}>
          {/* Product Information */}
          <section className="form-section">
            <h2>Product Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={form.product_name}
                  onChange={(e) => handleInputChange("product_name", e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Lot Type *</label>
                <select
                  value={form.lot_type}
                  onChange={(e) => handleInputChange("lot_type", e.target.value)}
                  required
                >
                  <option value="Single Item">Single Item</option>
                  <option value="Lot/Bulk">Lot/Bulk</option>
                </select>
              </div>

              <div className="form-group">
                <label>Reserve Type *</label>
                <select
                  value={form.reserve_type}
                  onChange={(e) => handleInputChange("reserve_type", e.target.value)}
                  required
                >
                  <option value="With Reserve">With Reserve</option>
                  <option value="No Reserve">No Reserve</option>
                </select>
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={form.category_id || ""}
                  onChange={(e) => handleInputChange("category_id", e.target.value)}
                  required
                  disabled={loadingCategories}
                >
                  <option value="">{loadingCategories ? "Loading categories..." : "Select Category"}</option>
                  {categories.map((cat) => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Sub-Category *</label>
                <select
                  value={form.sub_category_id || ""}
                  onChange={(e) => handleInputChange("sub_category_id", e.target.value)}
                  required
                  disabled={!form.category_id || subCategories.length === 0}
                >
                  <option value="">
                    {!form.category_id 
                      ? "Select Category first" 
                      : subCategories.length === 0 
                        ? "Loading sub-categories..." 
                        : "Select Sub-Category"}
                  </option>
                  {subCategories.map((subCat) => (
                    <option key={subCat.sub_category_id} value={subCat.sub_category_id}>
                      {subCat.sub_category_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Quantity *</label>
                <input
                  type="number"
                  value={form.quantity}
                  onChange={(e) => handleInputChange("quantity", e.target.value)}
                  required
                  min="1"
                />
              </div>

              <div className="form-group">
                <label>Unit of Measure *</label>
                <select
                  value={form.unit_of_measure}
                  onChange={(e) => handleInputChange("unit_of_measure", e.target.value)}
                  required
                >
                  {attributeUnitOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group full-width">
                <label>Description *</label>
                <textarea
                  value={form.product_description}
                  onChange={(e) => handleInputChange("product_description", e.target.value)}
                  rows="4"
                  required
                  placeholder="Detailed product description with specifications and condition"
                />
              </div>
            </div>
          </section>

          {/* Keywords */}
          <section className="form-section">
            <h2>Keywords (For Search)</h2>
            <div className="keyword-input">
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddKeyword())}
                placeholder="Enter keywords (e.g., BMW, luxury, sedan)"
              />
              <button type="button" onClick={handleAddKeyword}>
                + Add
              </button>
            </div>
            <div className="keyword-tags">
              {form.keywords.map((keyword, index) => (
                <span key={index} className="keyword-tag">
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(index)}
                    className="remove-btn"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </section>

          {/* Product Attributes */}
          {!form.category_id ? (
            <section className="form-section">
              <h2>Product Attributes</h2>
              <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontStyle: 'italic' }}>
                No attributes. Please select any category
              </div>
            </section>
          ) : parseInt(form.category_id) === 12 ? (
            <>
              {/* Vehicle Section */}
              <section className="form-section">
                <h2>Vehicle</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition/Grade</label>
                    <select
                      value={form.product_attributes.condition_grade || ""}
                      onChange={(e) => handleAttributeChange("condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Rental">Rental</option>
                      <option value="Reconditioned">Reconditioned</option>
                      <option value="Demo/Ex-display">Demo/Ex-display</option>
                      <option value="Certified Pre-owned">Certified Pre-owned</option>
                      <option value="Overstock">Overstock</option>
                      <option value="Damaged Packaging">Damaged Packaging</option>
                      <option value="Returns">Returns</option>
                      <option value="Medical Grade">Medical Grade</option>
                      <option value="Industrial Grade">Industrial Grade</option>
                      <option value="Export Grade">Export Grade</option>
                      <option value="Sterile Grade">Sterile Grade</option>
                      <option value="Non-Sterile Grade">Non-Sterile Grade</option>
                      <option value="Single-use/Disposable">Single-use/Disposable</option>
                      <option value="Demo/Sample Unit">Demo/Sample Unit</option>
                      <option value="A+">A+</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="As per ITAD">As per ITAD</option>
                      <option value="Refurb Standard">Refurb Standard</option>
                      <option value="Like New">Like New</option>
                      <option value="Used - Good">Used - Good</option>
                      <option value="Used - Fair">Used - Fair</option>
                      <option value="Damaged/Scrap">Damaged/Scrap</option>
                      <option value="Production surplus">Production surplus</option>
                      <option value="Overstock/rejected but unused">Overstock/rejected but unused</option>
                      <option value="Free from major contamination">Free from major contamination</option>
                      <option value="Pre-sorted by type">Pre-sorted by type</option>
                      <option value="High-grade recyclable">High-grade recyclable</option>
                      <option value="Mixed material streams">Mixed material streams</option>
                      <option value="Requires segregation before recycling">Requires segregation before recycling</option>
                      <option value="With oil, grease, paint, or coatings">With oil, grease, paint, or coatings</option>
                      <option value="With hazardous residues">With hazardous residues</option>
                      <option value="With moisture or rust">With moisture or rust</option>
                      <option value="Broken, worn-out">Broken, worn-out</option>
                      <option value="Dismantled parts">Dismantled parts</option>
                      <option value="Expired equipment/machinery">Expired equipment/machinery</option>
                      <option value="Shredded/cut/baled">Shredded/cut/baled</option>
                      <option value="Ready-to-recycle form">Ready-to-recycle form</option>
                      <option value="Bulk/raw waste without treatment">Bulk/raw waste without treatment</option>
                      <option value="Recently built">Recently built</option>
                      <option value="never occupied">never occupied</option>
                      <option value="Ongoing development">Ongoing development</option>
                      <option value="With RERA registration">With RERA registration</option>
                      <option value="Delivery timeline available">Delivery timeline available</option>
                      <option value="Fully constructed, immediately usable">Fully constructed, immediately usable</option>
                      <option value="With all utilities connected">With all utilities connected</option>
                      <option value="Recently upgraded interiors/exteriors">Recently upgraded interiors/exteriors</option>
                      <option value="Modernized facilities">Modernized facilities</option>
                      <option value="Pre-owned but functional and in good condition">Pre-owned but functional and in good condition</option>
                      <option value="Regular maintenance records available">Regular maintenance records available</option>
                      <option value="Structurally safe but requires cosmetic/minor repair work">Structurally safe but requires cosmetic/minor repair work</option>
                      <option value="Outdated interiors, plumbing, or electricals">Outdated interiors, plumbing, or electricals</option>
                      <option value="Structurally weak or condemned building">Structurally weak or condemned building</option>
                      <option value="Not suitable for immediate occupancy without major restoration">Not suitable for immediate occupancy without major restoration</option>
                      <option value="Fresh/Recently Harvested">Fresh/Recently Harvested</option>
                      <option value="Well-Preserved/Properly Stored">Well-Preserved/Properly Stored</option>
                      <option value="Aged but Usable">Aged but Usable</option>
                      <option value="Damaged/Infested">Damaged/Infested</option>
                      <option value="Spoiled/Unfit for Consumption">Spoiled/Unfit for Consumption</option>
                      <option value="Grade A/Premium/Export Quality">Grade A/Premium/Export Quality</option>
                      <option value="Grade B/Standard/Domestic Quality">Grade B/Standard/Domestic Quality</option>
                      <option value="Grade C/Fair/Processing Quality">Grade C/Fair/Processing Quality</option>
                      <option value="Grade D/Low/Feed Grade">Grade D/Low/Feed Grade</option>
                      <option value="Breeding Grade">Breeding Grade</option>
                      <option value="Dairy Grade">Dairy Grade</option>
                      <option value="Meat Grade">Meat Grade</option>
                      <option value="Work Grade">Work Grade</option>
                      <option value="Cull/Low Grade">Cull/Low Grade</option>
                      <option value="Wool Quality">Wool Quality</option>
                      <option value="Milk Grade">Milk Grade</option>
                      <option value="Cull Stock">Cull Stock</option>
                      <option value="Broiler Grade">Broiler Grade</option>
                      <option value="Layer Grade">Layer Grade</option>
                      <option value="Chick Quality">Chick Quality</option>
                      <option value="Egg Production Grade">Egg Production Grade</option>
                      <option value="Table Fish Grade">Table Fish Grade</option>
                      <option value="Shrimp/Fish Size Grade">Shrimp/Fish Size Grade</option>
                      <option value="Quality Grade">Quality Grade</option>
                      <option value="Hatchery/Fingerling Grade">Hatchery/Fingerling Grade</option>
                      <option value="Table Eggs - Grade A">Table Eggs - Grade A</option>
                      <option value="B (minor defects), C (cracks)">B (minor defects), C (cracks)</option>
                      <option value="Size Grade">Size Grade</option>
                      <option value="Specialty Grade">Specialty Grade</option>
                      <option value="Milk Grade - A">Milk Grade - A</option>
                      <option value="B (standard)">B (standard)</option>
                      <option value="C (low fat %)">C (low fat %)</option>
                      <option value="FSSAI">FSSAI</option>
                      <option value="BIS">BIS</option>
                      <option value="Export Grade Certified">Export Grade Certified</option>
                      <option value="Quality Indicators - Fat %, SNF (solids-not-fat), Protein %">Quality Indicators - Fat %, SNF (solids-not-fat), Protein %</option>
                      <option value="Leather Grade">Leather Grade</option>
                      <option value="Wool Grade">Wool Grade</option>
                      <option value="A-Grade (Prime/Clear/Select Grade)">A-Grade (Prime/Clear/Select Grade)</option>
                      <option value="B-Grade (Standard/Utility Grade)">B-Grade (Standard/Utility Grade)</option>
                      <option value="C-Grade (Economy/Industrial Grade)">C-Grade (Economy/Industrial Grade)</option>
                      <option value="Appearance Grades">Appearance Grades</option>
                      <option value="Moisture Content Based">Moisture Content Based</option>
                      <option value="Premium/Furniture Grade">Premium/Furniture Grade</option>
                      <option value="Construction Grade">Construction Grade</option>
                      <option value="Industrial Grade">Industrial Grade</option>
                      <option value="Craft/Handicraft Grade">Craft/Handicraft Grade</option>
                      <option value="Calorific Value Grade">Calorific Value Grade</option>
                      <option value="Moisture Content Grade">Moisture Content Grade</option>
                      <option value="Charcoal Grade">Charcoal Grade</option>
                      <option value="Food Grade/Pharmaceutical Grade">Food Grade/Pharmaceutical Grade</option>
                      <option value="Organic Certified/Conventional">Organic Certified/Conventional</option>
                      <option value="Purity Grade - % pure extract">Purity Grade - % pure extract</option>
                      <option value="Export/Premium Grade">Export/Premium Grade</option>
                      <option value="Furniture Grade">Furniture Grade</option>
                      <option value="Craft Grade - Thin, highly flexible, defect-free">Craft Grade - Thin, highly flexible, defect-free</option>
                      <option value="Utility Grade">Utility Grade</option>
                      <option value="Fresh Cut (Green)">Fresh Cut (Green)</option>
                      <option value="Air-Dried">Air-Dried</option>
                      <option value="Kiln-Dried">Kiln-Dried</option>
                      <option value="Seasoned">Seasoned</option>
                      <option value="Processed/Finished">Processed/Finished</option>
                      <option value="Weathered/Aged">Weathered/Aged</option>
                      <option value="Reclaimed/Salvaged">Reclaimed/Salvaged</option>
                      <option value="Mint/Pristine">Mint/Pristine</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Very Good">Very Good</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                      <option value="Restored">Restored</option>
                      <option value="Original Condition">Original Condition</option>
                      <option value="Unrestored">Unrestored</option>
                      <option value="Restored/Conserved">Restored/Conserved</option>
                      <option value="Surface Wear/Patina">Surface Wear/Patina</option>
                      <option value="Cracks/Chips/Losses">Cracks/Chips/Losses</option>
                      <option value="Re-framed/Re-mounted">Re-framed/Re-mounted</option>
                      <option value="Uncirculated (UNC/Mint State)">Uncirculated (UNC/Mint State)</option>
                      <option value="About Uncirculated (AU)">About Uncirculated (AU)</option>
                      <option value="Extremely Fine (EF/XF)">Extremely Fine (EF/XF)</option>
                      <option value="Very Fine (VF)">Very Fine (VF)</option>
                      <option value="Fine (F)">Fine (F)</option>
                      <option value="Good (G)">Good (G)</option>
                      <option value="About Good (AG)">About Good (AG)</option>
                      <option value="Proof">Proof</option>
                      <option value="Mint Never Hinged (MNH)">Mint Never Hinged (MNH)</option>
                      <option value="Mint Hinged (MH)">Mint Hinged (MH)</option>
                      <option value="Unused/No Gum (NG)">Unused/No Gum (NG)</option>
                      <option value="Used/Cancelled">Used/Cancelled</option>
                      <option value="Factory Sealed/Original Packaging">Factory Sealed/Original Packaging</option>
                      <option value="Lightly Used">Lightly Used</option>
                      <option value="Display Wear">Display Wear</option>
                      <option value="Damaged/Repaired">Damaged/Repaired</option>
                      <option value="Incomplete/Missing Parts">Incomplete/Missing Parts</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life/ Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.packaging_unit || opt.label === form.product_attributes.packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.packaging_type || ""}
                      onChange={(e) => handleAttributeChange("packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Bottle">Bottle</option>
                      <option value="Pouch">Pouch</option>
                      <option value="Jar">Jar</option>
                      <option value="Can">Can</option>
                      <option value="Tetra Pack">Tetra Pack</option>
                      <option value="Sachet">Sachet</option>
                      <option value="Box">Box</option>
                      <option value="Carton">Carton</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.lot_size || ""}
                      onChange={(e) => handleAttributeChange("lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no/ lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Registration Certificate (RC)">Registration Certificate (RC)</option>
                      <option value="Valid Insurance">Valid Insurance</option>
                      <option value="Pollution Under Control (PUC)">Pollution Under Control (PUC)</option>
                      <option value="Driving License">Driving License</option>
                      <option value="Fitness Certificate">Fitness Certificate</option>
                      <option value="Commercial Vehicle Permit">Commercial Vehicle Permit</option>
                      <option value="Road Tax Receipt">Road Tax Receipt</option>
                      <option value="National / State Permit">National / State Permit</option>
                      <option value="Speed Governor Certificate">Speed Governor Certificate</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Registration Number</label>
                    <input
                      type="text"
                      value={form.product_attributes.registration_number || ""}
                      onChange={(e) => handleAttributeChange("registration_number", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>VIN/Chassis Number</label>
                    <input
                      type="text"
                      value={form.product_attributes.vin_chassis_number || ""}
                      onChange={(e) => handleAttributeChange("vin_chassis_number", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Insurance Status</label>
                    <select
                      value={form.product_attributes.insurance_status || ""}
                      onChange={(e) => handleAttributeChange("insurance_status", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Valid">Valid</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Model</label>
                    <input
                      type="text"
                      value={form.product_attributes.model || ""}
                      onChange={(e) => handleAttributeChange("model", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Road Permit</label>
                    <select
                      value={form.product_attributes.road_permit || ""}
                      onChange={(e) => handleAttributeChange("road_permit", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Region specific">Region specific</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Odometer/Hours</label>
                    <input
                      type="text"
                      value={form.product_attributes.odometer_hours || ""}
                      onChange={(e) => handleAttributeChange("odometer_hours", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Tyre/Track Condition</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.tyre_track_condition || ""}
                        onChange={(e) => handleAttributeChange("tyre_track_condition", e.target.value)}
                        placeholder="Enter"
                      />
                      <span>%</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Ownership Count</label>
                    <input
                      type="text"
                      value={form.product_attributes.ownership_count || ""}
                      onChange={(e) => handleAttributeChange("ownership_count", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Service History Available</label>
                    <select
                      value={form.product_attributes.service_history_available || ""}
                      onChange={(e) => handleAttributeChange("service_history_available", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Accident / Damage History</label>
                    <select
                      value={form.product_attributes.accident_damage_history || ""}
                      onChange={(e) => handleAttributeChange("accident_damage_history", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Engine Power</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.engine_power || ""}
                        onChange={(e) => handleAttributeChange("engine_power", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[{ value: "HP", label: "HP" }, { value: "kW", label: "kW" }]}
                        onChange={(opt) => handleAttributeChange("engine_power_unit", opt?.value || "")}
                        value={[{ value: "HP", label: "HP" }, { value: "kW", label: "kW" }].find(opt => opt.value === form.product_attributes.engine_power_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Fuel/EV Type</label>
                    <select
                      value={form.product_attributes.fuel_ev_type || ""}
                      onChange={(e) => handleAttributeChange("fuel_ev_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Petrol">Petrol</option>
                      <option value="CNG">CNG</option>
                      <option value="LNG">LNG</option>
                      <option value="LPG">LPG</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Diesel">Diesel</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Axle Configuration</label>
                    <input
                      type="text"
                      value={form.product_attributes.axle_configuration || ""}
                      onChange={(e) => handleAttributeChange("axle_configuration", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Emission Norm</label>
                    <select
                      value={form.product_attributes.emission_norm || ""}
                      onChange={(e) => handleAttributeChange("emission_norm", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Euro 1">Euro 1</option>
                      <option value="Euro 2">Euro 2</option>
                      <option value="Euro 3">Euro 3</option>
                      <option value="Euro 4">Euro 4</option>
                      <option value="Euro 5">Euro 5</option>
                      <option value="Euro 6">Euro 6</option>
                      <option value="Euro 7">Euro 7</option>
                      <option value="Tier 1">Tier 1</option>
                      <option value="Tier 2">Tier 2</option>
                      <option value="Tier 3">Tier 3</option>
                      <option value="Tier 4">Tier 4</option>
                      <option value="Tier 4 Final">Tier 4 Final</option>
                      <option value="Tier 5">Tier 5</option>
                      <option value="Lev">Lev</option>
                      <option value="ULEV">ULEV</option>
                      <option value="SULEV">SULEV</option>
                      <option value="ZEV">ZEV</option>
                      <option value="BS I">BS I</option>
                      <option value="BS II">BS II</option>
                      <option value="BS III">BS III</option>
                      <option value="BS IV">BS IV</option>
                      <option value="BS V">BS V</option>
                      <option value="BS VI">BS VI</option>
                      <option value="China I">China I</option>
                      <option value="China II">China II</option>
                      <option value="China III">China III</option>
                      <option value="China IV">China IV</option>
                      <option value="China V">China V</option>
                      <option value="China VI">China VI</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Body Type/Config</label>
                    <select
                      value={form.product_attributes.body_type_config || ""}
                      onChange={(e) => handleAttributeChange("body_type_config", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Pickup">Pickup</option>
                      <option value="Light Commercial Vehicle (LCV)">Light Commercial Vehicle (LCV)</option>
                      <option value="Medium Duty Truck (MDT)">Medium Duty Truck (MDT)</option>
                      <option value="Heavy Duty Truck (HDT)">Heavy Duty Truck (HDT)</option>
                      <option value="Tipper / Dumper">Tipper / Dumper</option>
                      <option value="Tanker">Tanker</option>
                      <option value="Flatbed / Open Body">Flatbed / Open Body</option>
                      <option value="Box / Container / Reefer">Box / Container / Reefer</option>
                      <option value="Trailer / Semi-Trailer">Trailer / Semi-Trailer</option>
                      <option value="Bus / Coach / Minibus">Bus / Coach / Minibus</option>
                      <option value="Cab Chassis">Cab Chassis</option>
                      <option value="Forward Control Cab">Forward Control Cab</option>
                      <option value="Conventional Cab">Conventional Cab</option>
                      <option value="Sleeper Cab">Sleeper Cab</option>
                      <option value="Hatchback">Hatchback</option>
                      <option value="Sedan / Saloon">Sedan / Saloon</option>
                      <option value="SUV">SUV</option>
                      <option value="Crossover">Crossover</option>
                      <option value="MUV / MPV">MUV / MPV</option>
                      <option value="Coupe">Coupe</option>
                      <option value="Convertible / Cabriolet">Convertible / Cabriolet</option>
                      <option value="Station Wagon / Estate">Station Wagon / Estate</option>
                      <option value="Pickup / Utility Vehicle">Pickup / Utility Vehicle</option>
                      <option value="Standard / Commuter Bike">Standard / Commuter Bike</option>
                      <option value="Cruiser">Cruiser</option>
                      <option value="Sports Bike">Sports Bike</option>
                      <option value="Touring / Adventure Bike">Touring / Adventure Bike</option>
                      <option value="Scooter">Scooter</option>
                      <option value="Moped">Moped</option>
                      <option value="Electric Scooter / Bike">Electric Scooter / Bike</option>
                      <option value="Utility Tractor">Utility Tractor</option>
                      <option value="Row Crop Tractor">Row Crop Tractor</option>
                      <option value="Orchard / Vineyard Tractor">Orchard / Vineyard Tractor</option>
                      <option value="Garden Tractor">Garden Tractor</option>
                      <option value="Industrial Tractor">Industrial Tractor</option>
                      <option value="Two-wheel Tractor">Two-wheel Tractor</option>
                      <option value="Combine Harvester">Combine Harvester</option>
                      <option value="Forage Harvester">Forage Harvester</option>
                      <option value="Rice / Wheat Harvester">Rice / Wheat Harvester</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Chassis Type</label>
                    <select
                      value={form.product_attributes.chassis_type || ""}
                      onChange={(e) => handleAttributeChange("chassis_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Ladder">Ladder</option>
                      <option value="Monocoque">Monocoque</option>
                      <option value="Modular">Modular</option>
                      <option value="Tubular Frame">Tubular Frame</option>
                      <option value="Backbone Chassis">Backbone Chassis</option>
                      <option value="Platform-Based">Platform-Based</option>
                      <option value="C-Channel">C-Channel</option>
                      <option value="Box Frame">Box Frame</option>
                      <option value="Semi-monocoque">Semi-monocoque</option>
                      <option value="Low-floor">Low-floor</option>
                      <option value="Perimeter Frame">Perimeter Frame</option>
                      <option value="Diamond">Diamond</option>
                      <option value="Cradle">Cradle</option>
                      <option value="Trellis">Trellis</option>
                      <option value="Reinforced Box Section Chassis">Reinforced Box Section Chassis</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Battery Health (EV)</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.battery_health_ev || ""}
                        onChange={(e) => handleAttributeChange("battery_health_ev", e.target.value)}
                        placeholder="Enter"
                      />
                      <span>%</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Transmission</label>
                    <select
                      value={form.product_attributes.transmission || ""}
                      onChange={(e) => handleAttributeChange("transmission", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Manual Transmission">Manual Transmission</option>
                      <option value="Automatic Transmission">Automatic Transmission</option>
                      <option value="Automated Manual Transmission">Automated Manual Transmission</option>
                      <option value="Electric Drive">Electric Drive</option>
                      <option value="Belt Drive">Belt Drive</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Registration Expiry Date</label>
                    <input
                      type="date"
                      value={form.product_attributes.registration_expiry_date || ""}
                      onChange={(e) => handleAttributeChange("registration_expiry_date", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Registration Title</label>
                    <input
                      type="text"
                      value={form.product_attributes.registration_title || ""}
                      onChange={(e) => handleAttributeChange("registration_title", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 2 ? (
            <>
              {/* Construction & Heavy Equipment Section */}
              <section className="form-section">
                <h2>Construction & Heavy Equipment</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.construction_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("construction_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Rental">Rental</option>
                      <option value="Reconditioned">Reconditioned</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("construction_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("construction_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.construction_packaging_unit || opt.label === form.product_attributes.construction_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("construction_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.construction_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("construction_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Bottle">Bottle</option>
                      <option value="Pouch">Pouch</option>
                      <option value="Jar">Jar</option>
                      <option value="Can">Can</option>
                      <option value="Tetra Pack">Tetra Pack</option>
                      <option value="Sachet">Sachet</option>
                      <option value="Box">Box</option>
                      <option value="Carton">Carton</option>
                      <option value="As-Is">As-Is</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_lot_size || ""}
                      onChange={(e) => handleAttributeChange("construction_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("construction_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.construction_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("construction_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Tier III">Tier III</option>
                      <option value="Tier IV">Tier IV</option>
                      <option value="Tier V">Tier V</option>
                      <option value="BS-IV">BS-IV</option>
                      <option value="BS-V">BS-V</option>
                      <option value="BS-VI - India">BS-VI - India</option>
                      <option value="ISO 20474">ISO 20474</option>
                      <option value="OSHA">OSHA</option>
                      <option value="CE Marking">CE Marking</option>
                      <option value="ISO 6395">ISO 6395</option>
                      <option value="ISO 2631">ISO 2631</option>
                      <option value="ISO 4413">ISO 4413</option>
                      <option value="EN 13000">EN 13000</option>
                      <option value="ISO 4301/4306">ISO 4301/4306</option>
                      <option value="ISO 140001">ISO 140001</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 12100">ISO 12100</option>
                      <option value="IEC 60204">IEC 60204</option>
                      <option value="ASME Certification">ASME Certification</option>
                      <option value="API Standards">API Standards</option>
                      <option value="RoHS">RoHS</option>
                      <option value="ISO 50001">ISO 50001</option>
                      <option value="IE2">IE2</option>
                      <option value="IE3">IE3</option>
                      <option value="IE4">IE4</option>
                      <option value="GMP">GMP</option>
                      <option value="ATEX">ATEX</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Machine Type</label>
                    <input
                      type="text"
                      value={form.product_attributes.machine_type || ""}
                      onChange={(e) => handleAttributeChange("machine_type", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Serial number</label>
                    <input
                      type="text"
                      value={form.product_attributes.serial_number || ""}
                      onChange={(e) => handleAttributeChange("serial_number", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Model</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_model || ""}
                      onChange={(e) => handleAttributeChange("construction_model", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Hydraulic System</label>
                    <input
                      type="text"
                      value={form.product_attributes.hydraulic_system || ""}
                      onChange={(e) => handleAttributeChange("hydraulic_system", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Ownership Count</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_ownership_count || ""}
                      onChange={(e) => handleAttributeChange("construction_ownership_count", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Capacity / Power</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.capacity_power || ""}
                        onChange={(e) => handleAttributeChange("capacity_power", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "kW", label: "kW" },
                          { value: "HP", label: "HP" },
                          { value: "RPM", label: "RPM" },
                          { value: "Nm", label: "Nm" },
                          { value: "lb-ft", label: "lb-ft" },
                          { value: "Liters", label: "Liters" },
                          { value: "KWh", label: "KWh" },
                          { value: "CFM", label: "CFM" },
                          { value: "kVA", label: "kVA" },
                          { value: "Voltage", label: "Voltage" },
                          { value: "Phase", label: "Phase" },
                          { value: "Frequency", label: "Frequency" }
                        ]}
                        onChange={(opt) => handleAttributeChange("capacity_power_unit", opt?.value || "")}
                        value={[
                          { value: "kW", label: "kW" },
                          { value: "HP", label: "HP" },
                          { value: "RPM", label: "RPM" },
                          { value: "Nm", label: "Nm" },
                          { value: "lb-ft", label: "lb-ft" },
                          { value: "Liters", label: "Liters" },
                          { value: "KWh", label: "KWh" },
                          { value: "CFM", label: "CFM" },
                          { value: "kVA", label: "kVA" },
                          { value: "Voltage", label: "Voltage" },
                          { value: "Phase", label: "Phase" },
                          { value: "Frequency", label: "Frequency" }
                        ].find(opt => opt.value === form.product_attributes.capacity_power_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Work Area / Dimensions</label>
                    <input
                      type="text"
                      value={form.product_attributes.work_area_dimensions || ""}
                      onChange={(e) => handleAttributeChange("work_area_dimensions", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Control System</label>
                    <select
                      value={form.product_attributes.control_system || ""}
                      onChange={(e) => handleAttributeChange("control_system", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Manual Control">Manual Control</option>
                      <option value="Hydraulic Control">Hydraulic Control</option>
                      <option value="Mechanical Linkage Control">Mechanical Linkage Control</option>
                      <option value="Pneumatic Control">Pneumatic Control</option>
                      <option value="Electronic Control">Electronic Control</option>
                      <option value="PLC">PLC</option>
                      <option value="CNC">CNC</option>
                      <option value="Joystick / Lever Control">Joystick / Lever Control</option>
                      <option value="Pedal Control">Pedal Control</option>
                      <option value="Remote Control">Remote Control</option>
                      <option value="Semi-Automated Control">Semi-Automated Control</option>
                      <option value="Fully Automated Control">Fully Automated Control</option>
                      <option value="Telematics / IoT-based Control">Telematics / IoT-based Control</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Maintenance Status</label>
                    <select
                      value={form.product_attributes.maintenance_status || ""}
                      onChange={(e) => handleAttributeChange("maintenance_status", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Up to Date">Up to Date</option>
                      <option value="Due">Due</option>
                      <option value="Overdue">Overdue</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Operating Hours</label>
                    <input
                      type="text"
                      value={form.product_attributes.operating_hours || ""}
                      onChange={(e) => handleAttributeChange("operating_hours", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Year of Last Overhaul</label>
                    <input
                      type="text"
                      value={form.product_attributes.year_of_last_overhaul || ""}
                      onChange={(e) => handleAttributeChange("year_of_last_overhaul", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Fuel Type</label>
                    <select
                      value={form.product_attributes.construction_fuel_type || ""}
                      onChange={(e) => handleAttributeChange("construction_fuel_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Petrol">Petrol</option>
                      <option value="CNG">CNG</option>
                      <option value="LNG">LNG</option>
                      <option value="LPG">LPG</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Diesel">Diesel</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Safety Features</label>
                    <select
                      value={form.product_attributes.safety_features || ""}
                      onChange={(e) => handleAttributeChange("safety_features", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="ROPS/FOPS cabin">ROPS/FOPS cabin</option>
                      <option value="Emergency Stop">Emergency Stop</option>
                      <option value="Alarms">Alarms</option>
                      <option value="Fire Suppression">Fire Suppression</option>
                      <option value="Speed Limiters">Speed Limiters</option>
                      <option value="Load Sensors">Load Sensors</option>
                    </select>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 11 ? (
            <>
              {/* Packaging & Printing Machinery Section */}
              <section className="form-section">
                <h2>Packaging & Printing Machinery</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.packaging_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("packaging_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Rental">Rental</option>
                      <option value="Reconditioned">Reconditioned</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.packaging_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("packaging_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.packaging_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("packaging_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.packaging_packaging_unit || opt.label === form.product_attributes.packaging_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("packaging_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.packaging_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("packaging_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Bottle">Bottle</option>
                      <option value="Pouch">Pouch</option>
                      <option value="Jar">Jar</option>
                      <option value="Can">Can</option>
                      <option value="Tetra Pack">Tetra Pack</option>
                      <option value="Sachet">Sachet</option>
                      <option value="Box">Box</option>
                      <option value="Carton">Carton</option>
                      <option value="As-Is">As-Is</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.packaging_lot_size || ""}
                      onChange={(e) => handleAttributeChange("packaging_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.packaging_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("packaging_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.packaging_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("packaging_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="CE Marking">CE Marking</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 13485">ISO 13485</option>
                      <option value="ISO 22000">ISO 22000</option>
                      <option value="HACCP">HACCP</option>
                      <option value="GMP Compliance">GMP Compliance</option>
                      <option value="IEC 60204-1">IEC 60204-1</option>
                      <option value="UL Certification">UL Certification</option>
                      <option value="RoHS">RoHS</option>
                      <option value="OSHA Compliance">OSHA Compliance</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="FDA">FDA</option>
                      <option value="cGMP">cGMP</option>
                      <option value="CE 1935/2004">CE 1935/2004</option>
                      <option value="ISO 12647">ISO 12647</option>
                      <option value="ISO 14298">ISO 14298</option>
                      <option value="FSC">FSC</option>
                      <option value="PEFC">PEFC</option>
                      <option value="REACH">REACH</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Machine Type</label>
                    <input
                      type="text"
                      value={form.product_attributes.packaging_machine_type || ""}
                      onChange={(e) => handleAttributeChange("packaging_machine_type", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Material Compatibility</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      value={form.product_attributes.material_compatibility?.map(val => ({ value: val, label: val })) || []}
                      onChange={(selected) => handleAttributeChange("material_compatibility", selected ? selected.map(opt => opt.value) : [])}
                      options={[
                        { value: "Paper", label: "Paper" },
                        { value: "Plastic", label: "Plastic" },
                        { value: "Metal", label: "Metal" },
                        { value: "Glass", label: "Glass" },
                        { value: "Fabric", label: "Fabric" },
                        { value: "Films", label: "Films" },
                        { value: "Rolls", label: "Rolls" },
                        { value: "Sheets", label: "Sheets" },
                        { value: "Bottles", label: "Bottles" },
                        { value: "Pouches", label: "Pouches" },
                        { value: "Blisters", label: "Blisters" },
                        { value: "Cartons", label: "Cartons" },
                        { value: "Thickness", label: "Thickness" },
                        { value: "GSM Range", label: "GSM Range" },
                        { value: "Sealability", label: "Sealability" },
                        { value: "Printability Features", label: "Printability Features" },
                        { value: "Ink", label: "Ink" },
                        { value: "Adhesive Compatibility", label: "Adhesive Compatibility" },
                        { value: "Surface Finish Compatibility", label: "Surface Finish Compatibility" }
                      ]}
                      placeholder="Select Materials"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Model</label>
                    <input
                      type="text"
                      value={form.product_attributes.packaging_model || ""}
                      onChange={(e) => handleAttributeChange("packaging_model", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Max Size / Capacity</label>
                    <input
                      type="text"
                      value={form.product_attributes.max_size_capacity || ""}
                      onChange={(e) => handleAttributeChange("max_size_capacity", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Output Quality</label>
                    <select
                      value={form.product_attributes.output_quality || ""}
                      onChange={(e) => handleAttributeChange("output_quality", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="DPI">DPI</option>
                      <option value="print resolution">print resolution</option>
                      <option value="packaging precision">packaging precision</option>
                      <option value="sealing quality">sealing quality</option>
                      <option value="defect rate %">defect rate %</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Printing Technology</label>
                    <select
                      value={form.product_attributes.printing_technology || ""}
                      onChange={(e) => handleAttributeChange("printing_technology", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Offset Printing">Offset Printing</option>
                      <option value="Flexographic Printing (Flexo)">Flexographic Printing (Flexo)</option>
                      <option value="Gravure Printing">Gravure Printing</option>
                      <option value="Screen Printing">Screen Printing</option>
                      <option value="Digital Printing">Digital Printing</option>
                      <option value="Thermal Printing">Thermal Printing</option>
                      <option value="Letterpress">Letterpress</option>
                      <option value="Pad Printing">Pad Printing</option>
                      <option value="3D Printing">3D Printing</option>
                      <option value="Hybrid Printing">Hybrid Printing</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Control System</label>
                    <select
                      value={form.product_attributes.packaging_control_system || ""}
                      onChange={(e) => handleAttributeChange("packaging_control_system", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Manual Control">Manual Control</option>
                      <option value="Semi-Automatic Control">Semi-Automatic Control</option>
                      <option value="Fully Automatic Control">Fully Automatic Control</option>
                      <option value="PCI">PCI</option>
                      <option value="HMI">HMI</option>
                      <option value="CNC">CNC</option>
                      <option value="SCADA">SCADA</option>
                      <option value="IoT-Enabled Control Systems">IoT-Enabled Control Systems</option>
                      <option value="Robotics Integration">Robotics Integration</option>
                      <option value="Recipe / Job Programming">Recipe / Job Programming</option>
                      <option value="Remote Monitoring & Diagnostics">Remote Monitoring & Diagnostics</option>
                      <option value="Automatic Error Detection & Alerts">Automatic Error Detection & Alerts</option>
                      <option value="Data Logging & Reporting">Data Logging & Reporting</option>
                      <option value="Energy Optimization Mode">Energy Optimization Mode</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Accessories / Tooling</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      value={form.product_attributes.accessories_tooling?.map(val => ({ value: val, label: val })) || []}
                      onChange={(selected) => handleAttributeChange("accessories_tooling", selected ? selected.map(opt => opt.value) : [])}
                      options={[
                        { value: "Die sets", label: "Die sets" },
                        { value: "rollers", label: "rollers" },
                        { value: "feeders", label: "feeders" },
                        { value: "applicators", label: "applicators" },
                        { value: "spare parts included", label: "spare parts included" }
                      ]}
                      placeholder="Select Accessories"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Cycle Time / Throughput</label>
                    <input
                      type="text"
                      value={form.product_attributes.cycle_time_throughput || ""}
                      onChange={(e) => handleAttributeChange("cycle_time_throughput", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Printing / Packaging Speed</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.printing_packaging_speed || ""}
                        onChange={(e) => handleAttributeChange("printing_packaging_speed", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "Second", label: "Second" },
                          { value: "Minute", label: "Minute" },
                          { value: "Hour", label: "Hour" },
                          { value: "Day", label: "Day" }
                        ]}
                        onChange={(opt) => handleAttributeChange("printing_packaging_speed_unit", opt?.value || "")}
                        value={[
                          { value: "Second", label: "Second" },
                          { value: "Minute", label: "Minute" },
                          { value: "Hour", label: "Hour" },
                          { value: "Day", label: "Day" }
                        ].find(opt => opt.value === form.product_attributes.printing_packaging_speed_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Power Requirements</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.power_requirements || ""}
                        onChange={(e) => handleAttributeChange("power_requirements", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "Single", label: "Single" },
                          { value: "Three Phase", label: "Three Phase" },
                          { value: "DC", label: "DC" },
                          { value: "V", label: "V" },
                          { value: "Hz", label: "Hz" },
                          { value: "kW", label: "kW" },
                          { value: "kWh", label: "kWh" },
                          { value: "HP", label: "HP" },
                          { value: "kVA", label: "kVA" },
                          { value: "W", label: "W" }
                        ]}
                        onChange={(opt) => handleAttributeChange("power_requirements_unit", opt?.value || "")}
                        value={[
                          { value: "Single", label: "Single" },
                          { value: "Three Phase", label: "Three Phase" },
                          { value: "DC", label: "DC" },
                          { value: "V", label: "V" },
                          { value: "Hz", label: "Hz" },
                          { value: "kW", label: "kW" },
                          { value: "kWh", label: "kWh" },
                          { value: "HP", label: "HP" },
                          { value: "kVA", label: "kVA" },
                          { value: "W", label: "W" }
                        ].find(opt => opt.value === form.product_attributes.power_requirements_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Safety Features</label>
                    <Select
                      className="input-pill p-0"
                      isMulti
                      value={form.product_attributes.packaging_safety_features?.map(val => ({ value: val, label: val })) || []}
                      onChange={(selected) => handleAttributeChange("packaging_safety_features", selected ? selected.map(opt => opt.value) : [])}
                      options={[
                        { value: "Emergency Stop Buttons", label: "Emergency Stop Buttons" },
                        { value: "Safety Interlocks", label: "Safety Interlocks" },
                        { value: "Guarding & Enclosures", label: "Guarding & Enclosures" },
                        { value: "Two-Hand Control Operation", label: "Two-Hand Control Operation" },
                        { value: "Overload Protection", label: "Overload Protection" },
                        { value: "Short-Circuit", label: "Short-Circuit" },
                        { value: "Lockout/Tagout", label: "Lockout/Tagout" },
                        { value: "Safe Torque Off", label: "Safe Torque Off" },
                        { value: "Automatic Shutoff on Fault", label: "Automatic Shutoff on Fault" },
                        { value: "Sensors & Light Curtains", label: "Sensors & Light Curtains" },
                        { value: "Pressure / Force Limiting", label: "Pressure / Force Limiting" },
                        { value: "Noise Reduction Features", label: "Noise Reduction Features" },
                        { value: "Ergonomic Design", label: "Ergonomic Design" },
                        { value: "CE Safety Compliance", label: "CE Safety Compliance" },
                        { value: "ISO 12100 / ISO 13849", label: "ISO 12100 / ISO 13849" },
                        { value: "OSHA / ANSI", label: "OSHA / ANSI" },
                        { value: "EN 415", label: "EN 415" },
                        { value: "IEC 60204-1", label: "IEC 60204-1" }
                      ]}
                      placeholder="Select Safety Features"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Service History Available</label>
                    <select
                      value={form.product_attributes.packaging_service_history_available || ""}
                      onChange={(e) => handleAttributeChange("packaging_service_history_available", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 3 ? (
            <>
              {/* Energy & Electrical Equipment Section */}
              <section className="form-section">
                <h2>Energy & Electrical Equipment</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.energy_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("energy_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Rental">Rental</option>
                      <option value="Reconditioned">Reconditioned</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.energy_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("energy_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.energy_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("energy_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.energy_packaging_unit || opt.label === form.product_attributes.energy_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("energy_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.energy_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("energy_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Bottle">Bottle</option>
                      <option value="Pouch">Pouch</option>
                      <option value="Jar">Jar</option>
                      <option value="Can">Can</option>
                      <option value="Tetra Pack">Tetra Pack</option>
                      <option value="Sachet">Sachet</option>
                      <option value="Box">Box</option>
                      <option value="Carton">Carton</option>
                      <option value="As-Is">As-Is</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.energy_lot_size || ""}
                      onChange={(e) => handleAttributeChange("energy_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.energy_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("energy_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.energy_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("energy_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="IEC 60034">IEC 60034</option>
                      <option value="IEC 60947">IEC 60947</option>
                      <option value="IEC 60204">IEC 60204</option>
                      <option value="IEC 60529">IEC 60529</option>
                      <option value="IEC 61000">IEC 61000</option>
                      <option value="IEC 61439">IEC 61439</option>
                      <option value="IEC 62133">IEC 62133</option>
                      <option value="IEC 62368">IEC 62368</option>
                      <option value="CE Marking">CE Marking</option>
                      <option value="UL">UL</option>
                      <option value="CSA">CSA</option>
                      <option value="BIS/ISI">BIS/ISI</option>
                      <option value="CCC">CCC</option>
                      <option value="PSE">PSE</option>
                      <option value="KC">KC</option>
                      <option value="EAC">EAC</option>
                      <option value="Energy Star">Energy Star</option>
                      <option value="BEE Star Rating">BEE Star Rating</option>
                      <option value="ErP Directive">ErP Directive</option>
                      <option value="RoHS">RoHS</option>
                      <option value="REACH">REACH</option>
                      <option value="WEEE">WEEE</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 45001">ISO 45001</option>
                      <option value="OHSAS">OHSAS</option>
                      <option value="ATEX">ATEX</option>
                      <option value="NEMA">NEMA</option>
                      <option value="ANSI">ANSI</option>
                      <option value="IEEE">IEEE</option>
                      <option value="NFPA 70">NFPA 70</option>
                      <option value="GB">GB</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Equipment Type</label>
                    <input
                      type="text"
                      value={form.product_attributes.equipment_type || ""}
                      onChange={(e) => handleAttributeChange("equipment_type", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Installation Year</label>
                    <input
                      type="text"
                      value={form.product_attributes.installation_year || ""}
                      onChange={(e) => handleAttributeChange("installation_year", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Model</label>
                    <input
                      type="text"
                      value={form.product_attributes.energy_model || ""}
                      onChange={(e) => handleAttributeChange("energy_model", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Efficiency/Class</label>
                    <select
                      value={form.product_attributes.efficiency_class || ""}
                      onChange={(e) => handleAttributeChange("efficiency_class", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="IE1">IE1</option>
                      <option value="IE2">IE2</option>
                      <option value="IE3">IE3</option>
                      <option value="IE4">IE4</option>
                      <option value="IE5">IE5</option>
                      <option value="Tier 1">Tier 1</option>
                      <option value="Tier 2">Tier 2</option>
                      <option value="Class AA">Class AA</option>
                      <option value="Class A">Class A</option>
                      <option value="Class B">Class B</option>
                      <option value="Class C">Class C</option>
                      <option value="1 Star">1 Star</option>
                      <option value="2 Star">2 Star</option>
                      <option value="3 Star">3 Star</option>
                      <option value="4 Star">4 Star</option>
                      <option value="5 Star">5 Star</option>
                      <option value="ISO 8528">ISO 8528</option>
                      <option value="IEC 60034">IEC 60034</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Voltage Rating</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.voltage_rating || ""}
                        onChange={(e) => handleAttributeChange("voltage_rating", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "V", label: "V" },
                          { value: "kV", label: "kV" }
                        ]}
                        onChange={(opt) => handleAttributeChange("voltage_rating_unit", opt?.value || "")}
                        value={[
                          { value: "V", label: "V" },
                          { value: "kV", label: "kV" }
                        ].find(opt => opt.value === form.product_attributes.voltage_rating_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Current Rating</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.current_rating || ""}
                        onChange={(e) => handleAttributeChange("current_rating", e.target.value)}
                        placeholder="Enter"
                      />
                      <span>A</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>kVA/kW Capacity</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.kva_kw_capacity || ""}
                        onChange={(e) => handleAttributeChange("kva_kw_capacity", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "kVA", label: "kVA" },
                          { value: "kW", label: "kW" }
                        ]}
                        onChange={(opt) => handleAttributeChange("kva_kw_capacity_unit", opt?.value || "")}
                        value={[
                          { value: "kVA", label: "kVA" },
                          { value: "kW", label: "kW" }
                        ].find(opt => opt.value === form.product_attributes.kva_kw_capacity_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Frequency</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.frequency || ""}
                        onChange={(e) => handleAttributeChange("frequency", e.target.value)}
                        placeholder="Enter"
                      />
                      <span>Hz</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Phase Type</label>
                    <select
                      value={form.product_attributes.phase_type || ""}
                      onChange={(e) => handleAttributeChange("phase_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Single-phase">Single-phase</option>
                      <option value="Three-phase">Three-phase</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Cooling Type</label>
                    <select
                      value={form.product_attributes.cooling_type || ""}
                      onChange={(e) => handleAttributeChange("cooling_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="AN">AN</option>
                      <option value="AF">AF</option>
                      <option value="AA">AA</option>
                      <option value="ONAN">ONAN</option>
                      <option value="ONAF">ONAF</option>
                      <option value="OFAF">OFAF</option>
                      <option value="OFWF">OFWF</option>
                      <option value="ODAF">ODAF</option>
                      <option value="ODWF">ODWF</option>
                      <option value="Water Natural">Water Natural</option>
                      <option value="Water Forced">Water Forced</option>
                      <option value="Closed-loop Water Cooling">Closed-loop Water Cooling</option>
                      <option value="Open-loop Water Cooling">Open-loop Water Cooling</option>
                      <option value="Dry Type Cooling">Dry Type Cooling</option>
                      <option value="Gas">Gas</option>
                      <option value="Heat Exchanger">Heat Exchanger</option>
                      <option value="Liquid Immersion">Liquid Immersion</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Standards Compliance</label>
                    <select
                      value={form.product_attributes.standards_compliance || ""}
                      onChange={(e) => handleAttributeChange("standards_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="IEC 60034">IEC 60034</option>
                      <option value="IEC 60947">IEC 60947</option>
                      <option value="IEC 60204">IEC 60204</option>
                      <option value="IEC 60529">IEC 60529</option>
                      <option value="IEC 61000">IEC 61000</option>
                      <option value="IEC 61439">IEC 61439</option>
                      <option value="IEC 62133">IEC 62133</option>
                      <option value="IEC 62368">IEC 62368</option>
                      <option value="CE Marking">CE Marking</option>
                      <option value="UL">UL</option>
                      <option value="CSA">CSA</option>
                      <option value="BIS/ISI">BIS/ISI</option>
                      <option value="CCC">CCC</option>
                      <option value="PSE">PSE</option>
                      <option value="KC">KC</option>
                      <option value="EAC">EAC</option>
                      <option value="Energy Star">Energy Star</option>
                      <option value="BEE Star Rating">BEE Star Rating</option>
                      <option value="ErP Directive">ErP Directive</option>
                      <option value="RoHS">RoHS</option>
                      <option value="REACH">REACH</option>
                      <option value="WEEE">WEEE</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 45001">ISO 45001</option>
                      <option value="OHSAS">OHSAS</option>
                      <option value="ATEX">ATEX</option>
                      <option value="NEMA">NEMA</option>
                      <option value="ANSI">ANSI</option>
                      <option value="IEEE">IEEE</option>
                      <option value="NFPA 70">NFPA 70</option>
                      <option value="GB">GB</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Operating Temp Range</label>
                    <input
                      type="text"
                      value={form.product_attributes.operating_temp_range || ""}
                      onChange={(e) => handleAttributeChange("operating_temp_range", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 21 ? (
            <>
              {/* Construction Materials (Bulk) Section */}
              <section className="form-section">
                <h2>Construction Materials (Bulk)</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.construction_materials_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Rental">Rental</option>
                      <option value="Reconditioned">Reconditioned</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_materials_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_materials_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.construction_materials_packaging_unit || opt.label === form.product_attributes.construction_materials_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("construction_materials_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.construction_materials_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_materials_lot_size || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_materials_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.construction_materials_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="REACH Compliance">REACH Compliance</option>
                      <option value="RoHS Compliance">RoHS Compliance</option>
                      <option value="GHS Classification">GHS Classification</option>
                      <option value="OSHA Compliance">OSHA Compliance</option>
                      <option value="DOT / ADR / IMDG / IATA">DOT / ADR / IMDG / IATA</option>
                      <option value="UN Number & Hazard Class">UN Number & Hazard Class</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 45001 / OHSAS 18001">ISO 45001 / OHSAS 18001</option>
                      <option value="GMP (Good Manufacturing Practices)">GMP (Good Manufacturing Practices)</option>
                      <option value="GLP (Good Laboratory Practices)">GLP (Good Laboratory Practices)</option>
                      <option value="Responsible Care® Certification">Responsible Care® Certification</option>
                      <option value="EcoVadis Certification">EcoVadis Certification</option>
                      <option value="Green Seal / EcoLabel">Green Seal / EcoLabel</option>
                      <option value="VOC Compliance">VOC Compliance</option>
                      <option value="Food Grade Compliance">Food Grade Compliance</option>
                      <option value="FDA Approval">FDA Approval</option>
                      <option value="EFSA">EFSA</option>
                      <option value="FSSAI">FSSAI</option>
                      <option value="Pharma Grade Compliance">Pharma Grade Compliance</option>
                      <option value="US Pharmacopeia">US Pharmacopeia</option>
                      <option value="European Pharmacopeia (Ph. Eur.)">European Pharmacopeia (Ph. Eur.)</option>
                      <option value="Indian Pharmacopeia (IP)">Indian Pharmacopeia (IP)</option>
                      <option value="Cosmetic Grade Compliance">Cosmetic Grade Compliance</option>
                      <option value="IFRA Standards (Fragrance materials)">IFRA Standards (Fragrance materials)</option>
                      <option value="Cosmetic Regulation (EC 1223/2009, EU)">Cosmetic Regulation (EC 1223/2009, EU)</option>
                      <option value="ISO 11114 / 9809">ISO 11114 / 9809</option>
                      <option value="CE Marking (Europe)">CE Marking (Europe)</option>
                      <option value="PESO Certification (India)">PESO Certification (India)</option>
                      <option value="ASME Compliance">ASME Compliance</option>
                      <option value="ASTM Standards">ASTM Standards</option>
                      <option value="EN 71-3 Certification">EN 71-3 Certification</option>
                      <option value="BIS Certification (India)">BIS Certification (India)</option>
                      <option value="Halal / Kosher Certification">Halal / Kosher Certification</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Material Type</label>
                    <select
                      value={form.product_attributes.material_type || ""}
                      onChange={(e) => handleAttributeChange("material_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Ordinary Portland Cement">Ordinary Portland Cement</option>
                      <option value="Portland Pozzolana Cement (PPC)">Portland Pozzolana Cement (PPC)</option>
                      <option value="Portland Slag Cement (PSC)">Portland Slag Cement (PSC)</option>
                      <option value="White Cement">White Cement</option>
                      <option value="Rapid Hardening Cement">Rapid Hardening Cement</option>
                      <option value="Sulphate Resistant Cement">Sulphate Resistant Cement</option>
                      <option value="Low Heat Cement">Low Heat Cement</option>
                      <option value="Oil Well Cement">Oil Well Cement</option>
                      <option value="Clay Bricks">Clay Bricks</option>
                      <option value="Fly Ash Bricks">Fly Ash Bricks</option>
                      <option value="Concrete Bricks / Blocks">Concrete Bricks / Blocks</option>
                      <option value="Sand Lime Bricks">Sand Lime Bricks</option>
                      <option value="Engineering Bricks">Engineering Bricks</option>
                      <option value="Fire / Refractory Bricks">Fire / Refractory Bricks</option>
                      <option value="Hollow / Perforated Bricks">Hollow / Perforated Bricks</option>
                      <option value="Eco / Recycled Bricks">Eco / Recycled Bricks</option>
                      <option value="Ceramic Tiles">Ceramic Tiles</option>
                      <option value="Vitrified Tiles">Vitrified Tiles</option>
                      <option value="Porcelain Tiles">Porcelain Tiles</option>
                      <option value="Mosaic Tiles">Mosaic Tiles</option>
                      <option value="Natural Stone Tiles">Natural Stone Tiles</option>
                      <option value="Cement Tiles">Cement Tiles</option>
                      <option value="Glass Tiles">Glass Tiles</option>
                      <option value="Terracotta Tiles">Terracotta Tiles</option>
                      <option value="Mild Steel (MS) Rebar">Mild Steel (MS) Rebar</option>
                      <option value="Thermo Mechanically Treated (TMT) Bars">Thermo Mechanically Treated (TMT) Bars</option>
                      <option value="High Strength Deformed (HSD) Bars">High Strength Deformed (HSD) Bars</option>
                      <option value="Epoxy Coated Rebar">Epoxy Coated Rebar</option>
                      <option value="Stainless Steel Rebar">Stainless Steel Rebar</option>
                      <option value="Welded Wire Fabric (Reinforcement Mesh)">Welded Wire Fabric (Reinforcement Mesh)</option>
                      <option value="Galvanized Steel Rebar">Galvanized Steel Rebar</option>
                      <option value="Precast Concrete Wall Panels">Precast Concrete Wall Panels</option>
                      <option value="Precast Hollow Core Slabs">Precast Hollow Core Slabs</option>
                      <option value="Precast Cladding Panels">Precast Cladding Panels</option>
                      <option value="Precast Façade Panels">Precast Façade Panels</option>
                      <option value="Precast Partition Panels">Precast Partition Panels</option>
                      <option value="Precast Boundary Wall Panels">Precast Boundary Wall Panels</option>
                      <option value="Lightweight Precast Panels">Lightweight Precast Panels</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Specification</label>
                    <input
                      type="text"
                      value={form.product_attributes.specification || ""}
                      onChange={(e) => handleAttributeChange("specification", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="text"
                      value={form.product_attributes.construction_materials_quantity || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_quantity", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Unit of Measure</label>
                    <select
                      value={form.product_attributes.construction_materials_unit_of_measure || ""}
                      onChange={(e) => handleAttributeChange("construction_materials_unit_of_measure", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Bag (Kg)">Bag (Kg)</option>
                      <option value="Metric Ton (MT)">Metric Ton (MT)</option>
                      <option value="Bulk Tanker (MT / KL)">Bulk Tanker (MT / KL)</option>
                      <option value="Piece (Nos.)">Piece (Nos.)</option>
                      <option value="1000 Pieces (Per Thousand)">1000 Pieces (Per Thousand)</option>
                      <option value="Cubic Meter (m³)">Cubic Meter (m³)</option>
                      <option value="Pallet">Pallet</option>
                      <option value="Square Foot (ft²)">Square Foot (ft²)</option>
                      <option value="Square Meter (m²)">Square Meter (m²)</option>
                      <option value="Box (per carton)">Box (per carton)</option>
                      <option value="Kilogram (Kg)">Kilogram (Kg)</option>
                      <option value="Running Meter (RM / Linear Meter)">Running Meter (RM / Linear Meter)</option>
                      <option value="Bundle">Bundle</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Test Certificate</label>
                    <select
                      value={form.product_attributes.test_certificate || ""}
                      onChange={(e) => handleAttributeChange("test_certificate", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 22 ? (
            <>
              {/* Event & Exhibition Assets Section */}
              <section className="form-section">
                <h2>Event & Exhibition Assets</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.event_exhibition_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("event_exhibition_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Like New">Like New</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good / Refurbished">Good / Refurbished</option>
                      <option value="Fair / Used">Fair / Used</option>
                      <option value="Needs Repair / Salvage">Needs Repair / Salvage</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.event_exhibition_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("event_exhibition_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.event_exhibition_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("event_exhibition_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.event_exhibition_packaging_unit || opt.label === form.product_attributes.event_exhibition_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("event_exhibition_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.event_exhibition_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("event_exhibition_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.event_exhibition_lot_size || ""}
                      onChange={(e) => handleAttributeChange("event_exhibition_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.event_exhibition_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("event_exhibition_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.event_exhibition_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("event_exhibition_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="REACH Compliance">REACH Compliance</option>
                      <option value="RoHS Compliance">RoHS Compliance</option>
                      <option value="GHS Classification">GHS Classification</option>
                      <option value="OSHA Compliance">OSHA Compliance</option>
                      <option value="DOT / ADR / IMDG / IATA">DOT / ADR / IMDG / IATA</option>
                      <option value="UN Number & Hazard Class">UN Number & Hazard Class</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 45001 / OHSAS 18001">ISO 45001 / OHSAS 18001</option>
                      <option value="GMP (Good Manufacturing Practices)">GMP (Good Manufacturing Practices)</option>
                      <option value="GLP (Good Laboratory Practices)">GLP (Good Laboratory Practices)</option>
                      <option value="Responsible Care® Certification">Responsible Care® Certification</option>
                      <option value="EcoVadis Certification">EcoVadis Certification</option>
                      <option value="Green Seal / EcoLabel">Green Seal / EcoLabel</option>
                      <option value="VOC Compliance">VOC Compliance</option>
                      <option value="Food Grade Compliance">Food Grade Compliance</option>
                      <option value="FDA Approval">FDA Approval</option>
                      <option value="EFSA">EFSA</option>
                      <option value="FSSAI">FSSAI</option>
                      <option value="Pharma Grade Compliance">Pharma Grade Compliance</option>
                      <option value="US Pharmacopeia">US Pharmacopeia</option>
                      <option value="European Pharmacopeia (Ph. Eur.)">European Pharmacopeia (Ph. Eur.)</option>
                      <option value="Indian Pharmacopeia (IP)">Indian Pharmacopeia (IP)</option>
                      <option value="Cosmetic Grade Compliance">Cosmetic Grade Compliance</option>
                      <option value="IFRA Standards (Fragrance materials)">IFRA Standards (Fragrance materials)</option>
                      <option value="Cosmetic Regulation (EC 1223/2009, EU)">Cosmetic Regulation (EC 1223/2009, EU)</option>
                      <option value="ISO 11114 / 9809">ISO 11114 / 9809</option>
                      <option value="CE Marking (Europe)">CE Marking (Europe)</option>
                      <option value="PESO Certification (India)">PESO Certification (India)</option>
                      <option value="ASME Compliance">ASME Compliance</option>
                      <option value="ASTM Standards">ASTM Standards</option>
                      <option value="EN 71-3 Certification">EN 71-3 Certification</option>
                      <option value="BIS Certification (India)">BIS Certification (India)</option>
                      <option value="Halal / Kosher Certification">Halal / Kosher Certification</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Asset Type</label>
                    <select
                      value={form.product_attributes.asset_type || ""}
                      onChange={(e) => handleAttributeChange("asset_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Modular Booths">Modular Booths</option>
                      <option value="Custom-Built Booths">Custom-Built Booths</option>
                      <option value="Portable Booths / Pop-up Stands">Portable Booths / Pop-up Stands</option>
                      <option value="Shell Scheme Stalls">Shell Scheme Stalls</option>
                      <option value="Backdrops & Display Walls">Backdrops & Display Walls</option>
                      <option value="Kiosks & Counters">Kiosks & Counters</option>
                      <option value="Truss Structures">Truss Structures</option>
                      <option value="Hanging Banners / Ceiling Suspensions">Hanging Banners / Ceiling Suspensions</option>
                      <option value="LED Walls / Video Walls">LED Walls / Video Walls</option>
                      <option value="Projectors & Projection Screens">Projectors & Projection Screens</option>
                      <option value="Plasma / LCD / LED Displays">Plasma / LCD / LED Displays</option>
                      <option value="Sound Systems">Sound Systems</option>
                      <option value="Microphones">Microphones</option>
                      <option value="Stage Lighting">Stage Lighting</option>
                      <option value="Control Systems">Control Systems</option>
                      <option value="Interactive Displays / Touchscreens">Interactive Displays / Touchscreens</option>
                      <option value="Thematic Decor Props">Thematic Decor Props</option>
                      <option value="Branding & Signage Props">Branding & Signage Props</option>
                      <option value="Promotional Standees & Cutouts">Promotional Standees & Cutouts</option>
                      <option value="3D Models / Replicas">3D Models / Replicas</option>
                      <option value="Interactive Props">Interactive Props</option>
                      <option value="Photo Booth Props">Photo Booth Props</option>
                      <option value="Stage / Show Props">Stage / Show Props</option>
                      <option value="Banners & Flags">Banners & Flags</option>
                      <option value="Tables">Tables</option>
                      <option value="Chairs">Chairs</option>
                      <option value="Sofas & Lounge Seating">Sofas & Lounge Seating</option>
                      <option value="Podiums / Lecterns">Podiums / Lecterns</option>
                      <option value="Display Racks & Stands">Display Racks & Stands</option>
                      <option value="Carpeting & Flooring">Carpeting & Flooring</option>
                      <option value="Partitions & Dividers">Partitions & Dividers</option>
                      <option value="Storage Units / Lockers">Storage Units / Lockers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Material</label>
                    <input
                      type="text"
                      value={form.product_attributes.material || ""}
                      onChange={(e) => handleAttributeChange("material", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>AV Specs</label>
                    <input
                      type="text"
                      value={form.product_attributes.av_specs || ""}
                      onChange={(e) => handleAttributeChange("av_specs", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Includes Installation</label>
                    <select
                      value={form.product_attributes.includes_installation || ""}
                      onChange={(e) => handleAttributeChange("includes_installation", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Dimensions</label>
                    <input
                      type="text"
                      value={form.product_attributes.dimensions || ""}
                      onChange={(e) => handleAttributeChange("dimensions", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="text"
                      value={form.product_attributes.event_exhibition_quantity || ""}
                      onChange={(e) => handleAttributeChange("event_exhibition_quantity", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 15 ? (
            <>
              {/* Scrap & Recyclables Section */}
              <section className="form-section">
                <h2>Scrap & Recyclables</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.scrap_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("scrap_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Production surplus">Production surplus</option>
                      <option value="Overstock / rejected but unused">Overstock / rejected but unused</option>
                      <option value="Free from major contamination">Free from major contamination</option>
                      <option value="Pre-sorted by type">Pre-sorted by type</option>
                      <option value="High-grade recyclable">High-grade recyclable</option>
                      <option value="Mixed material streams">Mixed material streams</option>
                      <option value="Requires segregation before recycling">Requires segregation before recycling</option>
                      <option value="With oil, grease, paint, or coatings">With oil, grease, paint, or coatings</option>
                      <option value="With hazardous residues">With hazardous residues</option>
                      <option value="With moisture or rust">With moisture or rust</option>
                      <option value="Broken, worn-out">Broken, worn-out</option>
                      <option value="Dismantled parts">Dismantled parts</option>
                      <option value="Expired equipment / machinery">Expired equipment / machinery</option>
                      <option value="Shredded / cut / baled">Shredded / cut / baled</option>
                      <option value="Ready-to-recycle form">Ready-to-recycle form</option>
                      <option value="Bulk / raw waste without treatment">Bulk / raw waste without treatment</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.scrap_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("scrap_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.scrap_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("scrap_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.scrap_packaging_unit || opt.label === form.product_attributes.scrap_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("scrap_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.scrap_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("scrap_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.scrap_lot_size || ""}
                      onChange={(e) => handleAttributeChange("scrap_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.scrap_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("scrap_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.scrap_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("scrap_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 45001">ISO 45001</option>
                      <option value="ISRI">ISRI</option>
                      <option value="EU Waste Framework Directive Compliance">EU Waste Framework Directive Compliance</option>
                      <option value="Basel Convention Compliance">Basel Convention Compliance</option>
                      <option value="R2 Certification">R2 Certification</option>
                      <option value="e-Stewards Certification">e-Stewards Certification</option>
                      <option value="WEEE Directive Compliance">WEEE Directive Compliance</option>
                      <option value="RoHS">RoHS</option>
                      <option value="REACH">REACH</option>
                      <option value="ELV Directive">ELV Directive</option>
                      <option value="Hazardous Waste Authorization">Hazardous Waste Authorization</option>
                      <option value="EPA Approval">EPA Approval</option>
                      <option value="OSHA Safety Compliance">OSHA Safety Compliance</option>
                      <option value="DGFT / Customs Clearance Certificates">DGFT / Customs Clearance Certificates</option>
                      <option value="HS Code Compliance">HS Code Compliance</option>
                      <option value="BIS Certification">BIS Certification</option>
                      <option value="CE Marking">CE Marking</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Scrap Type</label>
                    <select
                      value={form.product_attributes.scrap_type || ""}
                      onChange={(e) => handleAttributeChange("scrap_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Iron">Iron</option>
                      <option value="Steel">Steel</option>
                      <option value="Cast Iron">Cast Iron</option>
                      <option value="Stainless Steel">Stainless Steel</option>
                      <option value="HMS (Heavy Melting Steel)">HMS (Heavy Melting Steel)</option>
                      <option value="Re-rollable">Re-rollable</option>
                      <option value="Turnings/borings">Turnings/borings</option>
                      <option value="Aluminium">Aluminium</option>
                      <option value="Copper">Copper</option>
                      <option value="Brass">Brass</option>
                      <option value="Zinc">Zinc</option>
                      <option value="Lead">Lead</option>
                      <option value="Nickel">Nickel</option>
                      <option value="Tin">Tin</option>
                      <option value="Magnesium">Magnesium</option>
                      <option value="Cable Scrap">Cable Scrap</option>
                      <option value="Radiator Scrap">Radiator Scrap</option>
                      <option value="Battery Scrap">Battery Scrap</option>
                      <option value="PET">PET</option>
                      <option value="HDPE">HDPE</option>
                      <option value="LDPE">LDPE</option>
                      <option value="PP">PP</option>
                      <option value="PVC">PVC</option>
                      <option value="ABS">ABS</option>
                      <option value="Mixed Plastics">Mixed Plastics</option>
                      <option value="Granules">Granules</option>
                      <option value="Flakes">Flakes</option>
                      <option value="Regrind">Regrind</option>
                      <option value="Films">Films</option>
                      <option value="OCC (Old Corrugated Cartons)">OCC (Old Corrugated Cartons)</option>
                      <option value="Kraft">Kraft</option>
                      <option value="Newsprint">Newsprint</option>
                      <option value="Mixed Paper">Mixed Paper</option>
                      <option value="White Cuttings">White Cuttings</option>
                      <option value="Clear Glass">Clear Glass</option>
                      <option value="Coloured Glass (Green/Brown)">Coloured Glass (Green/Brown)</option>
                      <option value="Mixed Glass">Mixed Glass</option>
                      <option value="Cullet">Cullet</option>
                      <option value="Tyres (whole, shredded, crumb)">Tyres (whole, shredded, crumb)</option>
                      <option value="Conveyor Belts">Conveyor Belts</option>
                      <option value="Rubber Sheets">Rubber Sheets</option>
                      <option value="Printed Circuit Boards (PCB)">Printed Circuit Boards (PCB)</option>
                      <option value="Cables/Wires">Cables/Wires</option>
                      <option value="Hard Drives">Hard Drives</option>
                      <option value="Mobile Phones">Mobile Phones</option>
                      <option value="Laptops">Laptops</option>
                      <option value="Desktops">Desktops</option>
                      <option value="Printers">Printers</option>
                      <option value="Routers">Routers</option>
                      <option value="Mixed E-waste">Mixed E-waste</option>
                      <option value="Treated/Untreated Wood">Treated/Untreated Wood</option>
                      <option value="Plywood">Plywood</option>
                      <option value="MDF">MDF</option>
                      <option value="Pallets">Pallets</option>
                      <option value="Cotton">Cotton</option>
                      <option value="Polyester">Polyester</option>
                      <option value="Mixed Fabrics">Mixed Fabrics</option>
                      <option value="Leather">Leather</option>
                      <option value="Car Bodies">Car Bodies</option>
                      <option value="Engines">Engines</option>
                      <option value="Gearboxes">Gearboxes</option>
                      <option value="Catalytic Converters">Catalytic Converters</option>
                      <option value="Concrete">Concrete</option>
                      <option value="Asphalt">Asphalt</option>
                      <option value="Bricks">Bricks</option>
                      <option value="Tiles">Tiles</option>
                      <option value="Mixed Debris">Mixed Debris</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Form</label>
                    <select
                      value={form.product_attributes.scrap_form || ""}
                      onChange={(e) => handleAttributeChange("scrap_form", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Ingots / Blocks">Ingots / Blocks</option>
                      <option value="Billets / Slabs">Billets / Slabs</option>
                      <option value="Castings">Castings</option>
                      <option value="Sheets / Plates">Sheets / Plates</option>
                      <option value="Coils / Rolls">Coils / Rolls</option>
                      <option value="Foils">Foils</option>
                      <option value="Granules / Pellets">Granules / Pellets</option>
                      <option value="Flakes">Flakes</option>
                      <option value="Powder / Dust">Powder / Dust</option>
                      <option value="Shredded Scrap">Shredded Scrap</option>
                      <option value="Turnings / Borings / Swarf">Turnings / Borings / Swarf</option>
                      <option value="Cuttings / Offcuts / Trimmings">Cuttings / Offcuts / Trimmings</option>
                      <option value="Whole Equipment / Assemblies">Whole Equipment / Assemblies</option>
                      <option value="Motors / Engines / Gearboxes">Motors / Engines / Gearboxes</option>
                      <option value="Batteries">Batteries</option>
                      <option value="Radiators / Pipes / Cables">Radiators / Pipes / Cables</option>
                      <option value="Used Oils / Lubricants">Used Oils / Lubricants</option>
                      <option value="Sludge / Slurry">Sludge / Slurry</option>
                      <option value="Chemical Waste">Chemical Waste</option>
                      <option value="Baled Plastics / Paper">Baled Plastics / Paper</option>
                      <option value="Compacted Metal Scrap">Compacted Metal Scrap</option>
                      <option value="Briquettes">Briquettes</option>
                      <option value="Printed Circuit Boards (PCB)">Printed Circuit Boards (PCB)</option>
                      <option value="Mixed Electronic Assemblies">Mixed Electronic Assemblies</option>
                      <option value="Disassembled Components">Disassembled Components</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Quantity</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.scrap_quantity || ""}
                        onChange={(e) => handleAttributeChange("scrap_quantity", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "MT", label: "MT" },
                          { value: "Kg", label: "Kg" },
                          { value: "Tons", label: "Tons" },
                          { value: "Cubic Meters", label: "Cubic Meters" }
                        ]}
                        onChange={(opt) => handleAttributeChange("scrap_quantity_unit", opt?.value || "")}
                        value={[
                          { value: "MT", label: "MT" },
                          { value: "Kg", label: "Kg" },
                          { value: "Tons", label: "Tons" },
                          { value: "Cubic Meters", label: "Cubic Meters" }
                        ].find(opt => opt.value === form.product_attributes.scrap_quantity_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Purity %</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.purity_percentage || ""}
                        onChange={(e) => handleAttributeChange("purity_percentage", e.target.value)}
                        placeholder="Enter"
                      />
                      <span>%</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Contamination Details</label>
                    <input
                      type="text"
                      value={form.product_attributes.contamination_details || ""}
                      onChange={(e) => handleAttributeChange("contamination_details", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Recycling Certificate</label>
                    <input
                      type="text"
                      value={form.product_attributes.recycling_certificate || ""}
                      onChange={(e) => handleAttributeChange("recycling_certificate", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Hazardous Material Flag</label>
                    <select
                      value={form.product_attributes.hazardous_material_flag || ""}
                      onChange={(e) => handleAttributeChange("hazardous_material_flag", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 5 ? (
            <>
              {/* Furniture & Office Assets Section */}
              <section className="form-section">
                <h2>Furniture & Office Assets</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.furniture_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("furniture_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New / Unused">New / Unused</option>
                      <option value="Like New">Like New</option>
                      <option value="Used – Good">Used – Good</option>
                      <option value="Used – Fair">Used – Fair</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Damaged / Scrap">Damaged / Scrap</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.furniture_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("furniture_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.furniture_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("furniture_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.furniture_packaging_unit || opt.label === form.product_attributes.furniture_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("furniture_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.furniture_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("furniture_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.furniture_lot_size || ""}
                      onChange={(e) => handleAttributeChange("furniture_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.furniture_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("furniture_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.furniture_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("furniture_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="BIFMA Certification">BIFMA Certification</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 45001">ISO 45001</option>
                      <option value="GreenGuard Certification">GreenGuard Certification</option>
                      <option value="FSC">FSC</option>
                      <option value="PEFC">PEFC</option>
                      <option value="LEED Credits Compliance">LEED Credits Compliance</option>
                      <option value="CARB / TSCA Title VI Compliance">CARB / TSCA Title VI Compliance</option>
                      <option value="BS 5852 / CAL TB 117">BS 5852 / CAL TB 117</option>
                      <option value="EN 1021">EN 1021</option>
                      <option value="NFPA Standards">NFPA Standards</option>
                      <option value="EN 527 / ISO 9241-5">EN 527 / ISO 9241-5</option>
                      <option value="EN 1335">EN 1335</option>
                      <option value="ANSI/HFES 100">ANSI/HFES 100</option>
                      <option value="CE Marking (Europe)">CE Marking (Europe)</option>
                      <option value="BIS Certification (India)">BIS Certification (India)</option>
                      <option value="UL / ETL Certification">UL / ETL Certification</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Furniture Type</label>
                    <select
                      value={form.product_attributes.furniture_type || ""}
                      onChange={(e) => handleAttributeChange("furniture_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Office Chair">Office Chair</option>
                      <option value="Sofa / Couch">Sofa / Couch</option>
                      <option value="Lounge Chair">Lounge Chair</option>
                      <option value="Stools / Benches">Stools / Benches</option>
                      <option value="Office Desk">Office Desk</option>
                      <option value="Conference / Meeting Table">Conference / Meeting Table</option>
                      <option value="Reception Desk / Counter">Reception Desk / Counter</option>
                      <option value="Adjustable / Standing Desk">Adjustable / Standing Desk</option>
                      <option value="Training / Folding Tables">Training / Folding Tables</option>
                      <option value="Filing Cabinet">Filing Cabinet</option>
                      <option value="Bookcase / Shelving Units">Bookcase / Shelving Units</option>
                      <option value="Lockers">Lockers</option>
                      <option value="Pedestals / Drawer Units">Pedestals / Drawer Units</option>
                      <option value="Cupboards / Wardrobes">Cupboards / Wardrobes</option>
                      <option value="Cubicle / Modular Partitions">Cubicle / Modular Partitions</option>
                      <option value="Acoustic Panels">Acoustic Panels</option>
                      <option value="Glass Partitions">Glass Partitions</option>
                      <option value="Whiteboards / Notice Boards">Whiteboards / Notice Boards</option>
                      <option value="Podiums / Lecterns">Podiums / Lecterns</option>
                      <option value="Computer Racks / Server Racks">Computer Racks / Server Racks</option>
                      <option value="Workbenches / Lab Tables">Workbenches / Lab Tables</option>
                      <option value="Cafeteria Tables & Chairs">Cafeteria Tables & Chairs</option>
                      <option value="Reception Sofas & Coffee Tables">Reception Sofas & Coffee Tables</option>
                      <option value="Outdoor Seating & Furniture">Outdoor Seating & Furniture</option>
                      <option value="Waiting Room Furniture">Waiting Room Furniture</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Material</label>
                    <select
                      value={form.product_attributes.furniture_material || ""}
                      onChange={(e) => handleAttributeChange("furniture_material", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Wood">Wood</option>
                      <option value="Metal">Metal</option>
                      <option value="Plastic">Plastic</option>
                      <option value="Glass">Glass</option>
                      <option value="Fabric">Fabric</option>
                      <option value="Leather">Leather</option>
                      <option value="Composite">Composite</option>
                      <option value="Mixed Materials">Mixed Materials</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Load Capacity</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.load_capacity || ""}
                        onChange={(e) => handleAttributeChange("load_capacity", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "kg", label: "kg" },
                          { value: "lbs", label: "lbs" }
                        ]}
                        onChange={(opt) => handleAttributeChange("load_capacity_unit", opt?.value || "")}
                        value={[
                          { value: "kg", label: "kg" },
                          { value: "lbs", label: "lbs" }
                        ].find(opt => opt.value === form.product_attributes.load_capacity_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Quantity Available</label>
                    <input
                      type="text"
                      value={form.product_attributes.quantity_available || ""}
                      onChange={(e) => handleAttributeChange("quantity_available", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Assembly Required</label>
                    <select
                      value={form.product_attributes.assembly_required || ""}
                      onChange={(e) => handleAttributeChange("assembly_required", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Dimensions</label>
                    <input
                      type="text"
                      value={form.product_attributes.furniture_dimensions || ""}
                      onChange={(e) => handleAttributeChange("furniture_dimensions", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Warranty / Return Policy</label>
                    <input
                      type="text"
                      value={form.product_attributes.warranty_return_policy || ""}
                      onChange={(e) => handleAttributeChange("warranty_return_policy", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Colour / Finish</label>
                    <select
                      value={form.product_attributes.colour_finish || ""}
                      onChange={(e) => handleAttributeChange("colour_finish", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Black">Black</option>
                      <option value="White">White</option>
                      <option value="Grey (Light / Dark)">Grey (Light / Dark)</option>
                      <option value="Beige / Cream">Beige / Cream</option>
                      <option value="Brown">Brown</option>
                      <option value="Natural Wood">Natural Wood</option>
                      <option value="Veneer Finish">Veneer Finish</option>
                      <option value="Laminate Finish">Laminate Finish</option>
                      <option value="Stained / Polished Wood">Stained / Polished Wood</option>
                      <option value="Powder-Coated">Powder-Coated</option>
                      <option value="Chrome-Plated">Chrome-Plated</option>
                      <option value="Nickel / Silver Finish">Nickel / Silver Finish</option>
                      <option value="Brushed Aluminium / Stainless Steel">Brushed Aluminium / Stainless Steel</option>
                      <option value="Clear Glass">Clear Glass</option>
                      <option value="Frosted Glass">Frosted Glass</option>
                      <option value="Tinted Glass">Tinted Glass</option>
                      <option value="Marble / Granite Finish">Marble / Granite Finish</option>
                      <option value="Solid Colours">Solid Colours</option>
                      <option value="Dual-Tone Combinations">Dual-Tone Combinations</option>
                      <option value="Patterned Fabric">Patterned Fabric</option>
                      <option value="Leather / Faux Leather Shades">Leather / Faux Leather Shades</option>
                      <option value="Gloss Finish">Gloss Finish</option>
                      <option value="Satin Finish">Satin Finish</option>
                      <option value="High-Gloss Polish">High-Gloss Polish</option>
                      <option value="Eco-Friendly / Low VOC Paints">Eco-Friendly / Low VOC Paints</option>
                    </select>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 6 ? (
            <>
              {/* Healthcare Supplies Section */}
              <section className="form-section">
                <h2>Healthcare Supplies</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.healthcare_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("healthcare_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New / Unused">New / Unused</option>
                      <option value="Like New">Like New</option>
                      <option value="Used – Good">Used – Good</option>
                      <option value="Used – Fair">Used – Fair</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Damaged / Scrap">Damaged / Scrap</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.healthcare_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("healthcare_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.healthcare_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("healthcare_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.healthcare_packaging_unit || opt.label === form.product_attributes.healthcare_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("healthcare_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.healthcare_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("healthcare_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.healthcare_lot_size || ""}
                      onChange={(e) => handleAttributeChange("healthcare_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.healthcare_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("healthcare_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.healthcare_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("healthcare_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="BIFMA Certification">BIFMA Certification</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 45001">ISO 45001</option>
                      <option value="GreenGuard Certification">GreenGuard Certification</option>
                      <option value="FSC">FSC</option>
                      <option value="PEFC">PEFC</option>
                      <option value="LEED Credits Compliance">LEED Credits Compliance</option>
                      <option value="CARB / TSCA Title VI Compliance">CARB / TSCA Title VI Compliance</option>
                      <option value="BS 5852 / CAL TB 117">BS 5852 / CAL TB 117</option>
                      <option value="EN 1021">EN 1021</option>
                      <option value="NFPA Standards">NFPA Standards</option>
                      <option value="EN 527 / ISO 9241-5">EN 527 / ISO 9241-5</option>
                      <option value="EN 1335">EN 1335</option>
                      <option value="ANSI/HFES 100">ANSI/HFES 100</option>
                      <option value="CE Marking (Europe)">CE Marking (Europe)</option>
                      <option value="BIS Certification (India)">BIS Certification (India)</option>
                      <option value="UL / ETL Certification">UL / ETL Certification</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Product Type</label>
                    <input
                      type="text"
                      value={form.product_attributes.product_type || ""}
                      onChange={(e) => handleAttributeChange("product_type", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Medical Device Classification</label>
                    <select
                      value={form.product_attributes.medical_device_classification || ""}
                      onChange={(e) => handleAttributeChange("medical_device_classification", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Class I">Class I</option>
                      <option value="Class II">Class II</option>
                      <option value="Class III">Class III</option>
                      <option value="Not Applicable">Not Applicable</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Sterilization Status</label>
                    <select
                      value={form.product_attributes.sterilization_status || ""}
                      onChange={(e) => handleAttributeChange("sterilization_status", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Sterilized">Sterilized</option>
                      <option value="Non-Sterile">Non-Sterile</option>
                      <option value="Sterile Ready">Sterile Ready</option>
                      <option value="Not Applicable">Not Applicable</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Expiry Date</label>
                    <input
                      type="date"
                      value={form.product_attributes.expiry_date || ""}
                      onChange={(e) => handleAttributeChange("expiry_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Storage Conditions</label>
                    <input
                      type="text"
                      value={form.product_attributes.storage_conditions || ""}
                      onChange={(e) => handleAttributeChange("storage_conditions", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Regulatory Compliance</label>
                    <select
                      value={form.product_attributes.regulatory_compliance || ""}
                      onChange={(e) => handleAttributeChange("regulatory_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="FDA Approved">FDA Approved</option>
                      <option value="CE Marking">CE Marking</option>
                      <option value="ISO 13485">ISO 13485</option>
                      <option value="GMP Certified">GMP Certified</option>
                      <option value="WHO Prequalified">WHO Prequalified</option>
                      <option value="BIS Certification">BIS Certification</option>
                      <option value="CDSCO Approved">CDSCO Approved</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Quantity Available</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.healthcare_quantity_available || ""}
                        onChange={(e) => handleAttributeChange("healthcare_quantity_available", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.healthcare_unit_of_measure || opt.label === form.product_attributes.healthcare_unit_of_measure) || null}
                        onChange={(selected) => handleAttributeChange("healthcare_unit_of_measure", selected ? selected.value : "")}
                        options={attributeUnitOptions}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 17 ? (
            <>
              {/* Livestock & Dairy Products Section */}
              <section className="form-section">
                <h2>Livestock & Dairy Products</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.livestock_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("livestock_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Breeding Grade">Breeding Grade</option>
                      <option value="Dairy Grade">Dairy Grade</option>
                      <option value="Meat Grade">Meat Grade</option>
                      <option value="Work Grade">Work Grade</option>
                      <option value="Cull / Low Grade">Cull / Low Grade</option>
                      <option value="Wool Quality">Wool Quality</option>
                      <option value="Milk Grade">Milk Grade</option>
                      <option value="Cull Stock">Cull Stock</option>
                      <option value="Broiler Grade">Broiler Grade</option>
                      <option value="Layer Grade">Layer Grade</option>
                      <option value="Chick Quality">Chick Quality</option>
                      <option value="Egg Production Grade">Egg Production Grade</option>
                      <option value="Table Fish Grade">Table Fish Grade</option>
                      <option value="Shrimp/Fish Size Grade">Shrimp/Fish Size Grade</option>
                      <option value="Quality Grade">Quality Grade</option>
                      <option value="Hatchery/Fingerling Grade">Hatchery/Fingerling Grade</option>
                      <option value="Table Eggs - Grade A">Table Eggs - Grade A</option>
                      <option value="Table Eggs - Grade B (minor defects)">Table Eggs - Grade B (minor defects)</option>
                      <option value="Table Eggs - Grade C (cracks)">Table Eggs - Grade C (cracks)</option>
                      <option value="Size Grade">Size Grade</option>
                      <option value="Specialty Grade">Specialty Grade</option>
                      <option value="Milk Grade - A">Milk Grade - A</option>
                      <option value="Milk Grade - B (standard)">Milk Grade - B (standard)</option>
                      <option value="Milk Grade - C (low fat %)">Milk Grade - C (low fat %)</option>
                      <option value="FSSAI">FSSAI</option>
                      <option value="BIS">BIS</option>
                      <option value="Export Grade Certified">Export Grade Certified</option>
                      <option value="Leather Grade">Leather Grade</option>
                      <option value="Wool Grade">Wool Grade</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.livestock_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("livestock_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.livestock_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("livestock_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <Select
                      className="input-pill p-0"
                      value={attributeUnitOptions.find(opt => opt.value === form.product_attributes.livestock_packaging_unit || opt.label === form.product_attributes.livestock_packaging_unit) || null}
                      onChange={(selected) => handleAttributeChange("livestock_packaging_unit", selected ? selected.value : "")}
                      options={attributeUnitOptions}
                      placeholder="Select Unit"
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.livestock_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("livestock_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Transport Cage / Crate (wooden, metal, plastic)">Transport Cage / Crate (wooden, metal, plastic)</option>
                      <option value="Ventilated Transport Boxes (for chicks/poultry)">Ventilated Transport Boxes (for chicks/poultry)</option>
                      <option value="Aquaculture Containers">Aquaculture Containers</option>
                      <option value="Oxygenated Bags (fish, shrimp)">Oxygenated Bags (fish, shrimp)</option>
                      <option value="Livestock Transport Truck">Livestock Transport Truck</option>
                      <option value="Carrier (with safety partitions)">Carrier (with safety partitions)</option>
                      <option value="Slatted Plastic/Metal Boxes (for small ruminants/poultry)">Slatted Plastic/Metal Boxes (for small ruminants/poultry)</option>
                      <option value="Paper Pulp Trays">Paper Pulp Trays</option>
                      <option value="Plastic Egg Trays (durable, reusable)">Plastic Egg Trays (durable, reusable)</option>
                      <option value="Cartons">Cartons</option>
                      <option value="Bulk Master Cartons (holding multiple trays)">Bulk Master Cartons (holding multiple trays)</option>
                      <option value="Foam/Plastic Protective Packs (premium or export-grade)">Foam/Plastic Protective Packs (premium or export-grade)</option>
                      <option value="Plastic Pouches (LDPE)">Plastic Pouches (LDPE)</option>
                      <option value="PET Bottles">PET Bottles</option>
                      <option value="Glass Bottles">Glass Bottles</option>
                      <option value="Bulk Stainless Steel Cans / Tankers">Bulk Stainless Steel Cans / Tankers</option>
                      <option value="Foil Wraps">Foil Wraps</option>
                      <option value="Cartons/Boxes">Cartons/Boxes</option>
                      <option value="Tin/Metal Containers">Tin/Metal Containers</option>
                      <option value="PET Jars">PET Jars</option>
                      <option value="Vacuum-Sealed Plastic Packs">Vacuum-Sealed Plastic Packs</option>
                      <option value="Plastic Cups/Tubs with Lid">Plastic Cups/Tubs with Lid</option>
                      <option value="Foil-Sealed Cups">Foil-Sealed Cups</option>
                      <option value="Wrappers & Cartons">Wrappers & Cartons</option>
                      <option value="Baled & Tied Bundles">Baled & Tied Bundles</option>
                      <option value="Plastic Sheets / Poly Wrapping">Plastic Sheets / Poly Wrapping</option>
                      <option value="Gunny Bags / Jute Sacks">Gunny Bags / Jute Sacks</option>
                      <option value="Wooden Pallets">Wooden Pallets</option>
                      <option value="Salted & Packed in Drums">Salted & Packed in Drums</option>
                      <option value="Vacuum Packaging (meat, fish, processed dairy)">Vacuum Packaging (meat, fish, processed dairy)</option>
                      <option value="MAP (Modified Atmosphere Packaging)">MAP (Modified Atmosphere Packaging)</option>
                      <option value="Frozen Cartons (with polythene lining)">Frozen Cartons (with polythene lining)</option>
                      <option value="Insulated Boxes / Cold Chain Containers">Insulated Boxes / Cold Chain Containers</option>
                      <option value="Export Palletized Cartons">Export Palletized Cartons</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.livestock_lot_size || ""}
                      onChange={(e) => handleAttributeChange("livestock_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.livestock_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("livestock_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.livestock_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("livestock_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Veterinary Health Certificate">Veterinary Health Certificate</option>
                      <option value="Animal Quarantine Clearance">Animal Quarantine Clearance</option>
                      <option value="OIE (World Organisation for Animal Health) Standards compliance">OIE (World Organisation for Animal Health) Standards compliance</option>
                      <option value="HACCP (Hazard Analysis and Critical Control Points) - for food safety">HACCP (Hazard Analysis and Critical Control Points) - for food safety</option>
                      <option value="ISO 22000">ISO 22000</option>
                      <option value="FSSC 22000">FSSC 22000</option>
                      <option value="FSSAI (India)">FSSAI (India)</option>
                      <option value="FDA (US)">FDA (US)</option>
                      <option value="EFSA (EU)">EFSA (EU)</option>
                      <option value="BIS">BIS</option>
                      <option value="Agmark">Agmark</option>
                      <option value="Codex Alimentarius Standards">Codex Alimentarius Standards</option>
                      <option value="Halal Certification">Halal Certification</option>
                      <option value="Kosher Certification">Kosher Certification</option>
                      <option value="NPOP">NPOP</option>
                      <option value="USDA Organic">USDA Organic</option>
                      <option value="EU Organic">EU Organic</option>
                      <option value="ISI Mark">ISI Mark</option>
                      <option value="Egg Grading Certification (A, B, C grade)">Egg Grading Certification (A, B, C grade)</option>
                      <option value="Salmonella-Free">Salmonella-Free</option>
                      <option value="Antibiotic-Free Certification">Antibiotic-Free Certification</option>
                      <option value="Animal Welfare Certification">Animal Welfare Certification</option>
                      <option value="Export Certification from APEDA (India)">Export Certification from APEDA (India)</option>
                      <option value="MPEDA Certification (India)">MPEDA Certification (India)</option>
                      <option value="Best Aquaculture Practices (BAP)">Best Aquaculture Practices (BAP)</option>
                      <option value="Marine Stewardship Council (MSC)">Marine Stewardship Council (MSC)</option>
                      <option value="Aquaculture Stewardship Council (ASC)">Aquaculture Stewardship Council (ASC)</option>
                      <option value="Ecolabels">Ecolabels</option>
                      <option value="Leather Working Group (LWG) Certification">Leather Working Group (LWG) Certification</option>
                      <option value="OEKO-TEX Certification (for wool/textiles)">OEKO-TEX Certification (for wool/textiles)</option>
                      <option value="Organic Wool Certification (GOTS, RWS)">Organic Wool Certification (GOTS, RWS)</option>
                      <option value="CITES Certificate">CITES Certificate</option>
                      <option value="Export Health Certificate">Export Health Certificate</option>
                      <option value="Phytosanitary">Phytosanitary</option>
                      <option value="Zoosanitary Certificate">Zoosanitary Certificate</option>
                      <option value="Chamber of Commerce Certificate of Origin">Chamber of Commerce Certificate of Origin</option>
                      <option value="EU">EU</option>
                      <option value="USDA">USDA</option>
                      <option value="UK Defra Approvals">UK Defra Approvals</option>
                      <option value="Customs">Customs</option>
                      <option value="Export Inspection Agency Certificates">Export Inspection Agency Certificates</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Species / Breed</label>
                    <select
                      value={form.product_attributes.species_breed || ""}
                      onChange={(e) => handleAttributeChange("species_breed", e.target.value)}
                    >
                      <option value="">Select</option>
                      <optgroup label="Cattle Breeds">
                        <option value="Holstein Friesian">Holstein Friesian</option>
                        <option value="Jersey">Jersey</option>
                        <option value="Gir">Gir</option>
                        <option value="Sahiwal">Sahiwal</option>
                        <option value="Red Sindhi">Red Sindhi</option>
                        <option value="Tharparkar">Tharparkar</option>
                      </optgroup>
                      <optgroup label="Buffalo Breeds">
                        <option value="Murrah">Murrah</option>
                        <option value="Mehsana">Mehsana</option>
                        <option value="Nili-Ravi">Nili-Ravi</option>
                        <option value="Jafarabad">Jafarabad</option>
                        <option value="Surti">Surti</option>
                      </optgroup>
                      <optgroup label="Sheep Breeds">
                        <option value="Merino">Merino</option>
                        <option value="Dorper">Dorper</option>
                        <option value="Suffolk">Suffolk</option>
                        <option value="Rambouillet">Rambouillet</option>
                        <option value="Deccani">Deccani</option>
                        <option value="Nellore">Nellore</option>
                        <option value="Chokla">Chokla</option>
                        <option value="Malpura">Malpura</option>
                      </optgroup>
                      <optgroup label="Goat Breeds">
                        <option value="Boer">Boer</option>
                        <option value="Sirohi">Sirohi</option>
                        <option value="Jamunapari">Jamunapari</option>
                        <option value="Barbari">Barbari</option>
                        <option value="Beetal">Beetal</option>
                        <option value="Osmanabadi">Osmanabadi</option>
                      </optgroup>
                      <optgroup label="Poultry Breeds">
                        <option value="Cobb 500">Cobb 500</option>
                        <option value="Ross 308">Ross 308</option>
                        <option value="Hubbard">Hubbard</option>
                        <option value="Hy-Line">Hy-Line</option>
                        <option value="Lohmann Brown">Lohmann Brown</option>
                        <option value="ISA Brown">ISA Brown</option>
                        <option value="BV 300">BV 300</option>
                        <option value="Kadaknath">Kadaknath</option>
                        <option value="Aseel">Aseel</option>
                        <option value="Rhode Island Red">Rhode Island Red</option>
                        <option value="White Leghorn">White Leghorn</option>
                      </optgroup>
                      <optgroup label="Fish Species">
                        <option value="Rohu">Rohu</option>
                        <option value="Catla">Catla</option>
                        <option value="Mrigal">Mrigal</option>
                        <option value="Tilapia">Tilapia</option>
                        <option value="Pangasius">Pangasius</option>
                        <option value="Seabass">Seabass</option>
                        <option value="Tuna">Tuna</option>
                        <option value="Mackerel">Mackerel</option>
                        <option value="Kingfish">Kingfish</option>
                        <option value="Vannamei">Vannamei</option>
                        <option value="Black Tiger">Black Tiger</option>
                      </optgroup>
                      <optgroup label="Specialty">
                        <option value="Free-range">Free-range</option>
                        <option value="Organic">Organic</option>
                        <option value="Omega-3">Omega-3</option>
                        <option value="Duck Eggs">Duck Eggs</option>
                        <option value="Quail Eggs">Quail Eggs</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Product Type</label>
                    <select
                      value={form.product_attributes.livestock_product_type || ""}
                      onChange={(e) => handleAttributeChange("livestock_product_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <optgroup label="Live Animal">
                        <option value="Live Animal - Breeding/Dairy/Draught">Live Animal - Breeding/Dairy/Draught</option>
                        <option value="Live Animal - Breeding/Meat/Wool">Live Animal - Breeding/Meat/Wool</option>
                      </optgroup>
                      <optgroup label="Meat/Beef">
                        <option value="Meat/Beef - Fresh">Meat/Beef - Fresh</option>
                        <option value="Meat/Beef - Frozen">Meat/Beef - Frozen</option>
                        <option value="Meat/Beef - Processed">Meat/Beef - Processed</option>
                      </optgroup>
                      <optgroup label="Milk">
                        <option value="Milk - Raw">Milk - Raw</option>
                        <option value="Milk - Chilled">Milk - Chilled</option>
                        <option value="Milk - Processed">Milk - Processed</option>
                        <option value="Milk - A2">Milk - A2</option>
                        <option value="Milk - Organic">Milk - Organic</option>
                        <option value="Milk - Goat/Sheep Milk">Milk - Goat/Sheep Milk</option>
                        <option value="Milk - Pasteurized (Toned, Double Toned, Skimmed, Whole)">Milk - Pasteurized (Toned, Double Toned, Skimmed, Whole)</option>
                      </optgroup>
                      <optgroup label="By-products">
                        <option value="By-products - Dung">By-products - Dung</option>
                        <option value="By-products - Urine (organic/agriculture use)">By-products - Urine (organic/agriculture use)</option>
                        <option value="By-products - Feathers">By-products - Feathers</option>
                      </optgroup>
                      <optgroup label="Wool">
                        <option value="Wool - Raw Wool">Wool - Raw Wool</option>
                        <option value="Wool - Scoured">Wool - Scoured</option>
                        <option value="Wool - Yarn">Wool - Yarn</option>
                        <option value="Wool - Wool Tops">Wool - Wool Tops</option>
                      </optgroup>
                      <optgroup label="Skins/Hides">
                        <option value="Skins/Hides - Raw">Skins/Hides - Raw</option>
                        <option value="Skins/Hides - Tanned">Skins/Hides - Tanned</option>
                        <option value="Skins/Hides - Semi-finished">Skins/Hides - Semi-finished</option>
                        <option value="Skins/Hides - Pickled">Skins/Hides - Pickled</option>
                        <option value="Skins/Hides - Wet Blue">Skins/Hides - Wet Blue</option>
                        <option value="Skins/Hides - Finished Leather">Skins/Hides - Finished Leather</option>
                      </optgroup>
                      <optgroup label="Poultry">
                        <option value="Live Birds - Broilers">Live Birds - Broilers</option>
                        <option value="Live Birds - Layers">Live Birds - Layers</option>
                        <option value="Live Birds - Breeding Stock">Live Birds - Breeding Stock</option>
                        <option value="Live Birds - Day-Old Chicks">Live Birds - Day-Old Chicks</option>
                        <option value="Processed Chicken - Fresh">Processed Chicken - Fresh</option>
                        <option value="Processed Chicken - Frozen">Processed Chicken - Frozen</option>
                        <option value="Eggs - Table Eggs">Eggs - Table Eggs</option>
                        <option value="Eggs - Hatching Eggs">Eggs - Hatching Eggs</option>
                        <option value="Eggs - Specialty Eggs (Organic, Free-range, Omega-3 Enriched, Duck, Quail)">Eggs - Specialty Eggs (Organic, Free-range, Omega-3 Enriched, Duck, Quail)</option>
                        <option value="Eggs - White/Brown">Eggs - White/Brown</option>
                      </optgroup>
                      <optgroup label="Fish">
                        <option value="Live Fish - Breeding Stock">Live Fish - Breeding Stock</option>
                        <option value="Live Fish - Fingerlings">Live Fish - Fingerlings</option>
                        <option value="Live Fish - Fry">Live Fish - Fry</option>
                        <option value="Table Fish - Fresh">Table Fish - Fresh</option>
                        <option value="Table Fish - Frozen">Table Fish - Frozen</option>
                        <option value="Table Fish - Chilled">Table Fish - Chilled</option>
                        <option value="Table Fish - Dried">Table Fish - Dried</option>
                        <option value="Processed Fish - Fillets">Processed Fish - Fillets</option>
                        <option value="Processed Fish - Canned">Processed Fish - Canned</option>
                        <option value="Processed Fish - Smoked">Processed Fish - Smoked</option>
                        <option value="Shellfish/Shrimp - Live">Shellfish/Shrimp - Live</option>
                        <option value="Shellfish/Shrimp - Processed">Shellfish/Shrimp - Processed</option>
                        <option value="Ornamental Fish">Ornamental Fish</option>
                      </optgroup>
                      <optgroup label="Dairy Products">
                        <option value="Dairy Products - Butter">Dairy Products - Butter</option>
                        <option value="Dairy Products - Cheese">Dairy Products - Cheese</option>
                        <option value="Dairy Products - Yogurt/Curd">Dairy Products - Yogurt/Curd</option>
                        <option value="Dairy Products - Ghee">Dairy Products - Ghee</option>
                        <option value="Dairy Products - Cream">Dairy Products - Cream</option>
                        <option value="Dairy Products - Milk Powder/Condensed Milk">Dairy Products - Milk Powder/Condensed Milk</option>
                        <option value="Specialty Dairy - Organic">Specialty Dairy - Organic</option>
                        <option value="Specialty Dairy - A2">Specialty Dairy - A2</option>
                        <option value="Specialty Dairy - Fortified">Specialty Dairy - Fortified</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      value={form.product_attributes.gender || ""}
                      onChange={(e) => handleAttributeChange("gender", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Unisex">Unisex</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Weight</label>
                    <input
                      type="text"
                      value={form.product_attributes.weight || ""}
                      onChange={(e) => handleAttributeChange("weight", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Weight / Bale Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.weight_bale_size || ""}
                      onChange={(e) => handleAttributeChange("weight_bale_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Unit Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.unit_size || ""}
                      onChange={(e) => handleAttributeChange("unit_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Age</label>
                    <input
                      type="text"
                      value={form.product_attributes.age || ""}
                      onChange={(e) => handleAttributeChange("age", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Vaccination Status</label>
                    <input
                      type="text"
                      value={form.product_attributes.vaccination_status || ""}
                      onChange={(e) => handleAttributeChange("vaccination_status", e.target.value)}
                      placeholder="Enter (e.g., FMD, HS, BQ, Brucellosis, etc.)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Storage Temperature</label>
                    <input
                      type="text"
                      value={form.product_attributes.storage_temperature || ""}
                      onChange={(e) => handleAttributeChange("storage_temperature", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Treatment</label>
                    <select
                      value={form.product_attributes.treatment || ""}
                      onChange={(e) => handleAttributeChange("treatment", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="No Treatment/Natural">No Treatment/Natural</option>
                      <option value="Vaccinated/Health-Checked">Vaccinated/Health-Checked</option>
                      <option value="Conditioned/Acclimatized">Conditioned/Acclimatized</option>
                      <option value="Feed/Nutrition Treatment">Feed/Nutrition Treatment</option>
                      <option value="Fresh/Chilled">Fresh/Chilled</option>
                      <option value="Frozen - long-term storage">Frozen - long-term storage</option>
                      <option value="Processed/Value-Added">Processed/Value-Added</option>
                      <option value="Halal/Kosher Certified Treatment">Halal/Kosher Certified Treatment</option>
                      <option value="Cured/Salted/Marinated">Cured/Salted/Marinated</option>
                      <option value="Vacuum Packed/MAP">Vacuum Packed/MAP</option>
                      <option value="Raw Milk - unprocessed">Raw Milk - unprocessed</option>
                      <option value="Pasteurized Milk - heat-treated to remove pathogens">Pasteurized Milk - heat-treated to remove pathogens</option>
                      <option value="Ultra-Heat Treated (UHT)">Ultra-Heat Treated (UHT)</option>
                      <option value="Long Life Milk - extended shelf life">Long Life Milk - extended shelf life</option>
                      <option value="Fortified/Enriched - added vitamins/minerals">Fortified/Enriched - added vitamins/minerals</option>
                      <option value="Fermented/Cultured - yogurt, cheese, kefir">Fermented/Cultured - yogurt, cheese, kefir</option>
                      <option value="Homogenized - uniform fat distribution">Homogenized - uniform fat distribution</option>
                      <option value="A2/Organic Treatment">A2/Organic Treatment</option>
                      <option value="Ghee/Butter Processed">Ghee/Butter Processed</option>
                      <option value="Fresh/Unprocessed">Fresh/Unprocessed</option>
                      <option value="Washed/Graded/Sanitized">Washed/Graded/Sanitized</option>
                      <option value="Pasteurized">Pasteurized</option>
                      <option value="Coated/Waxed">Coated/Waxed</option>
                      <option value="Fortified/Specialty - omega-3 enriched, organic, free-range">Fortified/Specialty - omega-3 enriched, organic, free-range</option>
                      <option value="Live - sold for stocking or breeding">Live - sold for stocking or breeding</option>
                      <option value="Chilled/Fresh - short-term consumption">Chilled/Fresh - short-term consumption</option>
                      <option value="Frozen - transport or export">Frozen - transport or export</option>
                      <option value="Processed - fillets, smoked, canned">Processed - fillets, smoked, canned</option>
                      <option value="Antibiotic Free/Organic Treatment">Antibiotic Free/Organic Treatment</option>
                      <option value="Raw/Salted/Fresh">Raw/Salted/Fresh</option>
                      <option value="Pickled/Preserved">Pickled/Preserved</option>
                      <option value="Tanned - vegetable, chrome, or hybrid tanning">Tanned - vegetable, chrome, or hybrid tanning</option>
                      <option value="Finished/Softened/Conditioned">Finished/Softened/Conditioned</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Purpose</label>
                    <select
                      value={form.product_attributes.purpose || ""}
                      onChange={(e) => handleAttributeChange("purpose", e.target.value)}
                    >
                      <option value="">Select</option>
                      <optgroup label="Dairy Production">
                        <option value="Milk">Milk</option>
                        <option value="Cheese">Cheese</option>
                        <option value="Yogurt">Yogurt</option>
                        <option value="Butter">Butter</option>
                      </optgroup>
                      <optgroup label="Breeding & Production">
                        <option value="Breeding">Breeding</option>
                        <option value="Genetic improvement">Genetic improvement</option>
                        <option value="Meat Production">Meat Production</option>
                        <option value="Beef Production">Beef Production</option>
                        <option value="Mutton">Mutton</option>
                        <option value="Chevon">Chevon</option>
                      </optgroup>
                      <optgroup label="Work & Dual Purpose">
                        <option value="Work">Work</option>
                        <option value="Drought Power">Drought Power</option>
                        <option value="Milk + work">Milk + work</option>
                        <option value="Milk + meat">Milk + meat</option>
                        <option value="Meat + wool">Meat + wool</option>
                        <option value="Meat + milk">Meat + milk</option>
                        <option value="Eggs (native breeds)">Eggs (native breeds)</option>
                      </optgroup>
                      <optgroup label="Wool & Fiber">
                        <option value="Wool">Wool</option>
                        <option value="Fiber Production">Fiber Production</option>
                      </optgroup>
                      <optgroup label="Poultry">
                        <option value="Broiler (Meat Production)">Broiler (Meat Production)</option>
                        <option value="Layer (Egg Production)">Layer (Egg Production)</option>
                        <option value="Breeding Stock (Parent Lines/Hatcheries)">Breeding Stock (Parent Lines/Hatcheries)</option>
                        <option value="Backyard">Backyard</option>
                        <option value="Free-Range Poultry">Free-Range Poultry</option>
                      </optgroup>
                      <optgroup label="Aquaculture">
                        <option value="Table Fish">Table Fish</option>
                        <option value="Edible Production">Edible Production</option>
                        <option value="Hatchery Stock">Hatchery Stock</option>
                        <option value="Aquaculture Seed">Aquaculture Seed</option>
                        <option value="Fingerlings">Fingerlings</option>
                        <option value="Export-Oriented Species (shrimp, tuna, seabass)">Export-Oriented Species (shrimp, tuna, seabass)</option>
                        <option value="Ornamental/Aquarium Purpose">Ornamental/Aquarium Purpose</option>
                      </optgroup>
                      <optgroup label="Consumption">
                        <option value="Table Consumption (Retail/Household)">Table Consumption (Retail/Household)</option>
                        <option value="Commercial/Bulk Supply (Hotels, Restaurants, Processors)">Commercial/Bulk Supply (Hotels, Restaurants, Processors)</option>
                        <option value="Hatching/Breeding Purpose">Hatching/Breeding Purpose</option>
                        <option value="Specialty Eggs (Omega-3 enriched, Organic, Free-range)">Specialty Eggs (Omega-3 enriched, Organic, Free-range)</option>
                        <option value="Direct Consumption (Raw/Pasteurized Milk)">Direct Consumption (Raw/Pasteurized Milk)</option>
                        <option value="Value-Added Processing (Butter, Cheese, Yogurt, Ghee, Powder)">Value-Added Processing (Butter, Cheese, Yogurt, Ghee, Powder)</option>
                        <option value="Baby/Nutritional Use (Infant formulas, fortified milk)">Baby/Nutritional Use (Infant formulas, fortified milk)</option>
                        <option value="Export-Grade Dairy Products">Export-Grade Dairy Products</option>
                      </optgroup>
                      <optgroup label="Industrial">
                        <option value="Leather Industry (Footwear, Bags, Belts, Upholstery)">Leather Industry (Footwear, Bags, Belts, Upholstery)</option>
                        <option value="Wool Industry (Textiles, Carpets, Apparel)">Wool Industry (Textiles, Carpets, Apparel)</option>
                        <option value="Industrial Use (Gloves, Saddlery, Machinery Belting)">Industrial Use (Gloves, Saddlery, Machinery Belting)</option>
                        <option value="Export-Grade Skins/Wool">Export-Grade Skins/Wool</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Health Certificate</label>
                    <select
                      value={form.product_attributes.health_certificate || ""}
                      onChange={(e) => handleAttributeChange("health_certificate", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Available">Available</option>
                      <option value="Not Available">Not Available</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Type (Hides/Skins)</label>
                    <select
                      value={form.product_attributes.type_hides_skins || ""}
                      onChange={(e) => handleAttributeChange("type_hides_skins", e.target.value)}
                    >
                      <option value="">Select</option>
                      <optgroup label="Bovine Hides">
                        <option value="Cow">Cow</option>
                        <option value="Buffalo">Buffalo</option>
                        <option value="Calf">Calf</option>
                      </optgroup>
                      <optgroup label="Sheep Skins">
                        <option value="Sheep">Sheep</option>
                        <option value="Lamb">Lamb</option>
                      </optgroup>
                      <optgroup label="Goat Skins">
                        <option value="Kid">Kid</option>
                        <option value="Adult Goat">Adult Goat</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="Camel Hides">Camel Hides</option>
                        <option value="Pig Skins">Pig Skins</option>
                        <option value="Crocodile (requires CITES certificate)">Crocodile (requires CITES certificate)</option>
                        <option value="Snake (requires CITES certificate)">Snake (requires CITES certificate)</option>
                        <option value="Ostrich (requires CITES certificate)">Ostrich (requires CITES certificate)</option>
                      </optgroup>
                      <optgroup label="Processing State">
                        <option value="Raw Hide/Skin - Fresh">Raw Hide/Skin - Fresh</option>
                        <option value="Raw Hide/Skin - Salted">Raw Hide/Skin - Salted</option>
                        <option value="Raw Hide/Skin - Dried">Raw Hide/Skin - Dried</option>
                        <option value="Pickled Hide/Skin - Acid-treated for preservation">Pickled Hide/Skin - Acid-treated for preservation</option>
                        <option value="Wet Blue Leather - Chrome-tanned intermediate product">Wet Blue Leather - Chrome-tanned intermediate product</option>
                        <option value="Crust Leather - Semi-processed, ready for finishing">Crust Leather - Semi-processed, ready for finishing</option>
                        <option value="Finished Leather - Fully processed, ready for use in goods">Finished Leather - Fully processed, ready for use in goods</option>
                        <option value="Upholstery Leather - Furniture, automotive">Upholstery Leather - Furniture, automotive</option>
                        <option value="Footwear Leather">Footwear Leather</option>
                        <option value="Garment Leather - Jackets, gloves">Garment Leather - Jackets, gloves</option>
                        <option value="Industrial Leather - Belts, safety gear">Industrial Leather - Belts, safety gear</option>
                        <option value="Luxury/Exotic Leather - Bags, fashion goods">Luxury/Exotic Leather - Bags, fashion goods</option>
                        <option value="Wool-on Skins - Sheepskin with wool intact (e.g., rugs, coats)">Wool-on Skins - Sheepskin with wool intact (e.g., rugs, coats)</option>
                        <option value="Full Grain - Highest quality, natural surface">Full Grain - Highest quality, natural surface</option>
                        <option value="Top Grain - Slightly corrected surface">Top Grain - Slightly corrected surface</option>
                        <option value="Split Leather - Inner layer, suede finish">Split Leather - Inner layer, suede finish</option>
                        <option value="Bonded Leather - Reconstituted scraps">Bonded Leather - Reconstituted scraps</option>
                        <option value="Chrome-Free/Vegetable-Tanned Leather">Chrome-Free/Vegetable-Tanned Leather</option>
                      </optgroup>
                    </select>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 14 ? (
            <>
              {/* Retail & FMCG Section */}
              <section className="form-section">
                <h2>Retail & FMCG</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.retail_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("retail_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Overstock">Overstock</option>
                      <option value="Damaged Packaging">Damaged Packaging</option>
                      <option value="Returns">Returns</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.retail_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("retail_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.retail_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("retail_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <select
                      value={form.product_attributes.retail_packaging_unit || ""}
                      onChange={(e) => handleAttributeChange("retail_packaging_unit", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Bottle">Bottle</option>
                      <option value="Pouch">Pouch</option>
                      <option value="Jar">Jar</option>
                      <option value="Can">Can</option>
                      <option value="Tetra Pack">Tetra Pack</option>
                      <option value="Sachet">Sachet</option>
                      <option value="Box">Box</option>
                      <option value="Carton">Carton</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.retail_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("retail_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.retail_lot_size || ""}
                      onChange={(e) => handleAttributeChange("retail_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.retail_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("retail_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <select
                      value={form.product_attributes.retail_certification_compliance || ""}
                      onChange={(e) => handleAttributeChange("retail_certification_compliance", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="FSSAI">FSSAI</option>
                      <option value="US FDA">US FDA</option>
                      <option value="EFSA">EFSA</option>
                      <option value="ISO 22000">ISO 22000</option>
                      <option value="HACCP">HACCP</option>
                      <option value="Halal">Halal</option>
                      <option value="Kosher">Kosher</option>
                      <option value="Organic">Organic</option>
                      <option value="Fair Trade">Fair Trade</option>
                      <option value="Gluten-Free">Gluten-Free</option>
                      <option value="Non-GMO">Non-GMO</option>
                      <option value="Allergen Compliance">Allergen Compliance</option>
                      <option value="BIS Certification">BIS Certification</option>
                      <option value="CE Marking">CE Marking</option>
                      <option value="UL Certification">UL Certification</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 45001">ISO 45001</option>
                      <option value="RoHS Compliance">RoHS Compliance</option>
                      <option value="WEEE Compliance">WEEE Compliance</option>
                      <option value="REACH Compliance">REACH Compliance</option>
                      <option value="Child Safety Standards">Child Safety Standards</option>
                      <option value="Eco-Label Certifications">Eco-Label Certifications</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Product Category</label>
                    <input
                      type="text"
                      value={form.product_attributes.product_category || ""}
                      onChange={(e) => handleAttributeChange("product_category", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Flavor</label>
                    <input
                      type="text"
                      value={form.product_attributes.flavor || ""}
                      onChange={(e) => handleAttributeChange("flavor", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Return Grade</label>
                    <select
                      value={form.product_attributes.return_grade || ""}
                      onChange={(e) => handleAttributeChange("return_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Returnable">Returnable</option>
                      <option value="Non-returnable">Non-returnable</option>
                      <option value="Recall Batch">Recall Batch</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Manufacturing Date</label>
                    <input
                      type="date"
                      value={form.product_attributes.manufacturing_date || ""}
                      onChange={(e) => handleAttributeChange("manufacturing_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Nutritional / Ingredient Label</label>
                    <input
                      type="text"
                      value={form.product_attributes.nutritional_ingredient_label || ""}
                      onChange={(e) => handleAttributeChange("nutritional_ingredient_label", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Storage Conditions</label>
                    <select
                      value={form.product_attributes.storage_conditions || ""}
                      onChange={(e) => handleAttributeChange("storage_conditions", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Ambient">Ambient</option>
                      <option value="Refrigerated">Refrigerated</option>
                      <option value="Frozen">Frozen</option>
                      <option value="Dry">Dry</option>
                      <option value="Humidity-controlled">Humidity-controlled</option>
                      <option value="Light-sensitive">Light-sensitive</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Allergen Information</label>
                    <input
                      type="text"
                      value={form.product_attributes.allergen_information || ""}
                      onChange={(e) => handleAttributeChange("allergen_information", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Handling Instructions</label>
                    <select
                      value={form.product_attributes.handling_instructions || ""}
                      onChange={(e) => handleAttributeChange("handling_instructions", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Fragile">Fragile</option>
                      <option value="Keep Upright">Keep Upright</option>
                      <option value="Do Not Freeze">Do Not Freeze</option>
                      <option value="Shake Before Use">Shake Before Use</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Storage Temperature</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.storage_temperature || ""}
                        onChange={(e) => handleAttributeChange("storage_temperature", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "°C", label: "°C" },
                          { value: "°F", label: "°F" }
                        ]}
                        onChange={(opt) => handleAttributeChange("storage_temperature_unit", opt?.value || "")}
                        value={[
                          { value: "°C", label: "°C" },
                          { value: "°F", label: "°F" }
                        ].find(opt => opt.value === form.product_attributes.storage_temperature_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Regulatory Certifications</label>
                    <select
                      value={form.product_attributes.regulatory_certifications || ""}
                      onChange={(e) => handleAttributeChange("regulatory_certifications", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="FSSAI">FSSAI</option>
                      <option value="US FDA">US FDA</option>
                      <option value="EFSA">EFSA</option>
                      <option value="ISO 22000">ISO 22000</option>
                      <option value="HACCP">HACCP</option>
                      <option value="Halal">Halal</option>
                      <option value="Kosher">Kosher</option>
                      <option value="Organic">Organic</option>
                      <option value="Fair Trade">Fair Trade</option>
                      <option value="Gluten-Free">Gluten-Free</option>
                      <option value="Non-GMO">Non-GMO</option>
                      <option value="Allergen Compliance">Allergen Compliance</option>
                      <option value="BIS Certification">BIS Certification</option>
                      <option value="CE Marking">CE Marking</option>
                      <option value="UL Certification">UL Certification</option>
                      <option value="ISO 9001">ISO 9001</option>
                      <option value="ISO 14001">ISO 14001</option>
                      <option value="ISO 45001">ISO 45001</option>
                      <option value="RoHS Compliance">RoHS Compliance</option>
                      <option value="WEEE Compliance">WEEE Compliance</option>
                      <option value="REACH Compliance">REACH Compliance</option>
                      <option value="Child Safety Standards">Child Safety Standards</option>
                      <option value="Eco-Label Certifications">Eco-Label Certifications</option>
                    </select>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 13 ? (
            <>
              {/* Real Estate & Leasing Section */}
              <section className="form-section">
                <h2>Real Estate & Leasing</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.real_estate_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("real_estate_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Recently built">Recently built</option>
                      <option value="never occupied">never occupied</option>
                      <option value="Ongoing development">Ongoing development</option>
                      <option value="With RERA registration">With RERA registration</option>
                      <option value="Delivery timeline available">Delivery timeline available</option>
                      <option value="Fully constructed, immediately usable">Fully constructed, immediately usable</option>
                      <option value="With all utilities connected">With all utilities connected</option>
                      <option value="Recently upgraded interiors / exteriors">Recently upgraded interiors / exteriors</option>
                      <option value="Modernized facilities">Modernized facilities</option>
                      <option value="Pre-owned but functional and in good condition">Pre-owned but functional and in good condition</option>
                      <option value="Regular maintenance records available">Regular maintenance records available</option>
                      <option value="Structurally safe but requires cosmetic / minor repair work">Structurally safe but requires cosmetic / minor repair work</option>
                      <option value="Outdated interiors, plumbing, or electricals">Outdated interiors, plumbing, or electricals</option>
                      <option value="Structurally weak or condemned building">Structurally weak or condemned building</option>
                      <option value="Not suitable for immediate occupancy without major restoration">Not suitable for immediate occupancy without major restoration</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.real_estate_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("real_estate_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.real_estate_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("real_estate_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <select
                      value={form.product_attributes.real_estate_packaging_unit || ""}
                      onChange={(e) => handleAttributeChange("real_estate_packaging_unit", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.real_estate_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("real_estate_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.real_estate_lot_size || ""}
                      onChange={(e) => handleAttributeChange("real_estate_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.real_estate_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("real_estate_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <Select
                      className="input-pill"
                      placeholder="Select"
                      isMulti
                      options={[
                        { value: "Ownership Title Deed", label: "Ownership Title Deed" },
                        { value: "Encumbrance Certificate (EC)", label: "Encumbrance Certificate (EC)" },
                        { value: "Land Use Conversion Approval", label: "Land Use Conversion Approval" },
                        { value: "Mutation Certificate / Khata Certificate", label: "Mutation Certificate / Khata Certificate" },
                        { value: "Building Plan Approval", label: "Building Plan Approval" },
                        { value: "Completion Certificate (CC)", label: "Completion Certificate (CC)" },
                        { value: "Occupancy Certificate (OC)", label: "Occupancy Certificate (OC)" },
                        { value: "Development Authority / Urban Planning Approval", label: "Development Authority / Urban Planning Approval" },
                        { value: "Height Clearance", label: "Height Clearance" },
                        { value: "Fire NOC", label: "Fire NOC" },
                        { value: "Environmental Clearance", label: "Environmental Clearance" },
                        { value: "Structural Stability Certificate", label: "Structural Stability Certificate" },
                        { value: "Disaster Management & Safety Compliance", label: "Disaster Management & Safety Compliance" },
                        { value: "RERA Registration", label: "RERA Registration" },
                        { value: "Local Municipal Approval", label: "Local Municipal Approval" },
                        { value: "Zoning / Master Plan Compliance", label: "Zoning / Master Plan Compliance" },
                        { value: "Utility Connections Approval", label: "Utility Connections Approval" },
                        { value: "GST / Tax Registration", label: "GST / Tax Registration" },
                        { value: "Green Building Certification (LEED, IGBC, GRIHA)", label: "Green Building Certification (LEED, IGBC, GRIHA)" },
                        { value: "Smart Building Certification", label: "Smart Building Certification" },
                        { value: "Heritage Approval", label: "Heritage Approval" }
                      ]}
                      onChange={(selected) => {
                        const values = selected ? selected.map(opt => opt.value) : [];
                        handleAttributeChange("real_estate_certification_compliance", values.join(", "));
                      }}
                      value={form.product_attributes.real_estate_certification_compliance
                        ? form.product_attributes.real_estate_certification_compliance.split(", ").map(val => ({ value: val, label: val }))
                        : []}
                      isClearable
                      isSearchable={false}
                    />
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Asset Type</label>
                    <select
                      value={form.product_attributes.asset_type || ""}
                      onChange={(e) => handleAttributeChange("asset_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Apartment / Flat">Apartment / Flat</option>
                      <option value="Independent House / Villa">Independent House / Villa</option>
                      <option value="Duplex / Row House">Duplex / Row House</option>
                      <option value="Studio / Serviced Apartment">Studio / Serviced Apartment</option>
                      <option value="Residential Plot / Land">Residential Plot / Land</option>
                      <option value="Office Space">Office Space</option>
                      <option value="Retail Space">Retail Space</option>
                      <option value="Hospitality">Hospitality</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Educational">Educational</option>
                      <option value="Factory / Manufacturing Unit">Factory / Manufacturing Unit</option>
                      <option value="Warehouse / Cold Storage">Warehouse / Cold Storage</option>
                      <option value="Industrial Land / Plot">Industrial Land / Plot</option>
                      <option value="Logistics Hub / Distribution Center">Logistics Hub / Distribution Center</option>
                      <option value="Data Center">Data Center</option>
                      <option value="Agricultural Land / Farm">Agricultural Land / Farm</option>
                      <option value="Plantation / Orchard Land">Plantation / Orchard Land</option>
                      <option value="Open Land / Vacant Plot">Open Land / Vacant Plot</option>
                      <option value="Special Economic Zone (SEZ) Land">Special Economic Zone (SEZ) Land</option>
                      <option value="Mining / Quarry Land">Mining / Quarry Land</option>
                      <option value="Mixed-Use Development">Mixed-Use Development</option>
                      <option value="Government / Institutional Property">Government / Institutional Property</option>
                      <option value="Religious / Community Spaces">Religious / Community Spaces</option>
                      <option value="Recreational Spaces">Recreational Spaces</option>
                      <option value="Parking Facility / Standalone Parking Lot">Parking Facility / Standalone Parking Lot</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Ownership / Title</label>
                    <input
                      type="text"
                      value={form.product_attributes.ownership_title || ""}
                      onChange={(e) => handleAttributeChange("ownership_title", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Tenure / Lease Terms</label>
                    <select
                      value={form.product_attributes.tenure_lease_terms || ""}
                      onChange={(e) => handleAttributeChange("tenure_lease_terms", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Annual">Annual</option>
                      <option value="Long-term">Long-term</option>
                      <option value="Renewable">Renewable</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Utilities</label>
                    <Select
                      className="input-pill"
                      placeholder="Select"
                      isMulti
                      options={[
                        { value: "Municipal Water Connection", label: "Municipal Water Connection" },
                        { value: "Borewell / Groundwater Source", label: "Borewell / Groundwater Source" },
                        { value: "Overhead Tank / Underground Storage", label: "Overhead Tank / Underground Storage" },
                        { value: "Rainwater Harvesting System", label: "Rainwater Harvesting System" },
                        { value: "Greywater Recycling / Reuse", label: "Greywater Recycling / Reuse" },
                        { value: "Electricity Connection", label: "Electricity Connection" },
                        { value: "Power Backup", label: "Power Backup" },
                        { value: "Renewable Energy Integration", label: "Renewable Energy Integration" },
                        { value: "Energy Efficiency Features", label: "Energy Efficiency Features" },
                        { value: "Internet / Broadband Connection", label: "Internet / Broadband Connection" },
                        { value: "Fiber Optic Connectivity", label: "Fiber Optic Connectivity" },
                        { value: "Telephone / Landline Provision", label: "Telephone / Landline Provision" },
                        { value: "Cable / Satellite TV Access", label: "Cable / Satellite TV Access" },
                        { value: "Piped Natural Gas (PNG) Connection", label: "Piped Natural Gas (PNG) Connection" },
                        { value: "LPG Provision", label: "LPG Provision" },
                        { value: "Centralized Gas Supply", label: "Centralized Gas Supply" },
                        { value: "Municipal Sewage Connection", label: "Municipal Sewage Connection" },
                        { value: "On-site Sewage Treatment Plant (STP)", label: "On-site Sewage Treatment Plant (STP)" },
                        { value: "Solid Waste Management System", label: "Solid Waste Management System" },
                        { value: "Hazardous Waste Disposal Facility", label: "Hazardous Waste Disposal Facility" },
                        { value: "Centralized Air Conditioning (HVAC)", label: "Centralized Air Conditioning (HVAC)" },
                        { value: "Individual AC Provision", label: "Individual AC Provision" },
                        { value: "Heating System", label: "Heating System" },
                        { value: "Ventilation System", label: "Ventilation System" },
                        { value: "Smart Home / Building Automation", label: "Smart Home / Building Automation" },
                        { value: "IoT-Enabled Utility Monitoring", label: "IoT-Enabled Utility Monitoring" },
                        { value: "EV Charging Stations", label: "EV Charging Stations" },
                        { value: "Water Softening / Filtration System", label: "Water Softening / Filtration System" }
                      ]}
                      onChange={(selected) => {
                        const values = selected ? selected.map(opt => opt.value) : [];
                        handleAttributeChange("utilities", values);
                      }}
                      value={Array.isArray(form.product_attributes.utilities)
                        ? form.product_attributes.utilities.map(val => ({ value: val, label: val }))
                        : []}
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Floor / Building Height</label>
                    <input
                      type="text"
                      value={form.product_attributes.floor_building_height || ""}
                      onChange={(e) => handleAttributeChange("floor_building_height", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Size / Area</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.size_area || ""}
                        onChange={(e) => handleAttributeChange("size_area", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "sq. ft.", label: "sq. ft." },
                          { value: "sq. m.", label: "sq. m." },
                          { value: "acres", label: "acres" },
                          { value: "hectares", label: "hectares" }
                        ]}
                        onChange={(opt) => handleAttributeChange("size_area_unit", opt?.value || "")}
                        value={[
                          { value: "sq. ft.", label: "sq. ft." },
                          { value: "sq. m.", label: "sq. m." },
                          { value: "acres", label: "acres" },
                          { value: "hectares", label: "hectares" }
                        ].find(opt => opt.value === form.product_attributes.size_area_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Year Built</label>
                    <input
                      type="text"
                      value={form.product_attributes.year_built || ""}
                      onChange={(e) => handleAttributeChange("year_built", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Insurance Coverage</label>
                    <select
                      value={form.product_attributes.insurance_coverage || ""}
                      onChange={(e) => handleAttributeChange("insurance_coverage", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Parking Spaces</label>
                    <input
                      type="text"
                      value={form.product_attributes.parking_spaces || ""}
                      onChange={(e) => handleAttributeChange("parking_spaces", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Additional Facilities</label>
                    <Select
                      className="input-pill"
                      placeholder="Select"
                      isMulti
                      options={[
                        { value: "Security Services", label: "Security Services" },
                        { value: "Clubhouse / Community Hall", label: "Clubhouse / Community Hall" },
                        { value: "Swimming Pool", label: "Swimming Pool" },
                        { value: "Gym / Fitness Center", label: "Gym / Fitness Center" },
                        { value: "Children's Play Area", label: "Children's Play Area" },
                        { value: "Garden / Green Spaces", label: "Garden / Green Spaces" },
                        { value: "Lift / Elevator Facility", label: "Lift / Elevator Facility" },
                        { value: "Power Backup", label: "Power Backup" },
                        { value: "Reception / Lobby Area", label: "Reception / Lobby Area" },
                        { value: "Meeting Rooms / Conference Halls", label: "Meeting Rooms / Conference Halls" },
                        { value: "Cafeteria / Food Court", label: "Cafeteria / Food Court" },
                        { value: "High-Speed Elevators / Escalators", label: "High-Speed Elevators / Escalators" },
                        { value: "HVAC Systems", label: "HVAC Systems" },
                        { value: "Fire Safety Systems", label: "Fire Safety Systems" },
                        { value: "Visitor Parking & Employee Parking", label: "Visitor Parking & Employee Parking" },
                        { value: "Access Control / Biometric Entry", label: "Access Control / Biometric Entry" },
                        { value: "IT & Networking Infrastructure", label: "IT & Networking Infrastructure" },
                        { value: "Loading / Unloading Bays", label: "Loading / Unloading Bays" },
                        { value: "Dock Levelers / Ramps", label: "Dock Levelers / Ramps" },
                        { value: "Cold Storage / Temperature Control", label: "Cold Storage / Temperature Control" },
                        { value: "Material Handling Equipment", label: "Material Handling Equipment" },
                        { value: "Truck Parking / Large Vehicle Access", label: "Truck Parking / Large Vehicle Access" },
                        { value: "Weighbridge Facility", label: "Weighbridge Facility" },
                        { value: "Fire Suppression Systems", label: "Fire Suppression Systems" },
                        { value: "Green Building Features", label: "Green Building Features" },
                        { value: "EV Charging Stations", label: "EV Charging Stations" },
                        { value: "Disaster Management Systems", label: "Disaster Management Systems" },
                        { value: "On-Site Maintenance Team", label: "On-Site Maintenance Team" },
                        { value: "Medical / First Aid Room", label: "Medical / First Aid Room" },
                        { value: "Waste Management & Recycling Facility", label: "Waste Management & Recycling Facility" }
                      ]}
                      onChange={(selected) => {
                        const values = selected ? selected.map(opt => opt.value) : [];
                        handleAttributeChange("additional_facilities", values);
                      }}
                      value={Array.isArray(form.product_attributes.additional_facilities)
                        ? form.product_attributes.additional_facilities.map(val => ({ value: val, label: val }))
                        : []}
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Zoning / Approval Status</label>
                    <select
                      value={form.product_attributes.zoning_approval_status || ""}
                      onChange={(e) => handleAttributeChange("zoning_approval_status", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="R1 – Low-Density Residential">R1 – Low-Density Residential</option>
                      <option value="R2 – Medium-Density Residential">R2 – Medium-Density Residential</option>
                      <option value="R3 – High-Density Residential">R3 – High-Density Residential</option>
                      <option value="Mixed Residential">Mixed Residential</option>
                      <option value="C1 – Retail / Shops / Showrooms">C1 – Retail / Shops / Showrooms</option>
                      <option value="C2 – Office Space / Business Parks">C2 – Office Space / Business Parks</option>
                      <option value="C3 – Hospitality">C3 – Hospitality</option>
                      <option value="C4 – Healthcare / Clinics / Nursing Homes">C4 – Healthcare / Clinics / Nursing Homes</option>
                      <option value="I1 – Light Industrial">I1 – Light Industrial</option>
                      <option value="I2 – Medium Industrial">I2 – Medium Industrial</option>
                      <option value="I3 – Heavy Industrial">I3 – Heavy Industrial</option>
                      <option value="Special Economic Zone (SEZ)">Special Economic Zone (SEZ)</option>
                      <option value="Agricultural Land">Agricultural Land</option>
                      <option value="Green Zone">Green Zone</option>
                      <option value="Forest / Eco-Sensitive Zone">Forest / Eco-Sensitive Zone</option>
                      <option value="Institutional">Institutional</option>
                      <option value="Government / Defense Zone">Government / Defense Zone</option>
                      <option value="Recreational / Entertainment Zone">Recreational / Entertainment Zone</option>
                      <option value="Religious / Community Zoning">Religious / Community Zoning</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Environmental Approvals</label>
                    <Select
                      className="input-pill"
                      placeholder="Select"
                      isMulti
                      options={[
                        { value: "Environmental Impact Assessment (EIA)", label: "Environmental Impact Assessment (EIA)" },
                        { value: "Land Use & Zoning Clearance", label: "Land Use & Zoning Clearance" },
                        { value: "Soil Quality & Contamination Certificate", label: "Soil Quality & Contamination Certificate" },
                        { value: "Water Table / Groundwater Use Approval", label: "Water Table / Groundwater Use Approval" },
                        { value: "Wetland / Forest / Wildlife Clearance", label: "Wetland / Forest / Wildlife Clearance" },
                        { value: "Consent to Establish (CTE)", label: "Consent to Establish (CTE)" },
                        { value: "Consent to Operate (CTO)", label: "Consent to Operate (CTO)" },
                        { value: "Air & Water Pollution Clearance", label: "Air & Water Pollution Clearance" },
                        { value: "Hazardous Waste Disposal Approval", label: "Hazardous Waste Disposal Approval" },
                        { value: "Sewage Treatment Plant (STP) Compliance", label: "Sewage Treatment Plant (STP) Compliance" },
                        { value: "Rainwater Harvesting Compliance", label: "Rainwater Harvesting Compliance" },
                        { value: "Energy Efficiency Compliance", label: "Energy Efficiency Compliance" },
                        { value: "Renewable Energy Integration Approval", label: "Renewable Energy Integration Approval" },
                        { value: "Carbon Footprint / Emission Compliance Certificate", label: "Carbon Footprint / Emission Compliance Certificate" },
                        { value: "Noise Pollution Approval", label: "Noise Pollution Approval" },
                        { value: "LEED", label: "LEED" },
                        { value: "IGBC", label: "IGBC" },
                        { value: "GRIHA", label: "GRIHA" },
                        { value: "BREEAM (ent Method)", label: "BREEAM (ent Method)" },
                        { value: "WELL Certification", label: "WELL Certification" }
                      ]}
                      onChange={(selected) => {
                        const values = selected ? selected.map(opt => opt.value) : [];
                        handleAttributeChange("environmental_approvals", values);
                      }}
                      value={Array.isArray(form.product_attributes.environmental_approvals)
                        ? form.product_attributes.environmental_approvals.map(val => ({ value: val, label: val }))
                        : []}
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Floor Load Capacity</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.floor_load_capacity || ""}
                        onChange={(e) => handleAttributeChange("floor_load_capacity", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "kg", label: "kg" },
                          { value: "sq. m.", label: "sq. m." }
                        ]}
                        onChange={(opt) => handleAttributeChange("floor_load_capacity_unit", opt?.value || "")}
                        value={[
                          { value: "kg", label: "kg" },
                          { value: "sq. m.", label: "sq. m." }
                        ].find(opt => opt.value === form.product_attributes.floor_load_capacity_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 7 ? (
            <>
              {/* IT & Electronics Section */}
              <section className="form-section">
                <h2>IT & Electronics</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.it_electronics_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("it_electronics_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Rental">Rental</option>
                      <option value="Reconditioned">Reconditioned</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.it_electronics_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("it_electronics_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.it_electronics_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("it_electronics_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <select
                      value={form.product_attributes.it_electronics_packaging_unit || ""}
                      onChange={(e) => handleAttributeChange("it_electronics_packaging_unit", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.it_electronics_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("it_electronics_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.it_electronics_lot_size || ""}
                      onChange={(e) => handleAttributeChange("it_electronics_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.it_electronics_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("it_electronics_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <Select
                      className="input-pill"
                      placeholder="Select"
                      isMulti
                      options={[
                        { value: "CE Marking", label: "CE Marking" },
                        { value: "FCC", label: "FCC" },
                        { value: "UL", label: "UL" },
                        { value: "CSA", label: "CSA" },
                        { value: "EAC", label: "EAC" },
                        { value: "RCM", label: "RCM" },
                        { value: "VCCI", label: "VCCI" },
                        { value: "KC", label: "KC" },
                        { value: "CCC", label: "CCC" },
                        { value: "BIS/ISI Mark", label: "BIS/ISI Mark" },
                        { value: "NOM", label: "NOM" },
                        { value: "PSE", label: "PSE" },
                        { value: "RoSH", label: "RoSH" },
                        { value: "REACH", label: "REACH" },
                        { value: "WEEE", label: "WEEE" },
                        { value: "ErP", label: "ErP" },
                        { value: "Energy Star", label: "Energy Star" },
                        { value: "BEE star", label: "BEE star" },
                        { value: "ISO 14001", label: "ISO 14001" },
                        { value: "Eco-Label", label: "Eco-Label" },
                        { value: "IEC", label: "IEC" },
                        { value: "IEEE", label: "IEEE" },
                        { value: "JEDEC", label: "JEDEC" },
                        { value: "PCI-SIG", label: "PCI-SIG" },
                        { value: "USB-IF", label: "USB-IF" },
                        { value: "Wi-fi", label: "Wi-fi" },
                        { value: "Bluetooth Sig", label: "Bluetooth Sig" },
                        { value: "HDMI Licensing", label: "HDMI Licensing" },
                        { value: "FIPS 140-2", label: "FIPS 140-2" },
                        { value: "TPM 2.0", label: "TPM 2.0" },
                        { value: "ISO/IEC 27001", label: "ISO/IEC 27001" },
                        { value: "ISO/IEC 15408", label: "ISO/IEC 15408" },
                        { value: "GDPR", label: "GDPR" }
                      ]}
                      onChange={(selected) => {
                        const values = selected ? selected.map(opt => opt.value) : [];
                        handleAttributeChange("it_electronics_certification_compliance", values);
                      }}
                      value={Array.isArray(form.product_attributes.it_electronics_certification_compliance)
                        ? form.product_attributes.it_electronics_certification_compliance.map(val => ({ value: val, label: val }))
                        : []}
                      isClearable
                      isSearchable={false}
                    />
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Device Type</label>
                    <input
                      type="text"
                      value={form.product_attributes.device_type || ""}
                      onChange={(e) => handleAttributeChange("device_type", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Model</label>
                    <input
                      type="text"
                      value={form.product_attributes.model || ""}
                      onChange={(e) => handleAttributeChange("model", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Ports / Connectivity</label>
                    <input
                      type="text"
                      value={form.product_attributes.ports_connectivity || ""}
                      onChange={(e) => handleAttributeChange("ports_connectivity", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Processor / Spec</label>
                    <input
                      type="text"
                      value={form.product_attributes.processor_spec || ""}
                      onChange={(e) => handleAttributeChange("processor_spec", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Memory / Storage</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.memory_storage || ""}
                        onChange={(e) => handleAttributeChange("memory_storage", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "GB", label: "GB" },
                          { value: "TB", label: "TB" }
                        ]}
                        onChange={(opt) => handleAttributeChange("memory_storage_unit", opt?.value || "")}
                        value={[
                          { value: "GB", label: "GB" },
                          { value: "TB", label: "TB" }
                        ].find(opt => opt.value === form.product_attributes.memory_storage_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Expandability Options</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.expandability_options || ""}
                        onChange={(e) => handleAttributeChange("expandability_options", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "RAM slots", label: "RAM slots" },
                          { value: "storage slots", label: "storage slots" },
                          { value: "expansion cards", label: "expansion cards" }
                        ]}
                        onChange={(opt) => handleAttributeChange("expandability_unit", opt?.value || "")}
                        value={[
                          { value: "RAM slots", label: "RAM slots" },
                          { value: "storage slots", label: "storage slots" },
                          { value: "expansion cards", label: "expansion cards" }
                        ].find(opt => opt.value === form.product_attributes.expandability_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Graphics / GPU Specs</label>
                    <select
                      value={form.product_attributes.graphics_gpu_specs || ""}
                      onChange={(e) => handleAttributeChange("graphics_gpu_specs", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Integrated Graphics">Integrated Graphics</option>
                      <option value="Dedicated GPU">Dedicated GPU</option>
                      <option value="NVIDIA">NVIDIA</option>
                      <option value="AMD">AMD</option>
                      <option value="Intel">Intel</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Networking Standards</label>
                    <select
                      value={form.product_attributes.networking_standards || ""}
                      onChange={(e) => handleAttributeChange("networking_standards", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Wi-Fi 4 (802.11n)">Wi-Fi 4 (802.11n)</option>
                      <option value="Wi-Fi 5 (802.11ac)">Wi-Fi 5 (802.11ac)</option>
                      <option value="Wi-Fi 6 (802.11ax)">Wi-Fi 6 (802.11ax)</option>
                      <option value="Wi-Fi 6E">Wi-Fi 6E</option>
                      <option value="Bluetooth 4.0">Bluetooth 4.0</option>
                      <option value="Bluetooth 4.2">Bluetooth 4.2</option>
                      <option value="Bluetooth 5.0">Bluetooth 5.0</option>
                      <option value="Bluetooth 5.1">Bluetooth 5.1</option>
                      <option value="Bluetooth 5.2">Bluetooth 5.2</option>
                      <option value="Bluetooth 5.3">Bluetooth 5.3</option>
                      <option value="Ethernet 10/100 Mbps">Ethernet 10/100 Mbps</option>
                      <option value="Ethernet 1 Gbps">Ethernet 1 Gbps</option>
                      <option value="Ethernet 10 Gbps">Ethernet 10 Gbps</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>OS / Firmware Version</label>
                    <input
                      type="text"
                      value={form.product_attributes.os_firmware_version || ""}
                      onChange={(e) => handleAttributeChange("os_firmware_version", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Warranty Status</label>
                    <select
                      value={form.product_attributes.warranty_status || ""}
                      onChange={(e) => handleAttributeChange("warranty_status", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="OEM Warranty">OEM Warranty</option>
                      <option value="Extended Warranty">Extended Warranty</option>
                      <option value="Out of Warranty">Out of Warranty</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Service / History</label>
                    <select
                      value={form.product_attributes.service_history || ""}
                      onChange={(e) => handleAttributeChange("service_history", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Software / Licenses</label>
                    <input
                      type="text"
                      value={form.product_attributes.software_licenses || ""}
                      onChange={(e) => handleAttributeChange("software_licenses", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Safety Standards</label>
                    <select
                      value={form.product_attributes.safety_standards || ""}
                      onChange={(e) => handleAttributeChange("safety_standards", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="CE Marking">CE Marking</option>
                      <option value="UL">UL</option>
                      <option value="CSA">CSA</option>
                      <option value="FCC">FCC</option>
                      <option value="IEC 60950">IEC 60950</option>
                      <option value="IEC 62368">IEC 62368</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Display spec Section */}
              <section className="form-section">
                <h2>Display spec</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Display</label>
                    <input
                      type="text"
                      value={form.product_attributes.display || ""}
                      onChange={(e) => handleAttributeChange("display", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Panel type</label>
                    <input
                      type="text"
                      value={form.product_attributes.panel_type || ""}
                      onChange={(e) => handleAttributeChange("panel_type", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Resolution</label>
                    <input
                      type="text"
                      value={form.product_attributes.resolution || ""}
                      onChange={(e) => handleAttributeChange("resolution", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Refresh Rate</label>
                    <input
                      type="text"
                      value={form.product_attributes.refresh_rate || ""}
                      onChange={(e) => handleAttributeChange("refresh_rate", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Brightness</label>
                    <input
                      type="text"
                      value={form.product_attributes.brightness || ""}
                      onChange={(e) => handleAttributeChange("brightness", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Color Gamut</label>
                    <input
                      type="text"
                      value={form.product_attributes.color_gamut || ""}
                      onChange={(e) => handleAttributeChange("color_gamut", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>HDR Support</label>
                    <select
                      value={form.product_attributes.hdr_support || ""}
                      onChange={(e) => handleAttributeChange("hdr_support", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Touchscreen</label>
                    <select
                      value={form.product_attributes.touchscreen || ""}
                      onChange={(e) => handleAttributeChange("touchscreen", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* Battery spec Section */}
              <section className="form-section">
                <h2>Battery spec</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Battery Type</label>
                    <input
                      type="text"
                      value={form.product_attributes.battery_type || ""}
                      onChange={(e) => handleAttributeChange("battery_type", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Capacity</label>
                    <input
                      type="text"
                      value={form.product_attributes.battery_capacity || ""}
                      onChange={(e) => handleAttributeChange("battery_capacity", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Backup Time</label>
                    <input
                      type="text"
                      value={form.product_attributes.backup_time || ""}
                      onChange={(e) => handleAttributeChange("backup_time", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Charging Power</label>
                    <input
                      type="text"
                      value={form.product_attributes.charging_power || ""}
                      onChange={(e) => handleAttributeChange("charging_power", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certifications</label>
                    <input
                      type="text"
                      value={form.product_attributes.battery_certifications || ""}
                      onChange={(e) => handleAttributeChange("battery_certifications", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Cycle Life</label>
                    <input
                      type="text"
                      value={form.product_attributes.cycle_life || ""}
                      onChange={(e) => handleAttributeChange("cycle_life", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>
                </div>
              </section>
            </>
          ) : parseInt(form.category_id) === 9 ? (
            <>
              {/* Medical & Laboratory Equipment Section */}
              <section className="form-section">
                <h2>Medical & Laboratory Equipment</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Condition / Grade</label>
                    <select
                      value={form.product_attributes.medical_condition_grade || ""}
                      onChange={(e) => handleAttributeChange("medical_condition_grade", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Demo / Ex-display">Demo / Ex-display</option>
                      <option value="Certified Pre-owned">Certified Pre-owned</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Year of Manufacture</label>
                    <input
                      type="text"
                      value={form.product_attributes.medical_year_of_manufacture || ""}
                      onChange={(e) => handleAttributeChange("medical_year_of_manufacture", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Shelf Life / Expire Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.medical_shelf_life_expire_date || ""}
                      onChange={(e) => handleAttributeChange("medical_shelf_life_expire_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Packaging unit</label>
                    <select
                      value={form.product_attributes.medical_packaging_unit || ""}
                      onChange={(e) => handleAttributeChange("medical_packaging_unit", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Packaging Type</label>
                    <select
                      value={form.product_attributes.medical_packaging_type || ""}
                      onChange={(e) => handleAttributeChange("medical_packaging_type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Paper Bags">Paper Bags</option>
                      <option value="HDPE / PP Laminated Bags">HDPE / PP Laminated Bags</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk Tanker">Bulk Tanker</option>
                      <option value="Loose Stack">Loose Stack</option>
                      <option value="Bundle / Pack">Bundle / Pack</option>
                      <option value="Palletized">Palletized</option>
                      <option value="Crated Packaging">Crated Packaging</option>
                      <option value="Cardboard Carton Boxes">Cardboard Carton Boxes</option>
                      <option value="Shrink-Wrapped Packs">Shrink-Wrapped Packs</option>
                      <option value="Wooden Crates">Wooden Crates</option>
                      <option value="Loose Bundle">Loose Bundle</option>
                      <option value="Strapped Coils">Strapped Coils</option>
                      <option value="Palletized Bundles">Palletized Bundles</option>
                      <option value="Custom Cut & Packaged">Custom Cut & Packaged</option>
                      <option value="Individual Wrapping">Individual Wrapping</option>
                      <option value="Wooden Frame / Crate Support">Wooden Frame / Crate Support</option>
                      <option value="Palletized Panels">Palletized Panels</option>
                      <option value="Loose Load with Protective Spacers">Loose Load with Protective Spacers</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lot Size</label>
                    <input
                      type="text"
                      value={form.product_attributes.medical_lot_size || ""}
                      onChange={(e) => handleAttributeChange("medical_lot_size", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Batch no / lot no</label>
                    <input
                      type="text"
                      value={form.product_attributes.medical_batch_no_lot_no || ""}
                      onChange={(e) => handleAttributeChange("medical_batch_no_lot_no", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Certification / Compliance</label>
                    <Select
                      className="input-pill"
                      placeholder="Select"
                      isMulti
                      options={[
                        { value: "Certified", label: "Certified" },
                        { value: "Pending", label: "Pending" },
                        { value: "Not Certified", label: "Not Certified" },
                        { value: "Regulatory", label: "Regulatory" },
                        { value: "Quality", label: "Quality" },
                        { value: "Safety", label: "Safety" },
                        { value: "Performance", label: "Performance" },
                        { value: "US FDA", label: "US FDA" },
                        { value: "CE", label: "CE" },
                        { value: "ISO", label: "ISO" },
                        { value: "CDSCO", label: "CDSCO" },
                        { value: "WHO", label: "WHO" },
                        { value: "IEC", label: "IEC" },
                        { value: "BIS", label: "BIS" },
                        { value: "ISO 13485", label: "ISO 13485" },
                        { value: "IEC 60601-1", label: "IEC 60601-1" },
                        { value: "CE-IVD", label: "CE-IVD" },
                        { value: "FDA 510(k)", label: "FDA 510(k)" },
                        { value: "Device safety", label: "Device safety" },
                        { value: "EMC", label: "EMC" },
                        { value: "Biocompatibility", label: "Biocompatibility" },
                        { value: "Sterility", label: "Sterility" },
                        { value: "IVD", label: "IVD" },
                        { value: "GMP", label: "GMP" },
                        { value: "IVD Certification", label: "IVD Certification" },
                        { value: "ISO 15189", label: "ISO 15189" },
                        { value: "CLIA Waiver", label: "CLIA Waiver" },
                        { value: "IEC 60601-2", label: "IEC 60601-2" },
                        { value: "AERB/NRC", label: "AERB/NRC" },
                        { value: "CE Mark", label: "CE Mark" },
                        { value: "BIS/CDSCO", label: "BIS/CDSCO" },
                        { value: "ISO 14971", label: "ISO 14971" },
                        { value: "RoHS/WEEE", label: "RoHS/WEEE" },
                        { value: "FDA", label: "FDA" },
                        { value: "ISO 17025", label: "ISO 17025" }
                      ]}
                      onChange={(selected) => {
                        const values = selected ? selected.map(opt => opt.value) : [];
                        handleAttributeChange("medical_certification_compliance", values);
                      }}
                      value={Array.isArray(form.product_attributes.medical_certification_compliance)
                        ? form.product_attributes.medical_certification_compliance.map(val => ({ value: val, label: val }))
                        : []}
                      isClearable
                      isSearchable={false}
                    />
                  </div>
                </div>
              </section>

              {/* Specialised Attributes Section */}
              <section className="form-section">
                <h2>Specialised Attributes</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Equipment Type</label>
                    <input
                      type="text"
                      value={form.product_attributes.equipment_type || ""}
                      onChange={(e) => handleAttributeChange("equipment_type", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Model</label>
                    <input
                      type="text"
                      value={form.product_attributes.model || ""}
                      onChange={(e) => handleAttributeChange("model", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Calibration Date</label>
                    <input
                      type="text"
                      value={form.product_attributes.last_calibration_date || ""}
                      onChange={(e) => handleAttributeChange("last_calibration_date", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Calibration Status</label>
                    <select
                      value={form.product_attributes.calibration_status || ""}
                      onChange={(e) => handleAttributeChange("calibration_status", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Calibrated">Calibrated</option>
                      <option value="Not Calibrated">Not Calibrated</option>
                      <option value="Due">Due</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Throughput / Capacity</label>
                    <input
                      type="text"
                      value={form.product_attributes.throughput_capacity || ""}
                      onChange={(e) => handleAttributeChange("throughput_capacity", e.target.value)}
                      placeholder="Enter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Power Requirements</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow-1"
                        value={form.product_attributes.power_requirements || ""}
                        onChange={(e) => handleAttributeChange("power_requirements", e.target.value)}
                        placeholder="Enter"
                      />
                      <Select
                        className="input-pill p-0 flex-grow-1"
                        placeholder="Unit"
                        options={[
                          { value: "Voltage", label: "Voltage" },
                          { value: "Frequency", label: "Frequency" },
                          { value: "Phase", label: "Phase" }
                        ]}
                        onChange={(opt) => handleAttributeChange("power_requirements_unit", opt?.value || "")}
                        value={[
                          { value: "Voltage", label: "Voltage" },
                          { value: "Frequency", label: "Frequency" },
                          { value: "Phase", label: "Phase" }
                        ].find(opt => opt.value === form.product_attributes.power_requirements_unit) || null}
                        isClearable
                        isSearchable={false}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Condition</label>
                    <select
                      value={form.product_attributes.condition || ""}
                      onChange={(e) => handleAttributeChange("condition", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                      <option value="Demo / Ex-display">Demo / Ex-display</option>
                      <option value="Certified Pre-owned">Certified Pre-owned</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Regulatory Approval</label>
                    <select
                      value={form.product_attributes.regulatory_approval || ""}
                      onChange={(e) => handleAttributeChange("regulatory_approval", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="US FDA">US FDA</option>
                      <option value="CE Mark">CE Mark</option>
                      <option value="CDSCO">CDSCO</option>
                      <option value="WHO">WHO</option>
                      <option value="IEC">IEC</option>
                      <option value="BIS">BIS</option>
                      <option value="ISO 13485">ISO 13485</option>
                      <option value="IEC 60601-1">IEC 60601-1</option>
                      <option value="CE-IVD">CE-IVD</option>
                      <option value="FDA 510(k)">FDA 510(k)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Maintenance Contract Status</label>
                    <select
                      value={form.product_attributes.maintenance_contract_status || ""}
                      onChange={(e) => handleAttributeChange("maintenance_contract_status", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="AMC">AMC</option>
                      <option value="CMC">CMC</option>
                      <option value="Warranty Active">Warranty Active</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Accessories Included</label>
                    <Select
                      className="input-pill"
                      placeholder="Select"
                      isMulti
                      options={[
                        { value: "Power Cable / Adapter", label: "Power Cable / Adapter" },
                        { value: "User Manual / IFU", label: "User Manual / IFU" },
                        { value: "Dust Cover / Safety Cover", label: "Dust Cover / Safety Cover" },
                        { value: "Pipettes", label: "Pipettes" },
                        { value: "Tubes", label: "Tubes" },
                        { value: "Racks", label: "Racks" },
                        { value: "Holders", label: "Holders" },
                        { value: "Cuvettes", label: "Cuvettes" },
                        { value: "Probes", label: "Probes" },
                        { value: "Sensors", label: "Sensors" },
                        { value: "Reagent Kits", label: "Reagent Kits" },
                        { value: "Cartridges", label: "Cartridges" },
                        { value: "Filters", label: "Filters" },
                        { value: "Slides", label: "Slides" },
                        { value: "Tips", label: "Tips" },
                        { value: "Syringes", label: "Syringes" },
                        { value: "Trays", label: "Trays" },
                        { value: "Dongle", label: "Dongle" }
                      ]}
                      onChange={(selected) => {
                        const values = selected ? selected.map(opt => opt.value) : [];
                        handleAttributeChange("accessories_included", values);
                      }}
                      value={Array.isArray(form.product_attributes.accessories_included)
                        ? form.product_attributes.accessories_included.map(val => ({ value: val, label: val }))
                        : []}
                      isClearable
                      isSearchable={false}
                    />
                  </div>

                  <div className="form-group">
                    <label>Safety Features</label>
                    <Select
                      className="input-pill"
                      placeholder="Select"
                      isMulti
                      options={[
                        { value: "Emergency Shutoff (Yes)", label: "Emergency Shutoff (Yes)" },
                        { value: "Emergency Shutoff (NO)", label: "Emergency Shutoff (NO)" },
                        { value: "Door Interlocks", label: "Door Interlocks" },
                        { value: "Safety Locks", label: "Safety Locks" },
                        { value: "Overload", label: "Overload" },
                        { value: "Overpressure Protection", label: "Overpressure Protection" },
                        { value: "Temperature", label: "Temperature" },
                        { value: "Voltage Alarms", label: "Voltage Alarms" },
                        { value: "HEPA filter", label: "HEPA filter" },
                        { value: "UV Sterilization", label: "UV Sterilization" },
                        { value: "Sealed Chambers", label: "Sealed Chambers" },
                        { value: "Disposable tips", label: "Disposable tips" },
                        { value: "Closed systems", label: "Closed systems" },
                        { value: "BSL compliance", label: "BSL compliance" },
                        { value: "Waste disposal system", label: "Waste disposal system" },
                        { value: "Barcode", label: "Barcode" },
                        { value: "RFID", label: "RFID" },
                        { value: "Fans", label: "Fans" },
                        { value: "Thermal cut-offs", label: "Thermal cut-offs" },
                        { value: "Cooling units", label: "Cooling units" }
                      ]}
                      onChange={(selected) => {
                        const values = selected ? selected.map(opt => opt.value) : [];
                        handleAttributeChange("safety_features", values);
                      }}
                      value={Array.isArray(form.product_attributes.safety_features)
                        ? form.product_attributes.safety_features.map(val => ({ value: val, label: val }))
                        : []}
                      isClearable
                      isSearchable={false}
                    />
                  </div>
                </div>
              </section>
            </>
          ) : (
            <section className="form-section">
              <h2>Product Attributes</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Brand</label>
                  <input
                    type="text"
                    value={form.product_attributes.brand || ""}
                    onChange={(e) => handleAttributeChange("brand", e.target.value)}
                    placeholder="Brand"
                  />
                </div>

                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="text"
                    value={form.product_attributes.year || ""}
                    onChange={(e) => handleAttributeChange("year", e.target.value)}
                    placeholder="Year"
                  />
                </div>

                <div className="form-group">
                  <label>Condition *</label>
                  <select
                    value={form.product_attributes.condition || ""}
                    onChange={(e) => handleAttributeChange("condition", e.target.value)}
                    required
                  >
                    <option value="">Select Condition</option>
                    <option value="New">New</option>
                    <option value="Like New">Like New</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Transmission</label>
                  <input
                    type="text"
                    value={form.product_attributes.transmission || ""}
                    onChange={(e) => handleAttributeChange("transmission", e.target.value)}
                    placeholder="Transmission"
                  />
                </div>

                <div className="form-group">
                  <label>Mileage (km)</label>
                  <input
                    type="text"
                    value={form.product_attributes.mileage || ""}
                    onChange={(e) => handleAttributeChange("mileage", e.target.value)}
                    placeholder="Mileage"
                  />
                </div>

                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="text"
                    value={form.product_attributes.color || ""}
                    onChange={(e) => handleAttributeChange("color", e.target.value)}
                    placeholder="Color"
                  />
                </div>

                <div className="form-group">
                  <label>Fuel</label>
                  <select
                    value={form.product_attributes.fuel || ""}
                    onChange={(e) => handleAttributeChange("fuel", e.target.value)}
                  >
                    <option value="">Select Fuel</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
              </div>
            </section>
          )}

          {/* Custom Attributes */}
          <section className="form-section">
            <div className="custom-attributes-section">
              <div className="custom-attributes-header">
                <h6 className="custom-attributes-title">🏷️ Add Attributes</h6>
                <button type="button" className="btn-add" onClick={handleAddCustomAttribute}>
                  + Add More
                </button>
              </div>
              {customAttributes.map((attr, index) => (
                <div key={attr.id} className={index > 0 ? "mt-3" : ""}>
                  <div className="row g-2">
                    <div className="col-12 col-md-4">
                      <input
                        className="input-pill"
                        placeholder="Enter Attribute"
                        value={attr.key || ""}
                        onChange={(e) => handleUpdateCustomAttribute(attr.id, 'key', e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <input
                        className="input-pill"
                        placeholder="Enter Value"
                        value={attr.value || ""}
                        onChange={(e) => handleUpdateCustomAttribute(attr.id, 'value', e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="d-flex align-items-center gap-2">
                        <Select
                          className="input-pill p-0 flex-grow-1"
                          placeholder="Select Unit"
                          options={attributeUnitOptions}
                          onChange={(opt) => handleUpdateCustomAttribute(attr.id, 'unit', opt?.value || "")}
                          value={attributeUnitOptions.find(opt => opt.value === attr.unit) || null}
                          isClearable
                        />
                        {customAttributes.length > 1 && (
                          <button
                            type="button"
                            className="btn-delete"
                            onClick={() => handleRemoveCustomAttribute(attr.id)}
                            title="Remove"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Pricing */}
          <section className="form-section">
            <h2>Pricing</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Reserve Price (₹) *</label>
                <input
                  type="number"
                  value={form.reserve_price}
                  onChange={(e) => handleInputChange("reserve_price", e.target.value)}
                  min="0"
                  step="any"
                  max={
                    creditReservePriceCap != null && creditReservePriceCap > 0
                      ? creditReservePriceCap
                      : undefined
                  }
                  required={form.reserve_type === "With Reserve"}
                  aria-invalid={!!reservePriceError}
                />
                {creditReservePriceCap != null && creditReservePriceCap > 0 ? (
                  <p className="field-description">
                    Minimum acceptable selling price. Maximum ₹
                    {Number(creditReservePriceCap).toLocaleString("en-IN")} — same limit as
                    entered on Purchase Auction Credits.
                  </p>
                ) : (
                  <p className="field-description">
                    Minimum acceptable selling price for the auction.
                  </p>
                )}
                {reservePriceError ? (
                  <p className="field-error">{reservePriceError}</p>
                ) : null}
              </div>

              <div className="form-group">
                <label>Base Price (₹) *</label>
                <input
                  type="number"
                  value={form.expected_price}
                  onChange={(e) => handleInputChange("expected_price", e.target.value)}
                  min="0"
                  step="any"
                  aria-invalid={!!basePriceError}
                />
                {form.reserve_type === "With Reserve" &&
                reservePriceNum != null &&
                reservePriceNum > 0 &&
                !basePriceError ? (
                  <p className="field-description">
                    Starting price for the auction. Must be less than or equal to reserve
                    (₹{reservePriceNum.toLocaleString("en-IN")}).
                  </p>
                ) : (
                  <p className="field-description">
                    Starting price for the auction.
                  </p>
                )}
                {basePriceError ? (
                  <p className="field-error">{basePriceError}</p>
                ) : null}
              </div>

              <div className="form-group">
                <label>Bid Increment (₹) *</label>
                <input
                  type="number"
                  value={form.bid_increment}
                  onChange={(e) => handleInputChange("bid_increment", e.target.value)}
                  required
                  min="1"
                />
              </div>

              <div className="form-group">
                <label>Auction End Condition *</label>
                <select
                  value={form.auction_end_condition}
                  onChange={(e) => handleInputChange("auction_end_condition", e.target.value)}
                  required
                >
                  <option value="End on Scheduled Date Only">
                    End on Scheduled Date Only
                  </option>
                  <option value="End When Reserve Met">End When Reserve Met</option>
                </select>
                {form.auction_end_condition === "End on Scheduled Date Only" && (
                  <p className="field-description">
                    Auction runs until scheduled end date regardless of reserve price
                  </p>
                )}
                {form.auction_end_condition === "End When Reserve Met" && (
                  <p className="field-description">
                    Auction ends immediately when reserve price is met
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Product Codes & Tax */}
          <section className="form-section">
            <h2>Product Codes & Tax</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>HSN Code</label>
                <input
                  type="text"
                  value={form.hsn_code}
                  onChange={(e) => handleInputChange("hsn_code", e.target.value)}
                  placeholder="HSN Code"
                />
              </div>

              <div className="form-group">
                <label>HS Code</label>
                <input
                  type="text"
                  value={form.hs_code}
                  onChange={(e) => handleInputChange("hs_code", e.target.value)}
                  placeholder="HS Code"
                />
              </div>

              <div className="form-group">
                <label>UNSPSC Code</label>
                <input
                  type="text"
                  value={form.unspsc_code}
                  onChange={(e) => handleInputChange("unspsc_code", e.target.value)}
                  placeholder="UNSPSC Code"
                />
              </div>

              <div className="form-group">
                <label>GST Rate (%)</label>
                <select
                  value={form.gst_rate}
                  onChange={(e) => handleInputChange("gst_rate", e.target.value)}
                >
                  <option value="0">0%</option>
                  <option value="5">5%</option>
                  <option value="12">12%</option>
                  <option value="18">18%</option>
                  <option value="28">28%</option>
                </select>
              </div>

              <div className="form-group">
                <label>CGST (%)</label>
                <input
                  type="number"
                  value={form.cgst_rate}
                  onChange={(e) => handleInputChange("cgst_rate", e.target.value)}
                  placeholder="CGST"
                />
              </div>

              <div className="form-group">
                <label>SGST (%)</label>
                <input
                  type="number"
                  value={form.sgst_rate}
                  onChange={(e) => handleInputChange("sgst_rate", e.target.value)}
                  placeholder="SGST"
                />
              </div>

              <div className="form-group">
                <label>IGST (%)</label>
                <input
                  type="number"
                  value={form.igst_rate}
                  onChange={(e) => handleInputChange("igst_rate", e.target.value)}
                  placeholder="IGST"
                />
              </div>
            </div>
          </section>

          {/* Shipping & Logistics */}
          <section className="form-section">
            <h2>Shipping & Logistics</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Export? *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="is_export"
                      value="Yes"
                      checked={form.is_export === "Yes"}
                      onChange={(e) => handleInputChange("is_export", e.target.value)}
                      required
                    />
                    <span>Yes</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="is_export"
                      value="No"
                      checked={form.is_export === "No"}
                      onChange={(e) => handleInputChange("is_export", e.target.value)}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {form.is_export === "Yes" && (
                <div className="form-group">
                  <label>Incoterms *</label>
                  <Select
                    className="input-pill p-0 flex-grow-1"
                    value={shippingTermsOptions.find(opt => opt.value === form.incoterms || opt.label === form.incoterms) || null}
                    onChange={(selected) => handleInputChange("incoterms", selected ? selected.value : "")}
                    options={shippingTermsOptions}
                    placeholder="Select Incoterms"
                    isClearable
                    isSearchable={false}
                    required={form.is_export === "Yes"}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Loading *</label>
                <select
                  value={form.loading_responsibility}
                  onChange={(e) => handleInputChange("loading_responsibility", e.target.value)}
                  required
                >
                  <option value="Buyer">Buyer</option>
                  <option value="Seller">Seller</option>
                  <option value="Shared">Shared</option>
                </select>
              </div>

              <div className="form-group">
                <label>Packaging *</label>
                <select
                  value={form.packaging_responsibility}
                  onChange={(e) => handleInputChange("packaging_responsibility", e.target.value)}
                  required
                >
                  <option value="Buyer">Buyer</option>
                  <option value="Seller">Seller</option>
                  <option value="As-Is">As-Is</option>
                </select>
              </div>

              <div className="form-group">
                <label>Insurance *</label>
                <select
                  value={form.insurance}
                  onChange={(e) => handleInputChange("insurance", e.target.value)}
                  required
                >
                  <option value="Required">Required</option>
                  <option value="Optional">Optional</option>
                  <option value="Not Required">Not Required</option>
                </select>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="form-section">
            <h2>Timeline</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  value={form.start_date ? form.start_date.split('T')[0] : ""}
                  onChange={(e) => handleInputChange("start_date", e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>End Date *</label>
                <input
                  type="date"
                  value={form.end_date ? form.end_date.split('T')[0] : ""}
                  onChange={(e) => handleInputChange("end_date", e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Pickup Window (Days) *</label>
                <input
                  type="number"
                  value={form.pickup_window_days}
                  onChange={(e) => handleInputChange("pickup_window_days", e.target.value)}
                  required
                  min="1"
                />
              </div>

              <div className="form-group">
                <label>Collection Terms *</label>
                <input
                  type="text"
                  value={form.collection_terms}
                  onChange={(e) => handleInputChange("collection_terms", e.target.value)}
                  required
                  placeholder="Collection Terms"
                />
              </div>
            </div>
          </section>

          {/* Inspection */}
          <section className="form-section">
            <h2>Inspection</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Inspection Allowed? *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="inspection_allowed"
                      value="Yes"
                      checked={form.inspection_allowed === "Yes"}
                      onChange={(e) => handleInputChange("inspection_allowed", e.target.value)}
                      required
                    />
                    <span>Yes</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="inspection_allowed"
                      value="No"
                      checked={form.inspection_allowed === "No"}
                      onChange={(e) => handleInputChange("inspection_allowed", e.target.value)}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {form.inspection_allowed === "Yes" && (
                <>
                  <div className="form-group">
                    <label>Inspector Type *</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="inspector_type"
                          value="Self Inspection"
                          checked={form.inspector_type === "Self Inspection"}
                          onChange={(e) => handleInputChange("inspector_type", e.target.value)}
                          required
                        />
                        <span>Self Inspection</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="inspector_type"
                          value="Private Inspector"
                          checked={form.inspector_type === "Private Inspector"}
                          onChange={(e) => handleInputChange("inspector_type", e.target.value)}
                        />
                        <span>Private Inspector</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Insp. Start Date *</label>
                    <input
                      type="date"
                      value={form.inspection_start_date ? form.inspection_start_date.split('T')[0] : ""}
                      onChange={(e) => handleInputChange("inspection_start_date", e.target.value)}
                      required={form.inspection_allowed === "Yes"}
                    />
                  </div>

                  <div className="form-group">
                    <label>Insp. End Date *</label>
                    <input
                      type="date"
                      value={form.inspection_end_date ? form.inspection_end_date.split('T')[0] : ""}
                      onChange={(e) => handleInputChange("inspection_end_date", e.target.value)}
                      required={form.inspection_allowed === "Yes"}
                    />
                  </div>

                  <div className="form-group">
                    <label>Time Window *</label>
                    <div className="time-window">
                      <input
                        type="time"
                        value={form.inspection_start_time}
                        onChange={(e) => handleInputChange("inspection_start_time", e.target.value)}
                        required={form.inspection_allowed === "Yes"}
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={form.inspection_end_time}
                        onChange={(e) => handleInputChange("inspection_end_time", e.target.value)}
                        required={form.inspection_allowed === "Yes"}
                      />
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Inspection Checklist (Optional)</label>
                    <div className="keyword-input">
                      <input
                        type="text"
                        value={inspectionChecklistInput}
                        onChange={(e) => setInspectionChecklistInput(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddInspectionChecklist())}
                        placeholder="Enter checklist item (e.g., Check engine oil level)"
                      />
                      <button type="button" onClick={handleAddInspectionChecklist}>
                        + Add
                      </button>
                    </div>
                    <div className="checklist-items">
                      {form.inspection_checklist.map((item, index) => (
                        <div key={index} className="checklist-item">
                          <span>{item}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveInspectionChecklist(index)}
                            className="remove-btn"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Location */}
          <section className="form-section">
            <h2>Location</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Country *</label>
                <Select
                  className="input-pill p-0"
                  value={countries.find(c => c.id === form.country || c.label === form.country) || null}
                  onChange={(selected) => handleInputChange("country", selected ? selected.id : "")}
                  options={countries}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  placeholder="Select Country"
                  isClearable
                  isSearchable={false}
                  required
                />
              </div>

              <div className="form-group">
                <label>State *</label>
                <Select
                  className="input-pill p-0"
                  value={states.find(s => s.id === form.state || s.label === form.state) || null}
                  onChange={(selected) => handleInputChange("state", selected ? selected.id : "")}
                  options={states}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  placeholder="Select State"
                  isClearable
                  isSearchable={false}
                  isDisabled={!form.country || states.length === 0}
                  required
                />
              </div>

              <div className="form-group">
                <label>City *</label>
                <Select
                  className="input-pill p-0"
                  value={cities.find(c => c.id === form.city || c.label === form.city) || null}
                  onChange={(selected) => handleInputChange("city", selected ? selected.id : "")}
                  options={cities}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  placeholder="Select City"
                  isClearable
                  isSearchable={false}
                  isDisabled={!form.state || cities.length === 0}
                  required
                />
              </div>

              <div className="form-group">
                <label>Pincode *</label>
                <input
                  type="text"
                  value={form.pincode}
                  onChange={(e) => handleInputChange("pincode", e.target.value)}
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Address</label>
                <textarea
                  value={form.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  rows="2"
                  placeholder="Building, street, landmark"
                />
              </div>
            </div>
          </section>

          {/* Contact Details */}
          <section className="form-section">
            <h2>Contact Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={(e) => handleInputChange("contact_name", e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Contact Phone *</label>
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => handleInputChange("contact_phone", e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Contact Email *</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => handleInputChange("contact_email", e.target.value)}
                  required
                />
              </div>
            </div>
          </section>

          {/* FAQs */}
          <section className="form-section">
            <div className="custom-attributes-section">
              <div className="custom-attributes-header">
                <h6 className="custom-attributes-title">❓ FAQs (Optional)</h6>
                <button type="button" className="btn-add" onClick={addFAQ}>+ Add More</button>
              </div>
              {faqList.map((faq, index) => (
                <div key={faq.id} className={index > 0 ? "mt-3" : ""}>
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <input 
                        className="input-pill" 
                        placeholder="Question" 
                        value={faq.question || ''} 
                        onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)} 
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center gap-2">
                        <input 
                          className="input-pill flex-grow-1" 
                          placeholder="Answer" 
                          value={faq.answer || ''} 
                          onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)} 
                        />
                        {faqList.length > 1 && (
                          <button
                            type="button"
                            className="btn-delete"
                            onClick={() => removeFAQ(faq.id)}
                            title="Remove"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Media */}
          <section className="form-section">
            <h2>Media</h2>
            <div className="form-grid">
              <div className="form-group">
                <div className="file-upload-component">
                  <label className="file-box">
                    <div className="file-content">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="upload-icon"
                      >
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                      </svg>
                      <span className="choose-text">
                        Upload product images ({(images.length + uploadedImageUrls.length)}/10)
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={(images.length + uploadedImageUrls.length) >= 10}
                    />
                  </label>
                  {(images.length > 0 || uploadedImageUrls.length > 0) && (
                    <div className="upload-preview-container" style={{ marginTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {/* Display existing uploaded images */}
                      {uploadedImageUrls.map((url, idx) => {
                        const imageUrl = getMediaUrl(url);
                        return (
                          <div key={`url-${idx}`} style={{ position: 'relative', width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e0e0e0' }}>
                            <img
                              src={imageUrl}
                              alt={`Uploaded ${idx + 1}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBFcnJvcjwvdGV4dD48L3N2Zz4=';
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                              style={{
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                padding: 0,
                                lineHeight: '1',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                border: '1px solid #fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onClick={() => removeImageUrl(idx)}
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                      {/* Display new files with preview */}
                      {images.map((file, idx) => {
                        const previewUrl = file instanceof File ? URL.createObjectURL(file) : getMediaUrl(file);
                        return (
                          <div key={`file-${idx}`} style={{ position: 'relative', width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e0e0e0' }}>
                            <img
                              src={previewUrl}
                              alt={file instanceof File ? file.name : 'Preview'}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                              style={{
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                padding: 0,
                                lineHeight: '1',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                border: '1px solid #fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onClick={() => {
                                if (file instanceof File) {
                                  URL.revokeObjectURL(previewUrl);
                                }
                                removeImage(idx);
                              }}
                              title="Remove image"
                            >
                              ×
                            </button>
                            {file instanceof File && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 8px', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.name}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Videos ({(videos.length + uploadedVideoUrls.length)}/2)</label>
                <div className="file-upload-component">
                  <label className="file-box">
                    <div className="file-content">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="upload-icon"
                      >
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                      </svg>
                      <span className="choose-text">
                        Upload product videos ({(videos.length + uploadedVideoUrls.length)}/2)
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="video/*"
                      multiple
                      onChange={handleVideoUpload}
                      disabled={(videos.length + uploadedVideoUrls.length) >= 2}
                    />
                  </label>
                  {(videos.length > 0 || uploadedVideoUrls.length > 0) && (
                    <div className="upload-preview-container" style={{ marginTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {/* Display existing uploaded videos */}
                      {uploadedVideoUrls.map((url, idx) => {
                        const videoUrl = getMediaUrl(url);
                        return (
                          <div key={`url-${idx}`} style={{ position: 'relative', width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                            <video
                              src={videoUrl}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              controls={false}
                              muted
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                              style={{
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                padding: 0,
                                lineHeight: '1',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                border: '1px solid #fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onClick={() => removeVideoUrl(idx)}
                              title="Remove video"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                      {/* Display new files with preview */}
                      {videos.map((file, idx) => {
                        const previewUrl = file instanceof File ? URL.createObjectURL(file) : getMediaUrl(file);
                        return (
                          <div key={`file-${idx}`} style={{ position: 'relative', width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                            <video
                              src={previewUrl}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              controls={false}
                              muted
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                              style={{
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                padding: 0,
                                lineHeight: '1',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                border: '1px solid #fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onClick={() => {
                                if (file instanceof File) {
                                  URL.revokeObjectURL(previewUrl);
                                }
                                removeVideo(idx);
                              }}
                              title="Remove video"
                            >
                              ×
                            </button>
                            {file instanceof File && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 8px', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.name}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <div className="disclaimer">
            <p>
              Disclaimer: You maintain full ownership and liability until buyer acceptance.
              Trocanet acts only as a facilitator.
            </p>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || !hasCredits}
            >
              {isSubmitting ? "Creating..." : "Preview Auction"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
